/**
 * Host Global - Admin Property Details Page
 * Professional property details page with inspector assignment and calendar view
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import AppBar from "@mui/material/AppBar";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import LinearProgress from "@mui/material/LinearProgress";

// react-big-calendar
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// moment.js localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Translation mappings for countries (EN -> AR)
const COUNTRY_TRANSLATIONS = {
  Egypt: "مصر",
  "United States": "الولايات المتحدة",
  "United Kingdom": "المملكة المتحدة",
  Canada: "كندا",
  Australia: "أستراليا",
  Germany: "ألمانيا",
  France: "فرنسا",
  Italy: "إيطاليا",
  Spain: "إسبانيا",
  Netherlands: "هولندا",
  Belgium: "بلجيكا",
  Switzerland: "سويسرا",
  Austria: "النمسا",
  Sweden: "السويد",
  Norway: "النرويج",
  Denmark: "الدنمارك",
  Finland: "فنلندا",
  Poland: "بولندا",
  Portugal: "البرتغال",
  Greece: "اليونان",
  Turkey: "تركيا",
  "United Arab Emirates": "الإمارات العربية المتحدة",
  "Saudi Arabia": "المملكة العربية السعودية",
  Qatar: "قطر",
  Kuwait: "الكويت",
  Bahrain: "البحرين",
  Oman: "عمان",
  Jordan: "الأردن",
  Lebanon: "لبنان",
  Morocco: "المغرب",
  Tunisia: "تونس",
  Algeria: "الجزائر",
  "South Africa": "جنوب أفريقيا",
  Kenya: "كينيا",
  Nigeria: "نيجيريا",
  India: "الهند",
  China: "الصين",
  Japan: "اليابان",
  "South Korea": "كوريا الجنوبية",
  Singapore: "سنغافورة",
  Malaysia: "ماليزيا",
  Thailand: "تايلاند",
  Indonesia: "إندونيسيا",
  Philippines: "الفلبين",
  Vietnam: "فيتنام",
  Brazil: "البرازيل",
  Argentina: "الأرجنتين",
  Mexico: "المكسيك",
  Chile: "تشيلي",
  Colombia: "كولومبيا",
  Peru: "بيرو",
  "New Zealand": "نيوزيلندا",
  Ireland: "أيرلندا",
  Iceland: "آيسلندا",
  Luxembourg: "لوكسمبورغ",
  "Czech Republic": "جمهورية التشيك",
  Hungary: "المجر",
  Romania: "رومانيا",
  Bulgaria: "بلغاريا",
  Croatia: "كرواتيا",
  Serbia: "صربيا",
  Slovenia: "سلوفينيا",
  Slovakia: "سلوفاكيا",
  Estonia: "إستونيا",
  Latvia: "لاتفيا",
  Lithuania: "ليتوانيا",
  Cyprus: "قبرص",
  Malta: "مالطا",
  Israel: "إسرائيل",
  Russia: "روسيا",
  Ukraine: "أوكرانيا",
  Belarus: "بيلاروس",
  Kazakhstan: "كازاخستان",
  Uzbekistan: "أوزبكستان",
  Azerbaijan: "أذربيجان",
  Georgia: "جورجيا",
  Armenia: "أرمينيا",
  Moldova: "مولدوفا",
  Albania: "ألبانيا",
  "Bosnia and Herzegovina": "البوسنة والهرسك",
  Macedonia: "مقدونيا",
  Montenegro: "الجبل الأسود",
  Kosovo: "كوسوفو",
  Iraq: "العراق",
  Iran: "إيران",
  Pakistan: "باكستان",
  Bangladesh: "بنغلاديش",
  "Sri Lanka": "سريلانكا",
  Nepal: "نيبال",
  Bhutan: "بوتان",
  Myanmar: "ميانمار",
  Cambodia: "كمبوديا",
  Laos: "لاوس",
  Mongolia: "منغوليا",
  Taiwan: "تايوان",
  "Hong Kong": "هونغ كونغ",
  Macau: "ماكاو",
};

// Translation mappings for cities (EN -> AR) - Focus on Egypt and major cities
const CITY_TRANSLATIONS = {
  // Egypt
  Cairo: "القاهرة",
  Alexandria: "الإسكندرية",
  Giza: "الجيزة",
  "Sharm El Sheikh": "شرم الشيخ",
  Luxor: "الأقصر",
  Aswan: "أسوان",
  Hurghada: "الغردقة",
  "Marsa Alam": "مرسى علم",
  Dahab: "دهب",
  Taba: "طابا",
  "Port Said": "بورسعيد",
  Suez: "السويس",
  Ismailia: "الإسماعيلية",
  Mansoura: "المنصورة",
  Tanta: "طنطا",
  Zagazig: "الزقازيق",
  Damietta: "دمياط",
  Minya: "المنيا",
  Asyut: "أسيوط",
  Sohag: "سوهاج",
  // UAE
  Dubai: "دبي",
  "Abu Dhabi": "أبو ظبي",
  Sharjah: "الشارقة",
  "Al Ain": "العين",
  Ajman: "عجمان",
  "Ras Al Khaimah": "رأس الخيمة",
  Fujairah: "الفجيرة",
  "Umm Al Quwain": "أم القيوين",
  // Saudi Arabia
  Riyadh: "الرياض",
  Jeddah: "جدة",
  Mecca: "مكة المكرمة",
  Medina: "المدينة المنورة",
  Dammam: "الدمام",
  Khobar: "الخبر",
  Taif: "الطائف",
  Abha: "أبها",
  Tabuk: "تبوك",
  Buraydah: "بريدة",
  // Qatar
  Doha: "الدوحة",
  "Al Rayyan": "الريان",
  "Al Wakrah": "الوكرة",
  "Al Khor": "الخور",
  // Jordan
  Amman: "عمان",
  Irbid: "إربد",
  Zarqa: "الزرقاء",
  Aqaba: "العقبة",
  // Lebanon
  Beirut: "بيروت",
  Tripoli: "طرابلس",
  Sidon: "صيدا",
  Tyre: "صور",
  // Morocco
  Casablanca: "الدار البيضاء",
  Rabat: "الرباط",
  Marrakech: "مراكش",
  Fes: "فاس",
  Tangier: "طنجة",
  // Other major cities
  London: "لندن",
  Paris: "باريس",
  Berlin: "برلين",
  Rome: "روما",
  Madrid: "مدريد",
  Amsterdam: "أمستردام",
  Vienna: "فيينا",
  Brussels: "بروكسل",
  Zurich: "زيورخ",
  Stockholm: "ستوكهولم",
  Oslo: "أوسلو",
  Copenhagen: "كوبنهاغن",
  Helsinki: "هلسنكي",
  Warsaw: "وارسو",
  Lisbon: "لشبونة",
  Athens: "أثينا",
  Istanbul: "إسطنبول",
  "New York": "نيويورك",
  "Los Angeles": "لوس أنجلوس",
  Chicago: "شيكاغو",
  Toronto: "تورونتو",
  Vancouver: "فانكوفر",
  Sydney: "سيدني",
  Melbourne: "ملبورن",
  Tokyo: "طوكيو",
  Seoul: "سيول",
  Singapore: "سنغافورة",
  Bangkok: "بانكوك",
  Mumbai: "مومباي",
  Delhi: "دلهي",
  Beijing: "بكين",
  Shanghai: "شنغهاي",
  "Hong Kong": "هونغ كونغ",
};

// Helper function to get Arabic translation
const getArabicTranslation = (englishText, translationMap) => {
  return translationMap[englishText] || englishText;
};

// Get all country options
const getCountryOptions = () => Object.keys(COUNTRY_TRANSLATIONS);

// Get all city options
const getCityOptions = () => Object.keys(CITY_TRANSLATIONS);

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { adminAPI, superAdminAPI } from "utils/api";
import { getStatusLabel } from "utils/statusLabels";

// Context
import { useAuth } from "context/authContext";

// Icons
import Icon from "@mui/material/Icon";

function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isSuperAdmin = userRole === "Super_Admin" || userRole === "Super Admin";
  const isAdmin = userRole === "Admin";

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inspectors, setInspectors] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState("");
  const [inspectorCalendar, setInspectorCalendar] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveToContractingDialogOpen, setApproveToContractingDialogOpen] = useState(false);
  const [approvingToContracting, setApprovingToContracting] = useState(false);
  const [approveToInspectionOfferDialogOpen, setApproveToInspectionOfferDialogOpen] =
    useState(false);
  const [approvingToInspectionOffer, setApprovingToInspectionOffer] = useState(false);
  const [movingStatus, setMovingStatus] = useState(false); // for move To Preparation/Legalization/etc.
  const [checkInOutAgents, setCheckInOutAgents] = useState([]);
  const [loadingCheckInOutAgents, setLoadingCheckInOutAgents] = useState(false);
  const [assignCheckInOutAgentDialogOpen, setAssignCheckInOutAgentDialogOpen] = useState(false);
  const [selectedCheckInOutAgentId, setSelectedCheckInOutAgentId] = useState("");
  const [updatingCheckInOutAgent, setUpdatingCheckInOutAgent] = useState(false);

  // Quick Actions - Finance Dialog State
  const [financeDialogOpen, setFinanceDialogOpen] = useState(false);
  const [financeData, setFinanceData] = useState({
    price: "",
    profitPercentage: "",
    insurancePrice: "",
    maintenanceCostPercentage: "",
    maxGuests: "",
    extraFeesPerPerson: "",
    earlyCheckInFeePercentage: "",
    lateCheckOutFeePercentage: "",
    packages: [],
    checkInOutAgentId: "",
    expectedReadyDate: "",
  });
  const [financePackagesList, setFinancePackagesList] = useState([]);
  const [loadingFinancePackages, setLoadingFinancePackages] = useState(false);
  const [updatingFinance, setUpdatingFinance] = useState(false);
  const [financeDialogMode, setFinanceDialogMode] = useState("edit"); // "edit" | "approve" (approve = Set Finance & Move to Preparation from Contracting)
  const [financeModalError, setFinanceModalError] = useState("");

  // Status and Featured State
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingFeatured, setUpdatingFeatured] = useState(false);

  // Send Missing Items Email State
  const [missingItemsDialogOpen, setMissingItemsDialogOpen] = useState(false);
  const [missingItemsData, setMissingItemsData] = useState({
    items: [],
    expectedDate: "",
    expectedDateApprovalToListing: "",
    customMessage: "",
    pricePerDay: "",
    occupancyRate: "",
    companyPercentage: "",
    bookingWebsitePercentage: "",
  });
  const [submittingMissingItems, setSubmittingMissingItems] = useState(false);

  // Official Documents Management State
  const [officialDocumentsDialogOpen, setOfficialDocumentsDialogOpen] = useState(false);
  const [officialDocumentsFiles, setOfficialDocumentsFiles] = useState([]);
  const [uploadingOfficialDocuments, setUploadingOfficialDocuments] = useState(false);
  const [activatingProperty, setActivatingProperty] = useState(false);

  // Fetch check-in/check-out agents when dialog opens
  useEffect(() => {
    const fetchCheckInOutAgents = async () => {
      if (assignCheckInOutAgentDialogOpen) {
        try {
          setLoadingCheckInOutAgents(true);
          const response = await adminAPI.getAvailableCheckInOutAgents();
          setCheckInOutAgents(response.agents || []);
        } catch (error) {
          console.error("Error fetching check-in/check-out agents:", error);
          setMessage({
            type: "error",
            text: "Failed to load check-in/check-out agents",
          });
        } finally {
          setLoadingCheckInOutAgents(false);
        }
      }
    };

    fetchCheckInOutAgents();
  }, [assignCheckInOutAgentDialogOpen]);

  // Initialize inspection location with property address and Google Maps link
  useEffect(() => {
    const addressValue = property?.address?.en || property?.address || "";
    if (property && addressValue && assignDialogOpen) {
      const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        addressValue
      )}`;
      const addressText = property.address?.en || property.address || "";
      const cityText = property.city?.en || property.city || "";
      const locationText = `${addressText}${
        cityText ? `, ${cityText}` : ""
      }\n\nGoogle Maps: ${googleMapsLink}`;
      setAssignFormData((prev) => {
        // Only auto-fill if location is empty or if we're opening for a new assignment (no existing inspector)
        if (
          !property?.assignedInspector &&
          (!prev.inspectionLocation || prev.inspectionLocation.trim() === "")
        ) {
          return {
            ...prev,
            inspectionLocation: locationText,
          };
        }
        // If there's an existing inspector, use their location if available, otherwise use property address
        if (property?.assignedInspector && property.inspectionLocation) {
          return {
            ...prev,
            inspectionLocation: property.inspectionLocation,
          };
        }
        // If no existing location but we have property address, use it
        if (!prev.inspectionLocation || prev.inspectionLocation.trim() === "") {
          return {
            ...prev,
            inspectionLocation: locationText,
          };
        }
        return prev;
      });
    }
  }, [property, assignDialogOpen]);

  // Pre-fill inspector dialog when changing existing inspector
  useEffect(() => {
    if (assignDialogOpen && property?.assignedInspector) {
      // Pre-fill the selected inspector
      if (property.assignedInspector._id || property.assignedInspector) {
        setSelectedInspector(property.assignedInspector._id || property.assignedInspector);
      }
      // Pre-fill inspection date if available
      if (property.inspectionDate) {
        const inspectionDate = new Date(property.inspectionDate);
        const formattedDate = formatDateTimeLocal(inspectionDate);
        setAssignFormData((prev) => ({
          ...prev,
          inspectionDate: formattedDate,
        }));
        // Set selected slot for calendar display
        const selectedDateTime = new Date(inspectionDate);
        selectedDateTime.setMinutes(0, 0, 0);
        const endDateTime = new Date(selectedDateTime);
        endDateTime.setHours(endDateTime.getHours() + 1);
        setSelectedSlot({
          start: selectedDateTime,
          end: endDateTime,
        });
        // Set calendar date to inspection date
        setCalendarDate(new Date(inspectionDate));
      }
      // Pre-fill inspection location if available
      if (property.inspectionLocation) {
        setAssignFormData((prev) => ({
          ...prev,
          inspectionLocation: property.inspectionLocation,
        }));
      }
    } else if (assignDialogOpen && !property?.assignedInspector) {
      // Reset inspector selection when opening for new assignment
      setSelectedInspector("");
      // Reset inspection date
      setAssignFormData((prev) => ({
        ...prev,
        inspectionDate: "",
      }));
      setSelectedSlot(null);
      setCalendarDate(new Date());
    }
  }, [assignDialogOpen, property]);

  const [assignFormData, setAssignFormData] = useState({
    inspectionDate: "",
    inspectionLocation: "",
  });
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [lightboxTitle, setLightboxTitle] = useState("");
  const openImageLightbox = (images, index = 0, title = "Image") => {
    const urls = (images || []).map((img) =>
      typeof img === "string" ? img : img?.photoUrl || img?.url || img
    );
    setLightboxImages(urls.filter(Boolean));
    setLightboxImageIndex(Math.min(index, Math.max(0, urls.length - 1)));
    setLightboxTitle(title);
    setLightboxOpen(true);
  };
  const [pdfLightboxOpen, setPdfLightboxOpen] = useState(false);
  const [pdfLightboxUrl, setPdfLightboxUrl] = useState("");
  const [pdfLightboxTitle, setPdfLightboxTitle] = useState("");
  const [useGoogleViewer, setUseGoogleViewer] = useState(false);
  const [calendarView, setCalendarView] = useState("month");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null); // Track selected time slot
  const [hoverModalOpen, setHoverModalOpen] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [editInsurancePrice, setEditInsurancePrice] = useState(false);
  const [insurancePriceValue, setInsurancePriceValue] = useState("");
  const [updatingInsurancePrice, setUpdatingInsurancePrice] = useState(false);

  // Quick Actions Pricing state
  const [quickPricingData, setQuickPricingData] = useState({
    price: "",
    profitPercentage: "",
    discount: "",
    packages: [],
  });
  const [updatingPricing, setUpdatingPricing] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updatingProperty, setUpdatingProperty] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [detailsTab, setDetailsTab] = useState(0);

  // Multi-step edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editActiveStep, setEditActiveStep] = useState(0);

  // Media management state
  const [newImages, setNewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [newVideos, setNewVideos] = useState([]);
  const [videosToDelete, setVideosToDelete] = useState([]);
  const [newLegalPapers, setNewLegalPapers] = useState([]);
  const [legalPapersToDelete, setLegalPapersToDelete] = useState([]);
  const [newBuildingImages, setNewBuildingImages] = useState([]);
  const [buildingImagesToDelete, setBuildingImagesToDelete] = useState([]);

  // New photo uploads
  const [newLivingRoomPhoto, setNewLivingRoomPhoto] = useState(null);
  const [newRoomPhotos, setNewRoomPhotos] = useState({}); // { room1: File, room2: File, ... }
  const [newBathroomPhotos, setNewBathroomPhotos] = useState({}); // { bathroom1: File, bathroom2: File, ... }
  const [livingRoomPhotoToDelete, setLivingRoomPhotoToDelete] = useState(false);
  const [roomPhotosToDelete, setRoomPhotosToDelete] = useState([]);
  const [bathroomPhotosToDelete, setBathroomPhotosToDelete] = useState([]);
  const [newBalconyPhotos, setNewBalconyPhotos] = useState({});
  const [balconyPhotosToDelete, setBalconyPhotosToDelete] = useState([]);

  // Property owner documents
  const [newPropertyOwnerId, setNewPropertyOwnerId] = useState(null);
  const [newRentalOrOwnershipContract, setNewRentalOrOwnershipContract] = useState(null);
  const [newPowerOfAttorney, setNewPowerOfAttorney] = useState(null);
  const [propertyOwnerIdToDelete, setPropertyOwnerIdToDelete] = useState(false);
  const [rentalOrOwnershipContractToDelete, setRentalOrOwnershipContractToDelete] = useState(false);
  const [powerOfAttorneyToDelete, setPowerOfAttorneyToDelete] = useState(false);

  // Criteria and amenities from API
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [criteriaList, setCriteriaList] = useState([]);
  const [loadingAmenitiesCriteria, setLoadingAmenitiesCriteria] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialize edit form data when entering edit mode or when property changes
  useEffect(() => {
    if (editMode && property) {
      initializeEditForm();
    }
  }, [editMode, property]);

  // Re-initialize form when dialog opens to ensure compound facilities are loaded
  useEffect(() => {
    if (editDialogOpen && property) {
      initializeEditForm();
    }
  }, [editDialogOpen]);

  // Fetch property details
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        const [propertyResponse, inspectorsResponse, packagesResponse] = await Promise.all([
          adminAPI.getPropertyById(id),
          adminAPI.getAvailableInspectors(),
          adminAPI.getAllPackages({ isActive: "true" }).catch(() => ({ data: [], packages: [] })),
        ]);
        setProperty(propertyResponse.property);
        setInspectors(inspectorsResponse.inspectors || []);
        setPackages(packagesResponse.data || packagesResponse.packages || []);
      } catch (error) {
        console.error("Error fetching property:", error);
        setMessage({
          type: "error",
          text: error.message || "Failed to load property details.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProperty();
    }
  }, [id]);

  // Fetch amenities and criteria for edit form
  useEffect(() => {
    const fetchAmenitiesCriteria = async () => {
      try {
        setLoadingAmenitiesCriteria(true);
        const [amenitiesRes, criteriaRes] = await Promise.all([
          superAdminAPI.getAllAmenities().catch(() => ({ data: [] })),
          superAdminAPI.getAllCriteria().catch(() => ({ data: [] })),
        ]);
        setAmenitiesList(amenitiesRes.data || []);
        setCriteriaList(criteriaRes.data || []);
      } catch (error) {
        console.error("Error fetching amenities/criteria:", error);
        setAmenitiesList([]);
        setCriteriaList([]);
      } finally {
        setLoadingAmenitiesCriteria(false);
      }
    };
    fetchAmenitiesCriteria();
  }, []);

  // Initialize quick pricing data when property loads
  useEffect(() => {
    if (property && packages.length > 0) {
      // Match property package IDs with package objects
      const selectedPackages = (property.packages || [])
        .map((pkgId) => packages.find((pkg) => pkg._id === pkgId || pkg._id === pkgId.toString()))
        .filter(Boolean);

      setQuickPricingData({
        price:
          property.price !== undefined && property.price !== null ? String(property.price) : "",
        profitPercentage:
          property.profitPercentage !== undefined && property.profitPercentage !== null
            ? String(property.profitPercentage)
            : "",
        discount:
          property.discount !== undefined && property.discount !== null
            ? String(property.discount)
            : "",
        packages: selectedPackages,
      });
    } else if (property) {
      setQuickPricingData({
        price:
          property.price !== undefined && property.price !== null ? String(property.price) : "",
        profitPercentage:
          property.profitPercentage !== undefined && property.profitPercentage !== null
            ? String(property.profitPercentage)
            : "",
        discount:
          property.discount !== undefined && property.discount !== null
            ? String(property.discount)
            : "",
        packages: [],
      });
    }
  }, [property, packages]);

  // Handle edit form change
  const handleEditChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle quick pricing change
  const handleQuickPricingChange = (field, value) => {
    setQuickPricingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle quick pricing update
  const handleQuickPricingUpdate = async () => {
    try {
      setUpdatingPricing(true);
      setMessage({ type: "", text: "" });

      const updateData = {};
      // Only Super Admin can update pricing
      if (!isSuperAdmin) {
        setMessage({ type: "error", text: "Only Super Admin can update pricing" });
        setUpdatingPricing(false);
        return;
      }

      if (quickPricingData.price !== "") {
        updateData.price = parseFloat(quickPricingData.price);
        if (isNaN(updateData.price) || updateData.price < 0) {
          setMessage({
            type: "error",
            text: "Price must be a valid number greater than or equal to 0",
          });
          setUpdatingPricing(false);
          return;
        }
      }
      if (quickPricingData.profitPercentage !== "") {
        updateData.profitPercentage = parseFloat(quickPricingData.profitPercentage);
        if (
          isNaN(updateData.profitPercentage) ||
          updateData.profitPercentage < 0 ||
          updateData.profitPercentage > 100
        ) {
          setMessage({ type: "error", text: "Platform percentage must be between 0 and 100" });
          setUpdatingPricing(false);
          return;
        }
      }
      if (quickPricingData.discount !== "") {
        updateData.discount = parseFloat(quickPricingData.discount);
        if (isNaN(updateData.discount) || updateData.discount < 0 || updateData.discount > 100) {
          setMessage({ type: "error", text: "Discount must be between 0 and 100" });
          setUpdatingPricing(false);
          return;
        }
      }
      if (quickPricingData.packages !== undefined) {
        // Convert package objects to IDs if needed
        updateData.packages = Array.isArray(quickPricingData.packages)
          ? quickPricingData.packages.map((pkg) =>
              typeof pkg === "object" && pkg._id ? pkg._id : pkg
            )
          : [];
      }

      if (Object.keys(updateData).length === 0) {
        setMessage({ type: "warning", text: "No changes to update" });
        setUpdatingPricing(false);
        return;
      }

      const response = await adminAPI.quickUpdatePropertyPricing(id, updateData);
      setProperty(response.property);
      setMessage({ type: "success", text: "Pricing updated successfully" });

      // Reset message after 3 seconds
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
    } catch (error) {
      console.error("Error updating pricing:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to update pricing. Please try again.",
      });
    } finally {
      setUpdatingPricing(false);
    }
  };

  // Helper function to fix PDF URLs that were uploaded as images
  const fixPdfUrl = (url) => {
    if (!url) return url;
    // Check if URL contains /image/upload/ and ends with .pdf
    if (
      url.includes("/image/upload/") &&
      (url.toLowerCase().endsWith(".pdf") || url.includes(".pdf"))
    ) {
      // Replace /image/upload/ with /raw/upload/ to properly serve PDF files
      return url.replace("/image/upload/", "/raw/upload/");
    }
    return url;
  };

  // Helper to add Cloudinary download transformation
  const addDownloadTransformation = (url) => {
    if (!url || !url.includes("cloudinary.com")) return url;

    try {
      const urlObj = new URL(url);
      // Add attachment query parameter to force download
      urlObj.searchParams.set("attachment", "true");
      return urlObj.toString();
    } catch (error) {
      // If URL parsing fails, try string manipulation
      if (url.includes("?")) {
        return url + "&attachment=true";
      } else {
        return url + "?attachment=true";
      }
    }
  };

  // Open PDF in lightbox viewer
  const viewPdf = (url, title = "Document") => {
    if (!url) return;

    // Fix URL if needed
    let fixedUrl = fixPdfUrl(url);

    // For Cloudinary URLs, ensure they're properly formatted for embedding
    // Remove any attachment parameters that might interfere with viewing
    if (fixedUrl.includes("cloudinary.com")) {
      try {
        const urlObj = new URL(fixedUrl);
        urlObj.searchParams.delete("attachment");
        fixedUrl = urlObj.toString();
      } catch (e) {
        // If URL parsing fails, just use the fixed URL as is
      }
    }

    setPdfLightboxUrl(fixedUrl);
    setPdfLightboxTitle(title);
    setUseGoogleViewer(false); // Reset to try object tag first
    setPdfLightboxOpen(true);
  };

  // Media handlers
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + newImages.length > 20) {
      setMessage({ type: "error", text: "Maximum 20 images allowed" });
      return;
    }
    setNewImages([...newImages, ...files]);
  };

  const removeImage = (index) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  const deleteImage = (imageUrl) => {
    if (!imagesToDelete.includes(imageUrl)) {
      setImagesToDelete([...imagesToDelete, imageUrl]);
    }
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + newVideos.length > 10) {
      setMessage({ type: "error", text: "Maximum 10 videos allowed" });
      return;
    }
    setNewVideos([...newVideos, ...files]);
  };

  const removeVideo = (index) => {
    setNewVideos(newVideos.filter((_, i) => i !== index));
  };

  const deleteVideo = (videoUrl) => {
    if (!videosToDelete.includes(videoUrl)) {
      setVideosToDelete([...videosToDelete, videoUrl]);
    }
  };

  const handleLegalPaperChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + newLegalPapers.length > 10) {
      setMessage({ type: "error", text: "Maximum 10 legal papers allowed" });
      return;
    }
    setNewLegalPapers([...newLegalPapers, ...files]);
  };

  const removeLegalPaper = (index) => {
    setNewLegalPapers(newLegalPapers.filter((_, i) => i !== index));
  };

  const deleteLegalPaper = (docUrl) => {
    if (!legalPapersToDelete.includes(docUrl)) {
      setLegalPapersToDelete([...legalPapersToDelete, docUrl]);
    }
  };

  const handleBuildingImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + newBuildingImages.length > 10) {
      setMessage({ type: "error", text: "Maximum 10 building images allowed" });
      return;
    }
    setNewBuildingImages([...newBuildingImages, ...files]);
  };

  const removeBuildingImage = (index) => {
    setNewBuildingImages(newBuildingImages.filter((_, i) => i !== index));
  };

  const deleteBuildingImage = (imageUrl) => {
    if (!buildingImagesToDelete.includes(imageUrl)) {
      setBuildingImagesToDelete([...buildingImagesToDelete, imageUrl]);
    }
  };

  // Initialize edit form data when entering edit mode
  const initializeEditForm = () => {
    if (property) {
      setEditFormData({
        titleEn: property.title?.en ?? property.title ?? "",
        titleAr: property.title?.ar ?? "",
        descriptionEn: property.description?.en ?? property.description ?? "",
        descriptionAr: property.description?.ar ?? "",
        propertyType: property.propertyType ?? "",
        rooms:
          property.rooms !== undefined && property.rooms !== null ? String(property.rooms) : "",
        bathrooms:
          property.bathrooms !== undefined && property.bathrooms !== null
            ? String(property.bathrooms)
            : "",
        balconies:
          property.balconies !== undefined && property.balconies !== null
            ? String(property.balconies)
            : "",
        size: property.size !== undefined && property.size !== null ? String(property.size) : "",
        floors:
          property.floors !== undefined && property.floors !== null ? String(property.floors) : "",
        floorNumber:
          property.floorNumber !== undefined && property.floorNumber !== null
            ? String(property.floorNumber)
            : "",
        buildingAge:
          property.buildingAge !== undefined && property.buildingAge !== null
            ? String(property.buildingAge)
            : "",
        furnitureAge:
          property.furnitureAge !== undefined && property.furnitureAge !== null
            ? String(property.furnitureAge)
            : "",
        apartmentsPerFloor:
          property.apartmentsPerFloor !== undefined && property.apartmentsPerFloor !== null
            ? String(property.apartmentsPerFloor)
            : "",
        price:
          property.price !== undefined && property.price !== null ? String(property.price) : "",
        profitPercentage:
          property.profitPercentage !== undefined && property.profitPercentage !== null
            ? String(property.profitPercentage)
            : "",
        insurancePrice:
          property.insurancePrice !== undefined && property.insurancePrice !== null
            ? String(property.insurancePrice)
            : "",
        addressEn: property.address?.en ?? property.address ?? "",
        addressAr: property.address?.ar ?? "",
        cityEn: property.city?.en ?? "",
        cityAr: property.city?.ar ?? "",
        countryEn: property.country?.en ?? "",
        countryAr: property.country?.ar ?? "",
        googleMapLink: property.googleMapLink ?? "",
        featured: property.featured ?? false,
        discount:
          property.discount !== undefined && property.discount !== null
            ? String(property.discount)
            : "",
        // Compound information
        isInCompound: property.isInCompound ?? false,
        compoundNameEn: property.compoundName?.en ?? "",
        compoundNameAr: property.compoundName?.ar ?? "",
        compoundFacilities:
          property.compoundFacilities && Array.isArray(property.compoundFacilities)
            ? property.compoundFacilities
            : [],
        // WiFi & Utilities
        wifiName: property.wifiName ?? "",
        wifiPassword: property.wifiPassword ?? "",
        electricityMeterType: property.electricityMeterType ?? "",
        waterMeterType: property.waterMeterType ?? "",
        propertyView: property.propertyView ?? "",
        accessCode: property.accessCode ?? "",
        landmarkEn: property.landmark?.en ?? "",
        landmarkAr: property.landmark?.ar ?? "",
        // Amenities - keep as array of objects
        amenities:
          property.amenities && Array.isArray(property.amenities) ? property.amenities : [],
        // Criteria - keep as array of objects
        criteria: property.criteria && Array.isArray(property.criteria) ? property.criteria : [],
        // Property status & inspection preferences
        currentPropertyState: property.currentPropertyState ?? "Available",
        readyForInspector: property.readyForInspector === true,
        currentRentalProcess: property.currentRentalProcess ?? "",
        currentRentalRevenue:
          property.currentRentalRevenue !== undefined && property.currentRentalRevenue !== null
            ? String(property.currentRentalRevenue)
            : "",
        preferredInspectionTime: property.preferredInspectionTime ?? "",
        tour360Url: property.tour360Url ?? "",
      });
    }
  };

  // Handle save property details
  const handleSaveProperty = async () => {
    try {
      setUpdatingProperty(true);
      setMessage({ type: "", text: "" });

      // Frontend validation - ensure required fields are not empty or null
      const titleEnTrimmed = editFormData.titleEn?.trim() || "";
      const titleArTrimmed = editFormData.titleAr?.trim() || "";
      const addressEnTrimmed = editFormData.addressEn?.trim() || "";
      const addressArTrimmed = editFormData.addressAr?.trim() || "";
      const propertyTypeValue = editFormData.propertyType || "";
      const roomsValue =
        editFormData.rooms !== undefined && editFormData.rooms !== null && editFormData.rooms !== ""
          ? parseInt(editFormData.rooms)
          : null;
      const bathroomsValue =
        editFormData.bathrooms !== undefined &&
        editFormData.bathrooms !== null &&
        editFormData.bathrooms !== ""
          ? parseInt(editFormData.bathrooms)
          : null;

      // Validate required fields - check for null, undefined, or empty strings
      const validationErrors = [];

      if (!titleEnTrimmed || titleEnTrimmed.length === 0) {
        validationErrors.push("Title (English) is required");
      }
      if (!titleArTrimmed || titleArTrimmed.length === 0) {
        validationErrors.push("Title (Arabic) is required");
      }
      if (!addressEnTrimmed || addressEnTrimmed.length === 0) {
        validationErrors.push("Address (English) is required");
      }
      if (!addressArTrimmed || addressArTrimmed.length === 0) {
        validationErrors.push("Address (Arabic) is required");
      }
      if (!propertyTypeValue || propertyTypeValue.length === 0) {
        validationErrors.push("Property Type is required");
      }
      if (roomsValue === null || roomsValue === undefined || isNaN(roomsValue)) {
        validationErrors.push("Rooms is required and must be a valid number");
      }
      if (bathroomsValue === null || bathroomsValue === undefined || isNaN(bathroomsValue)) {
        validationErrors.push("Bathrooms is required and must be a valid number");
      }

      if (validationErrors.length > 0) {
        setMessage({
          type: "error",
          text: `Please fill in all required fields: ${validationErrors.join(", ")}`,
        });
        setUpdatingProperty(false);
        return;
      }

      // Prepare data for API - always send required fields
      const updateData = {
        titleEn: titleEnTrimmed,
        titleAr: titleArTrimmed,
        addressEn: addressEnTrimmed,
        addressAr: addressArTrimmed,
        propertyType: propertyTypeValue,
        rooms: roomsValue,
        bathrooms: bathroomsValue,
      };

      // Optional fields - only send if they have values
      if (editFormData.descriptionEn !== undefined)
        updateData.descriptionEn = editFormData.descriptionEn.trim();
      if (editFormData.descriptionAr !== undefined)
        updateData.descriptionAr = editFormData.descriptionAr.trim();
      if (editFormData.size !== undefined && editFormData.size !== "")
        updateData.size = parseFloat(editFormData.size);
      if (editFormData.floors !== undefined && editFormData.floors !== "")
        updateData.floors = parseInt(editFormData.floors);
      if (editFormData.floorNumber !== undefined && editFormData.floorNumber !== "")
        updateData.floorNumber = parseInt(editFormData.floorNumber);
      // Only Super Admin can update price and discount
      if (isSuperAdmin) {
        if (editFormData.price !== undefined && editFormData.price !== "")
          updateData.price = parseFloat(editFormData.price);
        if (editFormData.discount !== undefined && editFormData.discount !== "")
          updateData.discount = parseFloat(editFormData.discount);
      }
      if (editFormData.cityEn !== undefined) updateData.cityEn = editFormData.cityEn.trim();
      if (editFormData.cityAr !== undefined) updateData.cityAr = editFormData.cityAr.trim();
      if (editFormData.countryEn !== undefined)
        updateData.countryEn = editFormData.countryEn.trim();
      if (editFormData.countryAr !== undefined)
        updateData.countryAr = editFormData.countryAr.trim();
      if (editFormData.googleMapLink !== undefined)
        updateData.googleMapLink = editFormData.googleMapLink.trim();
      if (editFormData.featured !== undefined) updateData.featured = editFormData.featured;
      if (editFormData.buildingAge !== undefined && editFormData.buildingAge !== "")
        updateData.buildingAge = parseInt(editFormData.buildingAge);
      if (editFormData.furnitureAge !== undefined && editFormData.furnitureAge !== "")
        updateData.furnitureAge = parseInt(editFormData.furnitureAge);
      if (editFormData.balconies !== undefined)
        updateData.balconies =
          editFormData.balconies !== "" ? parseInt(editFormData.balconies) || 0 : 0;
      if (editFormData.apartmentsPerFloor !== undefined && editFormData.apartmentsPerFloor !== "")
        updateData.apartmentsPerFloor = parseInt(editFormData.apartmentsPerFloor);
      if (editFormData.currentPropertyState !== undefined)
        updateData.currentPropertyState = editFormData.currentPropertyState;
      if (editFormData.readyForInspector !== undefined)
        updateData.readyForInspector = editFormData.readyForInspector;
      if (editFormData.currentRentalProcess !== undefined)
        updateData.currentRentalProcess = editFormData.currentRentalProcess || null;
      if (
        editFormData.currentRentalRevenue !== undefined &&
        editFormData.currentRentalRevenue !== ""
      )
        updateData.currentRentalRevenue = parseFloat(editFormData.currentRentalRevenue);
      if (editFormData.preferredInspectionTime !== undefined)
        updateData.preferredInspectionTime = editFormData.preferredInspectionTime?.trim() || null;
      if (editFormData.tour360Url !== undefined)
        updateData.tour360Url = editFormData.tour360Url?.trim() || "";
      if (editFormData.isInCompound !== undefined)
        updateData.isInCompound = editFormData.isInCompound;
      if (editFormData.compoundNameEn !== undefined)
        updateData.compoundNameEn = editFormData.compoundNameEn.trim();
      if (editFormData.compoundNameAr !== undefined)
        updateData.compoundNameAr = editFormData.compoundNameAr.trim();
      if (
        editFormData.compoundFacilities !== undefined &&
        Array.isArray(editFormData.compoundFacilities)
      ) {
        // Ensure compound facilities are in the correct format (array of objects with en/ar)
        updateData.compoundFacilities = editFormData.compoundFacilities.map((f) => {
          if (typeof f === "object" && (f.en || f.ar)) {
            return { en: f.en || f.ar || "", ar: f.ar || f.en || "" };
          }
          return f;
        });
      }
      if (editFormData.wifiName !== undefined) updateData.wifiName = editFormData.wifiName.trim();
      if (editFormData.wifiPassword !== undefined)
        updateData.wifiPassword = editFormData.wifiPassword.trim();
      if (editFormData.electricityMeterType !== undefined)
        updateData.electricityMeterType = editFormData.electricityMeterType;
      if (editFormData.waterMeterType !== undefined)
        updateData.waterMeterType = editFormData.waterMeterType;
      if (editFormData.propertyView !== undefined)
        updateData.propertyView = editFormData.propertyView;
      if (editFormData.accessCode !== undefined)
        updateData.accessCode = editFormData.accessCode.trim();
      if (editFormData.landmarkEn !== undefined)
        updateData.landmarkEn = editFormData.landmarkEn.trim();
      if (editFormData.landmarkAr !== undefined)
        updateData.landmarkAr = editFormData.landmarkAr.trim();
      if (editFormData.amenities !== undefined) updateData.amenities = editFormData.amenities;
      if (editFormData.criteria !== undefined) updateData.criteria = editFormData.criteria;

      // Media deletions
      if (imagesToDelete.length > 0) updateData.imagesToDelete = imagesToDelete;
      if (videosToDelete.length > 0) updateData.videosToDelete = videosToDelete;
      if (legalPapersToDelete.length > 0) updateData.legalPapersToDelete = legalPapersToDelete;
      if (buildingImagesToDelete.length > 0)
        updateData.buildingImagesToDelete = buildingImagesToDelete;

      // Only include insurancePrice and profitPercentage for Super Admin
      if (isSuperAdmin) {
        if (editFormData.profitPercentage !== undefined && editFormData.profitPercentage !== "") {
          updateData.profitPercentage = parseFloat(editFormData.profitPercentage);
        }
        if (editFormData.insurancePrice !== undefined && editFormData.insurancePrice !== "") {
          updateData.insurancePrice = parseFloat(editFormData.insurancePrice);
        }
      }

      // Prepare files object if there are new files to upload
      const filesToUpload = {};
      if (newImages.length > 0) filesToUpload.images = newImages;
      if (newVideos.length > 0) filesToUpload.videos = newVideos;
      if (newLegalPapers.length > 0) filesToUpload.legalPapers = newLegalPapers;
      if (newBuildingImages.length > 0) filesToUpload.buildingImages = newBuildingImages;
      if (newLivingRoomPhoto) filesToUpload.livingRoomPhoto = [newLivingRoomPhoto];
      if (newPropertyOwnerId) filesToUpload.propertyOwnerId = [newPropertyOwnerId];
      if (newRentalOrOwnershipContract)
        filesToUpload.rentalOrOwnershipContract = [newRentalOrOwnershipContract];
      if (newPowerOfAttorney) filesToUpload.powerOfAttorney = [newPowerOfAttorney];

      // Add room photos
      const roomPhotoFiles = Object.values(newRoomPhotos).filter(Boolean);
      if (roomPhotoFiles.length > 0) {
        filesToUpload.roomPhotos = roomPhotoFiles;
        // Also send room photo keys
        const roomPhotoKeys = Object.keys(newRoomPhotos).filter((key) => newRoomPhotos[key]);
        if (roomPhotoKeys.length > 0) {
          updateData.roomPhotoKeys = JSON.stringify(roomPhotoKeys);
        }
      }

      // Add bathroom photos
      const bathroomPhotoFiles = Object.values(newBathroomPhotos).filter(Boolean);
      if (bathroomPhotoFiles.length > 0) {
        filesToUpload.bathroomPhotos = bathroomPhotoFiles;
        const bathroomPhotoKeys = Object.keys(newBathroomPhotos).filter(
          (key) => newBathroomPhotos[key]
        );
        if (bathroomPhotoKeys.length > 0) {
          updateData.bathroomPhotoKeys = JSON.stringify(bathroomPhotoKeys);
        }
      }

      const balconyPhotoFiles = Object.values(newBalconyPhotos).filter(Boolean);
      if (balconyPhotoFiles.length > 0) {
        filesToUpload.balconyPhotos = balconyPhotoFiles;
        const balconyPhotoKeys = Object.keys(newBalconyPhotos).filter(
          (key) => newBalconyPhotos[key]
        );
        if (balconyPhotoKeys.length > 0) {
          updateData.balconyPhotoKeys = JSON.stringify(balconyPhotoKeys);
        }
      }

      // Handle deletions
      if (livingRoomPhotoToDelete) updateData.livingRoomPhotoToDelete = true;
      if (propertyOwnerIdToDelete) updateData.propertyOwnerIdToDelete = true;
      if (rentalOrOwnershipContractToDelete) updateData.rentalOrOwnershipContractToDelete = true;
      if (powerOfAttorneyToDelete) updateData.powerOfAttorneyToDelete = true;
      if (roomPhotosToDelete.length > 0) updateData.roomPhotosToDelete = roomPhotosToDelete;
      if (bathroomPhotosToDelete.length > 0)
        updateData.bathroomPhotosToDelete = bathroomPhotosToDelete;
      if (balconyPhotosToDelete.length > 0)
        updateData.balconyPhotosToDelete = balconyPhotosToDelete;

      const hasFiles = Object.keys(filesToUpload).length > 0;
      const response = await adminAPI.updatePropertyDetails(
        id,
        updateData,
        hasFiles ? filesToUpload : null
      );
      setProperty(response.property);
      setEditMode(false);
      setEditFormData({});
      setEditDialogOpen(false);
      setEditActiveStep(0);

      // Reset file uploads
      setNewImages([]);
      setNewVideos([]);
      setNewLegalPapers([]);
      setNewBuildingImages([]);
      setNewLivingRoomPhoto(null);
      setNewRoomPhotos({});
      setNewBathroomPhotos({});
      setNewBalconyPhotos({});
      setNewPropertyOwnerId(null);
      setNewRentalOrOwnershipContract(null);
      setNewPowerOfAttorney(null);
      setLivingRoomPhotoToDelete(false);
      setRoomPhotosToDelete([]);
      setBathroomPhotosToDelete([]);
      setBalconyPhotosToDelete([]);
      setPropertyOwnerIdToDelete(false);
      setRentalOrOwnershipContractToDelete(false);
      setPowerOfAttorneyToDelete(false);
      setImagesToDelete([]);
      setVideosToDelete([]);
      setLegalPapersToDelete([]);
      setBuildingImagesToDelete([]);
      // Reset media state
      setNewImages([]);
      setImagesToDelete([]);
      setNewVideos([]);
      setVideosToDelete([]);
      setNewLegalPapers([]);
      setLegalPapersToDelete([]);
      setNewBuildingImages([]);
      setBuildingImagesToDelete([]);
      setMessage({
        type: "success",
        text: "Property updated successfully",
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error updating property:", error);
      const errorMessage = error.message || "Failed to update property details";
      // Extract validation errors from response if available
      const validationError = error.data?.error || error.data?.message || errorMessage;
      setMessage({
        type: "error",
        text: validationError,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    } finally {
      setUpdatingProperty(false);
    }
  };

  // Fetch inspector calendar when inspector is selected
  useEffect(() => {
    const fetchCalendar = async () => {
      if (!selectedInspector) {
        setInspectorCalendar(null);
        return;
      }

      try {
        setLoadingCalendar(true);
        const response = await adminAPI.getInspectorCalendar(selectedInspector);
        setInspectorCalendar(response);
      } catch (error) {
        console.error("Error fetching inspector calendar:", error);
        setMessage({
          type: "error",
          text: error.message || "Failed to load inspector calendar.",
        });
      } finally {
        setLoadingCalendar(false);
      }
    };

    fetchCalendar();
  }, [selectedInspector]);

  // Check if selected date conflicts with inspector's schedule
  const checkDateConflict = (selectedDate, selectedEndDate) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections || !selectedDate) {
      return null;
    }

    const selectedDateTime = new Date(selectedDate);
    const selectedEndTime = selectedEndDate
      ? new Date(selectedEndDate)
      : new Date(selectedDateTime);

    const conflict = inspectorCalendar.inspections.find((inspection) => {
      const inspectionDate = new Date(inspection.inspectionDate);
      // Use a 1-hour default duration for existing inspections (just for conflict checking)
      const inspectionEndTime = new Date(inspectionDate);
      inspectionEndTime.setHours(inspectionEndTime.getHours() + 1);

      // Check if there's an overlap
      return (
        (selectedDateTime >= inspectionDate && selectedDateTime < inspectionEndTime) ||
        (selectedEndTime > inspectionDate && selectedEndTime <= inspectionEndTime) ||
        (selectedDateTime <= inspectionDate && selectedEndTime >= inspectionEndTime)
      );
    });

    return conflict || null;
  };

  // Handle inspector selection
  const handleInspectorChange = (inspectorId) => {
    setSelectedInspector(inspectorId);
    setAssignFormData((prev) => ({ ...prev, inspectionDate: "" }));
    setSelectedSlot(null); // Clear selected slot when changing inspector
  };

  // Handle date change
  const handleDateChange = (date) => {
    setAssignFormData((prev) => ({ ...prev, inspectionDate: date }));
    // Update selected slot when date is manually changed
    if (date) {
      const selectedDateTime = new Date(date);
      const endDateTime = new Date(selectedDateTime);
      endDateTime.setHours(endDateTime.getHours() + 1); // Use 1 hour default
      setSelectedSlot({
        start: selectedDateTime,
        end: endDateTime,
      });
    } else {
      setSelectedSlot(null);
    }
  };

  // Helper function to get multilingual text
  const getMultilingualText = (text, defaultText = "") => {
    if (!text) return defaultText;
    if (typeof text === "string") return text;
    if (typeof text === "object" && text.en) return text.en;
    if (typeof text === "object" && text.ar) return text.ar;
    return defaultText;
  };

  // Convert inspector inspections to calendar events
  const getCalendarEvents = () => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) return [];

    const events = inspectorCalendar.inspections.map((inspection) => {
      const startDate = new Date(inspection.inspectionDate);
      // Use 1-hour duration for display purposes only
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1);

      // Format time for display
      const timeStr = startDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      // Handle multilingual title
      const inspectionTitle = getMultilingualText(
        inspection.title,
        inspection.propertyTitle || `Inspection - ${timeStr}`
      );

      return {
        title: inspectionTitle,
        start: startDate,
        end: endDate,
        resource: {
          id: inspection.id,
          title: inspectionTitle,
          address: getMultilingualText(inspection.address, inspection.propertyAddress || "N/A"),
          city: getMultilingualText(inspection.city, inspection.propertyCity || "N/A"),
          location: inspection.inspectionLocation,
          status: inspection.status,
          isExisting: true, // Mark as existing inspection
        },
      };
    });

    // Add selected slot as a temporary event for visual feedback
    if (selectedSlot) {
      events.push({
        title: "Selected Time",
        start: selectedSlot.start,
        end: selectedSlot.end,
        resource: {
          id: "selected",
          isSelected: true,
        },
      });
    }

    return events;
  };

  // Helper function to format date for datetime-local input (local time)
  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Check if a slot is reserved
  const isSlotReserved = (slotStart, slotEnd) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return false;
    }

    return inspectorCalendar.inspections.some((inspection) => {
      const inspectionStart = new Date(inspection.inspectionDate);
      const inspectionEnd = new Date(inspectionStart);
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);

      // Check for overlap
      return (
        (slotStart >= inspectionStart && slotStart < inspectionEnd) ||
        (slotEnd > inspectionStart && slotEnd <= inspectionEnd) ||
        (slotStart <= inspectionStart && slotEnd >= inspectionEnd)
      );
    });
  };

  // Slot prop getter for calendar
  const slotPropGetter = (date) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return {};
    }

    // Round to the hour for consistent checking
    const slotStart = new Date(date);
    slotStart.setMinutes(0, 0, 0);
    const slotEnd = new Date(slotStart);
    slotEnd.setHours(slotEnd.getHours() + 1);

    // Check if this slot conflicts with any existing inspection
    const hasConflict = inspectorCalendar.inspections.some((inspection) => {
      const inspectionStart = new Date(inspection.inspectionDate);
      inspectionStart.setMinutes(0, 0, 0);
      const inspectionEnd = new Date(inspectionStart);
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);

      // Check for overlap - any overlap means the slot is reserved
      return (
        (slotStart.getTime() >= inspectionStart.getTime() &&
          slotStart.getTime() < inspectionEnd.getTime()) ||
        (slotEnd.getTime() > inspectionStart.getTime() &&
          slotEnd.getTime() <= inspectionEnd.getTime()) ||
        (slotStart.getTime() <= inspectionStart.getTime() &&
          slotEnd.getTime() >= inspectionEnd.getTime())
      );
    });

    if (hasConflict) {
      return {
        className: "rbc-slot-reserved",
        style: {
          backgroundColor: "rgba(244, 67, 54, 0.15)",
          border: "1px dashed rgba(244, 67, 54, 0.5)",
          cursor: "not-allowed",
          pointerEvents: "none",
          opacity: 0.7,
        },
      };
    }

    return {};
  };

  // Handle calendar slot selection (click on empty slot to create new inspection)
  const handleSelectSlot = ({ start, end }) => {
    // Use the start time from the calendar (this is the actual clicked slot)
    const clickedTime = new Date(start);

    // Round to the nearest hour (set minutes and seconds to 0)
    const selectedStart = new Date(clickedTime);
    selectedStart.setMinutes(0, 0, 0);

    // Set end time to exactly 1 hour after start
    const selectedEnd = new Date(selectedStart);
    selectedEnd.setHours(selectedEnd.getHours() + 1);

    // Check if the slot is already reserved
    if (isSlotReserved(selectedStart, selectedEnd)) {
      setMessage({
        type: "error",
        text: `This time slot (${selectedStart.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${selectedEnd.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })}) is already reserved. Please choose a different time.`,
      });
      setSelectedSlot(null);
      return;
    }

    // Check if the selected slot conflicts with existing inspections (double check)
    const conflict = checkDateConflict(selectedStart.toISOString(), selectedEnd.toISOString());
    if (conflict) {
      setMessage({
        type: "error",
        text: `This time slot conflicts with an existing inspection at ${new Date(
          conflict.inspectionDate
        ).toLocaleString()}. Please choose a different time.`,
      });
      setSelectedSlot(null);
      return;
    }

    // Format for datetime-local input (use start time in local timezone)
    // datetime-local expects format: YYYY-MM-DDTHH:mm in local time
    const formattedDateTime = formatDateTimeLocal(selectedStart);

    // Store selected slot for visual feedback
    setSelectedSlot({
      start: selectedStart,
      end: selectedEnd,
    });

    // Close any open modal when selecting a new slot
    setHoverModalOpen(false);

    setAssignFormData((prev) => ({
      ...prev,
      inspectionDate: formattedDateTime,
    }));

    setMessage({
      type: "success",
      text: `Time selected: ${selectedStart.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })} - ${selectedEnd.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}`,
    });
  };

  // Handle calendar event click (click on existing inspection)
  const handleSelectEvent = (event) => {
    // Prevent clicking on existing inspections
    if (event.resource?.isExisting) {
      setMessage({
        type: "info",
        text: `This time slot is already reserved for: ${event.resource.title || "Inspection"}`,
      });
      return;
    }

    if (event.resource?.isSelected) {
      // Clicking on selected slot - deselect it
      setSelectedSlot(null);
      setAssignFormData((prev) => ({ ...prev, inspectionDate: "" }));
      setMessage({ type: "", text: "" });
    }
  };

  // Calendar event style getter
  const eventStyleGetter = (event) => {
    // Selected slot (new selection)
    if (event.resource?.isSelected) {
      return {
        style: {
          backgroundColor: "#4CAF50",
          borderRadius: "6px",
          opacity: 1,
          color: "white",
          border: "2px solid #2e7d32",
          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.5)",
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          fontWeight: 600,
          fontSize: "0.875rem",
          minHeight: "32px",
          zIndex: 10,
          position: "relative",
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: "1.4",
          cursor: "pointer",
        },
      };
    }

    // Existing inspections (not clickable) - truncate text
    if (event.resource?.isExisting) {
      return {
        style: {
          backgroundColor: "#1976d2",
          borderRadius: "6px",
          opacity: 0.95,
          color: "white",
          border: "none",
          display: "flex",
          alignItems: "center",
          padding: "6px 10px",
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: "28px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "1.4",
          cursor: "pointer",
        },
      };
    }

    // Default style
    const style = {
      backgroundColor: "#1976d2",
      borderRadius: "6px",
      opacity: 0.95,
      color: "white",
      border: "none",
      display: "flex",
      alignItems: "center",
      padding: "6px 10px",
      fontWeight: 500,
      fontSize: "0.875rem",
      minHeight: "28px",
      whiteSpace: "normal",
      wordWrap: "break-word",
      lineHeight: "1.4",
    };
    return { style };
  };

  // Navigate calendar
  const navigateCalendar = (action) => {
    const newDate = new Date(calendarDate);
    if (calendarView === "month") {
      newDate.setMonth(
        action === "PREV" ? calendarDate.getMonth() - 1 : calendarDate.getMonth() + 1
      );
    } else if (calendarView === "week") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 7 : calendarDate.getDate() + 7);
    } else if (calendarView === "day") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 1 : calendarDate.getDate() + 1);
    }
    setCalendarDate(newDate);
  };

  // Handle view change
  const handleViewChange = (view) => {
    setCalendarView(view);
  };

  // Handle assign inspector
  const handleAssignInspector = async () => {
    if (
      !selectedInspector ||
      !assignFormData.inspectionDate ||
      !assignFormData.inspectionLocation.trim()
    ) {
      setMessage({
        type: "error",
        text: "Please fill in all required fields: Inspector, Date, and Location.",
      });
      return;
    }

    // Check for conflicts using selected slot times if available
    const inspectionStart = new Date(assignFormData.inspectionDate);
    const inspectionEnd = selectedSlot?.end
      ? new Date(selectedSlot.end)
      : new Date(inspectionStart);

    // If no end time from selected slot, use 1 hour default
    if (!selectedSlot?.end || inspectionEnd.getTime() === inspectionStart.getTime()) {
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);
    }

    const conflict = checkDateConflict(inspectionStart.toISOString(), inspectionEnd.toISOString());
    if (conflict) {
      setMessage({
        type: "error",
        text: `Inspector is already assigned to another inspection at ${new Date(
          conflict.inspectionDate
        ).toLocaleString()}. Please choose a different time.`,
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      // Convert local datetime to ISO string for backend (backend expects ISO format)
      // assignFormData.inspectionDate is in format "YYYY-MM-DDTHH:mm" (local time)
      // When we create a Date object from this, it interprets it as local time
      // Then toISOString() converts it to UTC for the backend
      const localDate = new Date(assignFormData.inspectionDate);

      // Validate the date is valid
      if (isNaN(localDate.getTime())) {
        setMessage({
          type: "error",
          text: "Invalid date format. Please select a valid date and time.",
        });
        setSubmitting(false);
        return;
      }

      const isoDateString = localDate.toISOString();

      const response = await adminAPI.assignInspector(property._id || property.id, {
        inspectorId: selectedInspector,
        inspectionDate: isoDateString,
        inspectionLocation: assignFormData.inspectionLocation.trim(),
      });

      if (response.message) {
        setMessage({
          type: "success",
          text: response.message,
        });

        // Refresh property data
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);

        setAssignDialogOpen(false);
        setSelectedInspector("");
        setSelectedSlot(null);
        setAssignFormData({
          inspectionDate: "",
          inspectionLocation: "",
        });
      }
    } catch (error) {
      console.error("Error assigning inspector:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to assign inspector. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle approve to Contracting (legacy/override at Inspection_Offer)
  const handleApproveToContracting = async () => {
    try {
      setApprovingToContracting(true);
      setMessage({ type: "", text: "" });
      const response = await adminAPI.approveToContracting(property._id || property.id);
      if (response.message) {
        setMessage({ type: "success", text: response.message });
        setApproveToContractingDialogOpen(false);
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to approve property." });
    } finally {
      setApprovingToContracting(false);
    }
  };

  // Handle approve to Inspection Offer (Inspection_Completed → draft contract + email)
  const handleApproveToInspectionOffer = async () => {
    try {
      setApprovingToInspectionOffer(true);
      setMessage({ type: "", text: "" });
      const response = await adminAPI.approveToInspectionOffer(property._id || property.id);
      if (response.message) {
        setMessage({ type: "success", text: response.message });
        setApproveToInspectionOfferDialogOpen(false);
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Failed to approve to Inspection Offer.",
      });
    } finally {
      setApprovingToInspectionOffer(false);
    }
  };

  // Handle move (Preparation, Legalization, Second Inspection, Approve second inspection)
  const handleMoveToPreparation = async () => {
    try {
      setMovingStatus(true);
      setMessage({ type: "", text: "" });
      const response = await adminAPI.moveToPreparation(property._id || property.id);
      if (response.message) {
        setMessage({ type: "success", text: response.message });
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to move to Preparation." });
    } finally {
      setMovingStatus(false);
    }
  };
  const handleMoveToLegalization = async () => {
    try {
      setMovingStatus(true);
      setMessage({ type: "", text: "" });
      const response = await adminAPI.moveToLegalization(property._id || property.id);
      if (response.message) {
        setMessage({ type: "success", text: response.message });
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to move to Legalization." });
    } finally {
      setMovingStatus(false);
    }
  };
  const handleMoveToSecondInspection = async () => {
    try {
      setMovingStatus(true);
      setMessage({ type: "", text: "" });
      const response = await adminAPI.moveToSecondInspection(property._id || property.id);
      if (response.message) {
        setMessage({ type: "success", text: response.message });
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to move to Second Inspection." });
    } finally {
      setMovingStatus(false);
    }
  };
  const handleApproveSecondInspection = async () => {
    try {
      setMovingStatus(true);
      setMessage({ type: "", text: "" });
      const response = await adminAPI.approveSecondInspection(property._id || property.id);
      if (response.message) {
        setMessage({ type: "success", text: response.message });
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to approve second inspection." });
    } finally {
      setMovingStatus(false);
    }
  };

  // Handle reject property
  const handleRejectProperty = async () => {
    if (!rejectReason.trim()) {
      setMessage({
        type: "error",
        text: "Please provide a rejection reason.",
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await adminAPI.rejectProperty(
        property._id || property.id,
        rejectReason.trim()
      );

      if (response.message) {
        setMessage({
          type: "success",
          text: response.message,
        });

        // Refresh property data
        const propertyResponse = await adminAPI.getPropertyById(id);
        setProperty(propertyResponse.property);

        setRejectDialogOpen(false);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error rejecting property:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to reject property. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate income projection (owner's share after company and booking website percentages)
  const missingItemsIncomeProjection = (() => {
    const pDay = parseFloat(missingItemsData.pricePerDay) || 0;
    const occ = (parseFloat(missingItemsData.occupancyRate) || 0) / 100;
    const compPct = (parseFloat(missingItemsData.companyPercentage) || 0) / 100;
    const bookPct = (parseFloat(missingItemsData.bookingWebsitePercentage) || 0) / 100;
    const grossPerDay = pDay * occ;
    const ownerShare = Math.max(0, 1 - compPct - bookPct);
    const incomePerDay = grossPerDay * ownerShare;
    return {
      incomePerDay,
      incomePerMonth: incomePerDay * 30,
      incomePerYear: incomePerDay * 365,
    };
  })();

  // Handle send missing items email
  const handleSendMissingItemsEmail = async () => {
    const validItems = (missingItemsData.items || [])
      .filter((i) => i && (i.name || i.item) && (i.name || i.item).trim())
      .map((i) => ({
        name: (i.name || i.item || "").trim(),
        quantity: i.quantity != null ? parseInt(i.quantity, 10) : 1,
        price: parseFloat(i.price) || 0,
      }));
    if (validItems.length === 0) {
      setMessage({ type: "error", text: "At least one item with a name is required." });
      return;
    }
    try {
      setSubmittingMissingItems(true);
      setMessage({ type: "", text: "" });
      await adminAPI.sendMissingItemsEmail(property._id || property.id, {
        items: validItems,
        expectedDate: missingItemsData.expectedDate || undefined,
        expectedDateApprovalToListing: missingItemsData.expectedDateApprovalToListing || undefined,
        customMessage: missingItemsData.customMessage?.trim() || undefined,
        pricePerDay: missingItemsData.pricePerDay
          ? parseFloat(missingItemsData.pricePerDay)
          : undefined,
        occupancyRate: missingItemsData.occupancyRate
          ? parseFloat(missingItemsData.occupancyRate)
          : undefined,
        companyPercentage: missingItemsData.companyPercentage
          ? parseFloat(missingItemsData.companyPercentage)
          : undefined,
        bookingWebsitePercentage: missingItemsData.bookingWebsitePercentage
          ? parseFloat(missingItemsData.bookingWebsitePercentage)
          : undefined,
      });
      setMessage({
        type: "success",
        text: "Missing items email sent successfully to property owner (PDF attached).",
      });
      setMissingItemsDialogOpen(false);
      setMissingItemsData({
        items: [],
        expectedDate: "",
        expectedDateApprovalToListing: "",
        customMessage: "",
        pricePerDay: "",
        occupancyRate: "",
        companyPercentage: "",
        bookingWebsitePercentage: "",
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Failed to send missing items email.",
      });
    } finally {
      setSubmittingMissingItems(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
      case "Active":
        return "success";
      case "Pending":
      case "Request_Submission":
        return "warning";
      case "Inspection":
      case "First_Inspection":
      case "Inspection_Completed":
      case "Inspection_Offer":
        return "info";
      case "Contracting":
      case "Preparation":
      case "Second_Inspection":
      case "Second_Inspection_Completed":
      case "Legalization":
      case "Photo_Shooting":
        return "info";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  const getOwnerStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Pending":
        return "warning";
      case "Suspended":
        return "error";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          py={3}
          px={{ xs: 2, sm: 3 }}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!property) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} px={{ xs: 2, sm: 3 }}>
          <Alert severity="error">Property not found.</Alert>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => navigate(isSuperAdmin ? "/super-admin/properties" : "/admin/properties")}
            fullWidth
            sx={{
              mt: 2,
              display: { xs: "flex", sm: "inline-flex" },
              minWidth: { xs: "100%", sm: "auto" },
            }}
          >
            Back to Properties
          </MDButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Check for date conflict for display purposes
  const inspectionStartForCheck = assignFormData.inspectionDate
    ? new Date(assignFormData.inspectionDate)
    : null;
  const inspectionEndForCheck = selectedSlot?.end
    ? new Date(selectedSlot.end)
    : inspectionStartForCheck
    ? new Date(inspectionStartForCheck)
    : null;
  if (
    inspectionEndForCheck &&
    inspectionStartForCheck &&
    inspectionEndForCheck.getTime() === inspectionStartForCheck.getTime()
  ) {
    inspectionEndForCheck.setHours(inspectionEndForCheck.getHours() + 1);
  }
  const dateConflict =
    inspectionStartForCheck && inspectionEndForCheck
      ? checkDateConflict(
          inspectionStartForCheck.toISOString(),
          inspectionEndForCheck.toISOString()
        )
      : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={{ xs: 2, sm: 3 }}>
        {/* Header */}
        <MDBox mb={4}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate(isSuperAdmin ? "/super-admin/properties" : "/admin/properties")}
            startIcon={<Icon>arrow_back</Icon>}
            fullWidth
            sx={{
              mb: 3,
              display: { xs: "flex", sm: "inline-flex" },
              minWidth: { xs: "100%", sm: "auto" },
            }}
          >
            Back to Properties
          </MDButton>

          <Card
            sx={{
              p: { xs: 2, sm: 4 },
              mb: 3,
              borderRadius: 4,
              boxShadow: "0 12px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
              background:
                "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 255, 1) 100%)",
              border: "1px solid",
              borderColor: "rgba(0,0,0,0.06)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)",
              },
            }}
          >
            <MDBox>
              {/* Row 1: Property name + ID chip only */}
              <MDBox
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
                mb={2}
                sx={{ minWidth: 0 }}
              >
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    lineHeight: 1.3,
                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    background: "linear-gradient(135deg, #1a237e 0%, #1976d2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {property.title?.en || property.title || "N/A"}
                </MDTypography>
                {property.shortId && (
                  <Chip
                    label={property.shortId}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      maxWidth: { xs: "100%", sm: "none" },
                      background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                      color: "white !important",
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      height: 28,
                      px: 1,
                      boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                      "& .MuiChip-label": {
                        color: "white !important",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: { xs: "120px", sm: "none" },
                      },
                    }}
                  />
                )}
              </MDBox>
              {/* Row 2: Arabic title if present */}
              {property.title?.ar && (
                <MDTypography
                  variant="h5"
                  fontWeight="600"
                  color="text.secondary"
                  mb={2}
                  sx={{
                    display: "block",
                    width: "100%",
                    direction: "rtl",
                    textAlign: "right",
                    lineHeight: 1.5,
                    opacity: 0.85,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  {property.title.ar}
                </MDTypography>
              )}
              {/* Row 3: Primary action buttons */}
              <MDBox
                display="flex"
                gap={2}
                alignItems="center"
                flexWrap="wrap"
                mb={3}
                sx={{
                  width: "100%",
                  "& .MuiButton-root": {
                    minWidth: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "center", sm: "flex-start" },
                  },
                }}
              >
                {/* Featured Toggle - Only show if property is Approved */}
                {property.status === "Approved" && (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    p={1.5}
                    borderRadius={2}
                    sx={{
                      bgcolor: property.featured ? "warning.light" : "grey.100",
                      border: "2px solid",
                      borderColor: property.featured ? "warning.main" : "grey.300",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={property.featured || false}
                          onChange={async (e) => {
                            const newFeatured = e.target.checked;
                            try {
                              setUpdatingFeatured(true);
                              setMessage({ type: "", text: "" });
                              const response = await adminAPI.updatePropertyDetails(
                                id,
                                { featured: newFeatured },
                                null
                              );
                              setProperty(response.property);
                              setMessage({
                                type: "success",
                                text: newFeatured
                                  ? "Property marked as featured"
                                  : "Property unfeatured",
                              });
                              setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                            } catch (error) {
                              console.error("Error updating featured status:", error);
                              setMessage({
                                type: "error",
                                text: error.message || "Failed to update featured status",
                              });
                              setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                            } finally {
                              setUpdatingFeatured(false);
                            }
                          }}
                          disabled={updatingFeatured}
                          color="warning"
                          size="medium"
                        />
                      }
                      label={
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon
                            sx={{ color: property.featured ? "warning.main" : "text.secondary" }}
                          >
                            {property.featured ? "star" : "star_border"}
                          </Icon>
                          <MDTypography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: property.featured ? "warning.main" : "text.secondary" }}
                          >
                            Featured
                          </MDTypography>
                        </MDBox>
                      }
                    />
                    {updatingFeatured && <CircularProgress size={20} sx={{ ml: 1 }} />}
                  </MDBox>
                )}

                {/* Inspection_Completed: Approve to Inspection Offer (draft contract + email). Inspection_Offer: waiting for owner. */}
                {(isAdmin || isSuperAdmin) && property.status === "Inspection_Completed" && (
                  <>
                    <MDButton
                      variant="gradient"
                      color="success"
                      size="large"
                      startIcon={<Icon>check_circle</Icon>}
                      onClick={() => setApproveToInspectionOfferDialogOpen(true)}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.75,
                        minHeight: "48px",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Approve to Inspection Offer
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="error"
                      size="large"
                      startIcon={<Icon>cancel</Icon>}
                      onClick={() => {
                        setRejectReason("");
                        setRejectDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.75,
                        minHeight: "48px",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Reject Property
                    </MDButton>
                    {/* Send Missing Items Email - show when there are missing items, or allow custom items */}
                    <MDButton
                      variant="outlined"
                      color="warning"
                      size="large"
                      startIcon={<Icon>email</Icon>}
                      onClick={() => {
                        const missing = property.inspectionFindings?.missingAmenities || [];
                        const items =
                          missing.length > 0
                            ? missing.map((m) => ({
                                name: typeof m === "string" ? m : m?.name || m?.en || m?.ar || "",
                                quantity: m && m.quantity != null ? m.quantity : 1,
                                price: m && m.price != null ? m.price : "",
                              }))
                            : [{ name: "", quantity: 1, price: "" }];
                        setMissingItemsData({
                          items,
                          expectedDate: "",
                          expectedDateApprovalToListing: "",
                          customMessage: "",
                          pricePerDay: "",
                          occupancyRate: "",
                          companyPercentage: "",
                          bookingWebsitePercentage: "",
                        });
                        setMissingItemsDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.75,
                        minHeight: "48px",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        borderWidth: 2,
                        "&:hover": { borderWidth: 2, transform: "translateY(-2px)" },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Send Missing Items Email
                    </MDButton>
                  </>
                )}
                {/* Inspection_Offer: Reject + Send Missing Items only (owner approves via dashboard) */}
                {(isAdmin || isSuperAdmin) && property.status === "Inspection_Offer" && (
                  <>
                    {property.contractResponse === "pending" && (
                      <MDTypography variant="body2" color="secondary" sx={{ fontWeight: 600 }}>
                        Waiting for owner response (Approve / Decline / Request meeting)
                      </MDTypography>
                    )}
                    {property.contractResponse === "meeting_requested" && (
                      <>
                        <MDTypography
                          variant="body2"
                          color="secondary"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Meeting requested
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          The property owner has requested a meeting. You can approve the contract
                          below to move the property to Contracting when ready.
                        </MDTypography>
                        <MDButton
                          variant="gradient"
                          color="success"
                          size="large"
                          startIcon={<Icon>check_circle</Icon>}
                          disabled={approvingToContracting}
                          onClick={handleApproveToContracting}
                          sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1.75,
                            minHeight: "48px",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            textTransform: "none",
                            mb: 2,
                          }}
                        >
                          {approvingToContracting ? "Processing…" : "Approve to Contract"}
                        </MDButton>
                      </>
                    )}
                    <MDButton
                      variant="gradient"
                      color="error"
                      size="large"
                      startIcon={<Icon>cancel</Icon>}
                      onClick={() => {
                        setRejectReason("");
                        setRejectDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.75,
                        minHeight: "48px",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Reject Property
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="warning"
                      size="large"
                      startIcon={<Icon>email</Icon>}
                      onClick={() => {
                        const missing = property.inspectionFindings?.missingAmenities || [];
                        const items =
                          missing.length > 0
                            ? missing.map((m) => ({
                                name: typeof m === "string" ? m : m?.name || m?.en || m?.ar || "",
                                quantity: m && m.quantity != null ? m.quantity : 1,
                                price: m && m.price != null ? m.price : "",
                              }))
                            : [{ name: "", quantity: 1, price: "" }];
                        setMissingItemsData({
                          items,
                          expectedDate: "",
                          expectedDateApprovalToListing: "",
                          customMessage: "",
                          pricePerDay: "",
                          occupancyRate: "",
                          companyPercentage: "",
                          bookingWebsitePercentage: "",
                        });
                        setMissingItemsDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.75,
                        minHeight: "48px",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textTransform: "none",
                        borderWidth: 2,
                        "&:hover": { borderWidth: 2, transform: "translateY(-2px)" },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Send Missing Items Email
                    </MDButton>
                  </>
                )}

                {/* Status Change Button - Show for Approved/Active (live states) */}
                {["Approved", "Active"].includes(property.status) && (
                  <MDButton
                    variant="outlined"
                    color={getStatusColor(property.status)}
                    size="large"
                    startIcon={<Icon>swap_horiz</Icon>}
                    onClick={() => {
                      setSelectedStatus(property.status);
                      setStatusDialogOpen(true);
                    }}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.75,
                      minHeight: "48px",
                      borderWidth: 2,
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      textTransform: "none",
                    }}
                  >
                    Change Status
                  </MDButton>
                )}

                {/* Accept/Reject Buttons for Pending/Request_Submission Properties - Hide if already inspected or approved */}
                {["Pending", "Request_Submission"].includes(property.status) &&
                  ![
                    "First_Inspection",
                    "Inspection",
                    "Inspection_Offer",
                    "Inspection_Completed",
                    "Approved",
                    "Rejected",
                    "Active",
                  ].includes(property.status) &&
                  !property.assignedInspector &&
                  !property.inspectionFindings && (
                    <>
                      <MDButton
                        variant="gradient"
                        color="success"
                        size="large"
                        startIcon={<Icon>check_circle</Icon>}
                        onClick={() => {
                          setSelectedInspector("");
                          setAssignDialogOpen(true);
                        }}
                        sx={{
                          borderRadius: 3,
                          px: 3,
                          py: 1.75,
                          minHeight: "48px",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          textTransform: "none",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Accept & Assign Inspector
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="error"
                        size="large"
                        startIcon={<Icon>cancel</Icon>}
                        onClick={() => {
                          setRejectReason("");
                          setRejectDialogOpen(true);
                        }}
                        sx={{
                          borderRadius: 3,
                          px: 3,
                          py: 1.75,
                          minHeight: "48px",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          textTransform: "none",
                          boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Reject Property
                      </MDButton>
                    </>
                  )}

                {/* Edit Property Button */}
                <MDButton
                  variant="gradient"
                  color="info"
                  size="large"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    initializeEditForm();
                    setEditDialogOpen(true);
                    setEditActiveStep(0);
                  }}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.75,
                    minHeight: "48px",
                    boxShadow: "0 8px 24px rgba(25, 118, 210, 0.4)",
                    background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                    "&:hover": {
                      boxShadow: "0 12px 32px rgba(25, 118, 210, 0.5)",
                      transform: "translateY(-2px)",
                      background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    fontWeight: 700,
                    fontSize: "1rem",
                    textTransform: "none",
                    letterSpacing: "0.5px",
                  }}
                >
                  Edit Property
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Second Inspection - Assign Inspector Alert */}
            {(isAdmin || isSuperAdmin) &&
              property.status === "Second_Inspection" &&
              !property.assignedInspector && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<Icon>assignment_ind</Icon>}>
                  <strong>Action Required:</strong> This property is ready for a second inspection.
                  Please assign an inspector to schedule and complete the second inspection.
                </Alert>
              )}

            {/* Second Inspection - Assign Inspector Alert */}
            {(isAdmin || isSuperAdmin) &&
              property.status === "Second_Inspection" &&
              !property.assignedInspector && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Assign Inspector Required:</strong> This property requires a second
                  inspection. Please assign an inspector below to schedule the second inspection.
                </Alert>
              )}

            {/* Second Inspection: Assign Inspector Alert */}
            {(isAdmin || isSuperAdmin) &&
              property.status === "Second_Inspection" &&
              !property.assignedInspector && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This property requires a second inspection. Please assign an inspector to schedule
                  the visit.
                </Alert>
              )}

            {/* Quick Actions - Admin and Super Admin */}
            {(isAdmin || isSuperAdmin) && (
              <MDBox mb={3}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(123, 31, 162, 0.05) 100%)",
                    border: "2px solid",
                    borderColor: "secondary.main",
                    boxShadow: "0 4px 20px rgba(156, 39, 176, 0.15)",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.5} mb={2.5} flexWrap="wrap">
                    <MDBox
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "secondary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ color: "white", fontSize: "1.5rem" }}>bolt</Icon>
                    </MDBox>
                    <MDTypography variant="h6" fontWeight="bold" color="secondary.main">
                      Quick Actions
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    display="flex"
                    gap={2}
                    flexWrap="wrap"
                    sx={{
                      "& .MuiButton-root": {
                        minWidth: { xs: "100%", sm: "auto" },
                        justifyContent: { xs: "center", sm: "flex-start" },
                      },
                    }}
                  >
                    {/* Assign/Change Inspector Button */}
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<Icon>assignment</Icon>}
                      onClick={() => {
                        if (property.assignedInspector) {
                          // If inspector is already assigned, pre-fill for change
                          const inspectorId =
                            property.assignedInspector._id || property.assignedInspector || "";
                          setSelectedInspector(inspectorId);

                          // Pre-fill inspection date and location if available
                          if (property.inspectionDate) {
                            const inspectionDate = new Date(property.inspectionDate);
                            const formattedDate = formatDateTimeLocal(inspectionDate);
                            setAssignFormData((prev) => ({
                              ...prev,
                              inspectionDate: formattedDate,
                              inspectionLocation:
                                property.inspectionLocation || prev.inspectionLocation || "",
                            }));

                            // Set selected slot for calendar
                            const selectedDateTime = new Date(inspectionDate);
                            selectedDateTime.setMinutes(0, 0, 0);
                            const endDateTime = new Date(selectedDateTime);
                            endDateTime.setHours(endDateTime.getHours() + 1);
                            setSelectedSlot({
                              start: selectedDateTime,
                              end: endDateTime,
                            });

                            // Set calendar date to inspection date
                            setCalendarDate(new Date(inspectionDate));
                          }
                        } else {
                          // Reset for new assignment
                          setSelectedInspector("");
                          setSelectedSlot(null);
                          setCalendarDate(new Date());
                        }
                        setAssignDialogOpen(true);
                      }}
                      sx={{
                        minHeight: "44px",
                        px: 3,
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {property.assignedInspector ? "Change Inspector" : "Assign Inspector"}
                    </MDButton>

                    {/* Assign/Change Check-In/Check-Out Agent Button - Only visible when inspection is complete */}
                    {["Inspection_Offer", "Inspection_Completed", "Approved", "Active"].includes(
                      property.status
                    ) && (
                      <MDButton
                        variant="gradient"
                        color="secondary"
                        startIcon={<Icon>person_pin</Icon>}
                        onClick={() => {
                          setSelectedCheckInOutAgentId(
                            property.assignedCheckInOutAgent?._id ||
                              property.assignedCheckInOutAgent ||
                              ""
                          );
                          setAssignCheckInOutAgentDialogOpen(true);
                        }}
                        sx={{
                          minHeight: "44px",
                          px: 3,
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(156, 39, 176, 0.4)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        {property.assignedCheckInOutAgent
                          ? "Change Check-In/Check-Out Agent"
                          : "Assign Check-In/Check-Out Agent"}
                      </MDButton>
                    )}
                    {/* Move to Preparation - Contracting only (no finance) */}
                    {isSuperAdmin && property.status === "Contracting" && (
                      <MDButton
                        variant="gradient"
                        color="success"
                        startIcon={<Icon>arrow_forward</Icon>}
                        onClick={handleMoveToPreparation}
                        disabled={movingStatus}
                        sx={{ minHeight: "44px", px: 3, borderRadius: 2 }}
                      >
                        {movingStatus ? "Moving..." : "Move to Preparation"}
                      </MDButton>
                    )}
                    {/* Move to Legalization - Preparation only */}
                    {isSuperAdmin && property.status === "Preparation" && (
                      <MDButton
                        variant="gradient"
                        color="info"
                        startIcon={<Icon>arrow_forward</Icon>}
                        onClick={handleMoveToLegalization}
                        disabled={movingStatus}
                        sx={{ minHeight: "44px", px: 3, borderRadius: 2 }}
                      >
                        {movingStatus ? "Moving..." : "Move to Legalization"}
                      </MDButton>
                    )}
                    {/* Move to Second Inspection - Legalization only */}
                    {isSuperAdmin && property.status === "Legalization" && (
                      <MDButton
                        variant="gradient"
                        color="info"
                        startIcon={<Icon>arrow_forward</Icon>}
                        onClick={handleMoveToSecondInspection}
                        disabled={movingStatus}
                        sx={{ minHeight: "44px", px: 3, borderRadius: 2 }}
                      >
                        {movingStatus ? "Moving..." : "Move to Second Inspection"}
                      </MDButton>
                    )}
                    {/* Approve second inspection - Second_Inspection_Completed → Photo_Shooting */}
                    {isSuperAdmin && property.status === "Second_Inspection_Completed" && (
                      <MDButton
                        variant="gradient"
                        color="success"
                        startIcon={<Icon>check_circle</Icon>}
                        onClick={handleApproveSecondInspection}
                        disabled={movingStatus}
                        sx={{ minHeight: "44px", px: 3, borderRadius: 2 }}
                      >
                        {movingStatus ? "Approving..." : "Approve Second Inspection"}
                      </MDButton>
                    )}
                    {/* Activate - Photo_Shooting only: opens Finance Modal; on Save calls activate with finance */}
                    {isSuperAdmin && property.status === "Photo_Shooting" && (
                      <MDButton
                        variant="gradient"
                        color="success"
                        startIcon={<Icon>play_arrow</Icon>}
                        onClick={async () => {
                          const propertyPackageIds = (property.packages || [])
                            .map((p) => (typeof p === "string" ? p : p._id || p.id))
                            .filter(Boolean);
                          setFinanceData({
                            price:
                              property.price !== undefined && property.price !== null
                                ? String(property.price)
                                : "",
                            profitPercentage:
                              property.profitPercentage !== undefined &&
                              property.profitPercentage !== null
                                ? String(property.profitPercentage)
                                : "",
                            insurancePrice:
                              property.insurancePrice !== undefined &&
                              property.insurancePrice !== null
                                ? String(property.insurancePrice)
                                : "",
                            maintenanceCostPercentage:
                              property.maintenanceCostPercentage !== undefined &&
                              property.maintenanceCostPercentage !== null
                                ? String(property.maintenanceCostPercentage)
                                : "",
                            maxGuests:
                              property.maxGuests !== undefined && property.maxGuests !== null
                                ? String(property.maxGuests)
                                : "",
                            extraFeesPerPerson:
                              property.extraFeesPerPerson !== undefined &&
                              property.extraFeesPerPerson !== null
                                ? String(property.extraFeesPerPerson)
                                : "",
                            earlyCheckInFeePercentage:
                              property.earlyCheckInFeePercentage !== undefined &&
                              property.earlyCheckInFeePercentage !== null
                                ? String(property.earlyCheckInFeePercentage)
                                : "",
                            lateCheckOutFeePercentage:
                              property.lateCheckOutFeePercentage !== undefined &&
                              property.lateCheckOutFeePercentage !== null
                                ? String(property.lateCheckOutFeePercentage)
                                : "",
                            packages: propertyPackageIds,
                            checkInOutAgentId:
                              property.assignedCheckInOutAgent?._id ||
                              property.assignedCheckInOutAgent ||
                              "",
                            expectedReadyDate: property.expectedReadyDate
                              ? new Date(property.expectedReadyDate).toISOString().slice(0, 10)
                              : "",
                          });
                          setFinanceDialogMode("activate");
                          setFinanceDialogOpen(true);
                          try {
                            setLoadingFinancePackages(true);
                            const [pkgRes, agentsRes] = await Promise.all([
                              adminAPI.getAllPackages({}),
                              adminAPI.getAvailableCheckInOutAgents(),
                            ]);
                            setFinancePackagesList(pkgRes.data || pkgRes.packages || []);
                            setCheckInOutAgents(agentsRes.agents || []);
                          } catch (err) {
                            setFinancePackagesList([]);
                            setCheckInOutAgents([]);
                          } finally {
                            setLoadingFinancePackages(false);
                          }
                        }}
                        sx={{
                          minHeight: "44px",
                          px: 3,
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Activate
                      </MDButton>
                    )}
                  </MDBox>
                </Card>
              </MDBox>
            )}

            <MDBox>
              <MDBox display="flex" alignItems="flex-start" gap={2} mb={2} flexWrap="wrap">
                <Chip
                  label={getStatusLabel(property.status, "admin", {
                    hasInspector: !!property.assignedInspector,
                  })}
                  color={getStatusColor(property.status)}
                  size="medium"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    height: 36,
                    px: 1.5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    flexShrink: 0,
                  }}
                />
                {property.featured && (
                  <Chip
                    icon={<Icon sx={{ color: "white !important" }}>star</Icon>}
                    label="Featured"
                    color="warning"
                    size="medium"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      height: 36,
                      px: 1.5,
                      boxShadow: "0 2px 8px rgba(237, 108, 2, 0.3)",
                      flexShrink: 0,
                    }}
                  />
                )}
                <MDBox
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  p={1.5}
                  borderRadius={2}
                  sx={{
                    minWidth: 0,
                    flex: "1 1 200px",
                    background:
                      "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.04) 100%)",
                    border: "1px solid",
                    borderColor: "rgba(25, 118, 210, 0.2)",
                    px: 2,
                  }}
                >
                  <Icon sx={{ fontSize: "1.25rem", color: "info.main", flexShrink: 0, mt: 0.25 }}>
                    location_on
                  </Icon>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="600"
                    sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                  >
                    {property.address?.en || property.address || "N/A"}
                    {property.city?.en || property.city
                      ? `, ${property.city?.en || property.city}`
                      : ""}
                    {property.country?.en || property.country
                      ? `, ${property.country?.en || property.country}`
                      : ""}
                  </MDTypography>
                </MDBox>
              </MDBox>

              {(property.address?.ar || property.city?.ar || property.country?.ar) && (
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1}
                  p={1.5}
                  borderRadius={2}
                  sx={{
                    minWidth: 0,
                    background:
                      "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.04) 100%)",
                    border: "1px solid",
                    borderColor: "rgba(25, 118, 210, 0.2)",
                    px: 2,
                    mb: 2,
                  }}
                >
                  <Icon sx={{ fontSize: "1.25rem", color: "info.main", flexShrink: 0 }}>
                    location_on
                  </Icon>
                  <MDTypography
                    variant="body2"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{
                      direction: "rtl",
                      textAlign: "right",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      minWidth: 0,
                    }}
                  >
                    {property.address?.ar || property.address || ""}
                    {property.city?.ar ? `، ${property.city.ar}` : ""}
                    {property.country?.ar ? `، ${property.country.ar}` : ""}
                  </MDTypography>
                </MDBox>
              )}

              {property.googleMapLink && (
                <MDBox mt={2.5}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="medium"
                    startIcon={<Icon>map</Icon>}
                    href={property.googleMapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      boxShadow: "0 4px 16px rgba(25, 118, 210, 0.3)",
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    View on Google Maps
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type === "success" ? "success" : "error"}
            sx={{
              mb: 3,
              borderRadius: 3,
              boxShadow:
                message.type === "success"
                  ? "0 4px 16px rgba(46, 125, 50, 0.2)"
                  : "0 4px 16px rgba(211, 47, 47, 0.2)",
              border: "1px solid",
              borderColor:
                message.type === "success" ? "rgba(46, 125, 50, 0.3)" : "rgba(211, 47, 47, 0.3)",
            }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            <MDTypography variant="body2" fontWeight="600">
              {message.text}
            </MDTypography>
          </Alert>
        )}

        {/* Tabbed Interface for Property Details */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 4,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid",
            borderColor: "rgba(0,0,0,0.06)",
            overflow: "hidden",
            background: "white",
          }}
        >
          <AppBar
            position="static"
            sx={{
              bgcolor: "white",
              boxShadow: "none",
              borderBottom: "2px solid",
              borderColor: "rgba(0,0,0,0.08)",
              background: "linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%)",
            }}
          >
            <Tabs
              value={detailsTab}
              onChange={(e, newValue) => setDetailsTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-flexContainer": {
                  flexWrap: "nowrap",
                },
                "& .MuiTab-root": {
                  minHeight: 72,
                  minWidth: 120,
                  flexShrink: 0,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "text.secondary",
                  padding: "12px 20px",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    color: "info.main",
                    bgcolor: "rgba(25, 118, 210, 0.04)",
                  },
                  "&.Mui-selected": {
                    color: "info.main",
                    fontWeight: 700,
                  },
                  "& .MuiTab-iconWrapper": {
                    marginRight: "8px",
                    marginBottom: "0 !important",
                  },
                },
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: "4px 4px 0 0",
                  background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                },
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab icon={<Icon>dashboard</Icon>} iconPosition="start" label="Overview" />
              <Tab icon={<Icon>info</Icon>} iconPosition="start" label="Property Details" />
              <Tab icon={<Icon>location_on</Icon>} iconPosition="start" label="Location" />
              <Tab icon={<Icon>attach_money</Icon>} iconPosition="start" label="Pricing" />
              <Tab icon={<Icon>wifi</Icon>} iconPosition="start" label="WiFi & Access" />
              <Tab icon={<Icon>business</Icon>} iconPosition="start" label="Building Info" />
              <Tab icon={<Icon>star</Icon>} iconPosition="start" label="Amenities" />
              <Tab icon={<Icon>checklist</Icon>} iconPosition="start" label="Criteria" />
              <Tab icon={<Icon>photo_library</Icon>} iconPosition="start" label="Media" />
              <Tab icon={<Icon>description</Icon>} iconPosition="start" label="Documents" />
              <Tab icon={<Icon>people</Icon>} iconPosition="start" label="Assignments" />
              <Tab icon={<Icon>person</Icon>} iconPosition="start" label="Owner Details" />
            </Tabs>
          </AppBar>

          <MDBox p={{ xs: 2, sm: 3, md: 4 }} sx={{ bgcolor: "#fafbfc" }}>
            {/* Overview Tab */}
            {detailsTab === 0 && (
              <MDBox sx={{ minWidth: 0 }}>
                <MDBox mb={{ xs: 3, sm: 4 }}>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    mb={1}
                    sx={{
                      background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Property Overview
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Comprehensive summary of property information and key metrics
                  </MDTypography>
                </MDBox>

                <Grid container spacing={3} sx={{ minWidth: 0 }}>
                  {/* Quick Stats */}
                  <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                    <Card
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        border: "2px solid",
                        borderColor: "rgba(25, 118, 210, 0.2)",
                        boxShadow: "0 4px 16px rgba(25, 118, 210, 0.15)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(25, 118, 210, 0.25)",
                        },
                        height: "100%",
                        minWidth: 0,
                      }}
                    >
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        textAlign="center"
                      >
                        <MDBox
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: "2rem" }}>bed</Icon>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="info.dark" mb={0.5}>
                          {property.rooms || "N/A"}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing={1}
                        >
                          Bedrooms
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                    <Card
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(46, 125, 50, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)",
                        border: "2px solid",
                        borderColor: "rgba(46, 125, 50, 0.2)",
                        boxShadow: "0 4px 16px rgba(46, 125, 50, 0.15)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(46, 125, 50, 0.25)",
                        },
                        height: "100%",
                        minWidth: 0,
                      }}
                    >
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        textAlign="center"
                      >
                        <MDBox
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            boxShadow: "0 4px 12px rgba(46, 125, 50, 0.3)",
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: "2rem" }}>bathroom</Icon>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="success.dark" mb={0.5}>
                          {property.bathrooms || "N/A"}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing={1}
                        >
                          Bathrooms
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                    <Card
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(237, 108, 2, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)",
                        border: "2px solid",
                        borderColor: "rgba(237, 108, 2, 0.2)",
                        boxShadow: "0 4px 16px rgba(237, 108, 2, 0.15)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(237, 108, 2, 0.25)",
                        },
                        height: "100%",
                        minWidth: 0,
                      }}
                    >
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        textAlign="center"
                      >
                        <MDBox
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            boxShadow: "0 4px 12px rgba(237, 108, 2, 0.3)",
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: "2rem" }}>square_foot</Icon>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="warning.dark" mb={0.5}>
                          {property.size ? `${property.size}` : "N/A"}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing={1}
                        >
                          Size (m²)
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                    <Card
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(186, 104, 200, 0.05) 100%)",
                        border: "2px solid",
                        borderColor: "rgba(156, 39, 176, 0.2)",
                        boxShadow: "0 4px 16px rgba(156, 39, 176, 0.15)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(156, 39, 176, 0.25)",
                        },
                        height: "100%",
                        minWidth: 0,
                      }}
                    >
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        textAlign="center"
                      >
                        <MDBox
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            background: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: "2rem" }}>attach_money</Icon>
                        </MDBox>
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color="secondary.dark"
                          mb={0.5}
                        >
                          {property.price ? `${(property.price / 1000).toFixed(0)}K` : "N/A"}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing={1}
                        >
                          Price/Night (EGP)
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* Description */}
                  {(property.description?.en || property.description?.ar) && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={property.description?.ar ? 6 : 12}
                        sx={{ minWidth: 0 }}
                      >
                        <Card
                          sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: 3,
                            height: "100%",
                            minWidth: 0,
                            background:
                              "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 255, 1) 100%)",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.08)",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1.5} mb={3}>
                            <MDBox
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Icon sx={{ color: "white", fontSize: "1.5rem" }}>description</Icon>
                            </MDBox>
                            <MDTypography variant="h6" fontWeight="bold" color="text">
                              Description (English)
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="body1"
                            color="text"
                            sx={{
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.9,
                              fontSize: "0.95rem",
                              color: "text.secondary",
                            }}
                          >
                            {property.description?.en ||
                              property.description ||
                              "No description available"}
                          </MDTypography>
                        </Card>
                      </Grid>

                      {property.description?.ar && (
                        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                          <Card
                            sx={{
                              p: { xs: 2, sm: 3 },
                              borderRadius: 3,
                              height: "100%",
                              minWidth: 0,
                              background:
                                "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 255, 1) 100%)",
                              border: "1px solid",
                              borderColor: "rgba(0,0,0,0.08)",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={1.5} mb={3}>
                              <MDBox
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2,
                                  background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Icon sx={{ color: "white", fontSize: "1.5rem" }}>description</Icon>
                              </MDBox>
                              <MDTypography variant="h6" fontWeight="bold" color="text">
                                Description (Arabic)
                              </MDTypography>
                            </MDBox>
                            <MDTypography
                              variant="body1"
                              color="text"
                              sx={{
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.9,
                                fontSize: "0.95rem",
                                color: "text.secondary",
                                direction: "rtl",
                                textAlign: "right",
                              }}
                            >
                              {property.description.ar}
                            </MDTypography>
                          </Card>
                        </Grid>
                      )}
                    </>
                  )}

                  {/* Status, Featured & Property Type */}
                  <Grid item xs={12} sx={{ minWidth: 0 }}>
                    <Card
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 255, 1) 100%)",
                        border: "1px solid",
                        borderColor: "rgba(0,0,0,0.08)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      }}
                    >
                      <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        mb={{ xs: 2, sm: 3 }}
                        color="text"
                      >
                        Property Information
                      </MDTypography>
                      <Grid container spacing={3}>
                        {/* Status */}
                        <Grid item xs={12} sm={4}>
                          <MDBox
                            p={2.5}
                            borderRadius={2}
                            sx={{
                              bgcolor: `${getStatusColor(property.status)}.light`,
                              border: "1px solid",
                              borderColor: `${getStatusColor(property.status)}.main`,
                              height: "100%",
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                              <Icon
                                color={getStatusColor(property.status)}
                                sx={{ fontSize: "1.5rem" }}
                              >
                                info
                              </Icon>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                                textTransform="uppercase"
                                letterSpacing={1}
                              >
                                Status
                              </MDTypography>
                            </MDBox>
                            <Chip
                              label={getStatusLabel(property.status, "admin", {
                                hasInspector: !!property.assignedInspector,
                              })}
                              color={getStatusColor(property.status)}
                              size="medium"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                height: 36,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            />
                          </MDBox>
                        </Grid>

                        {/* Featured */}
                        <Grid item xs={12} sm={4}>
                          <MDBox
                            p={2.5}
                            borderRadius={2}
                            sx={{
                              bgcolor: property.featured ? "warning.light" : "grey.100",
                              border: "1px solid",
                              borderColor: property.featured ? "warning.main" : "grey.300",
                              height: "100%",
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                              <Icon
                                color={property.featured ? "warning" : "default"}
                                sx={{ fontSize: "1.5rem" }}
                              >
                                {property.featured ? "star" : "star_border"}
                              </Icon>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                                textTransform="uppercase"
                                letterSpacing={1}
                              >
                                Featured
                              </MDTypography>
                            </MDBox>
                            <Chip
                              icon={<Icon>{property.featured ? "star" : "star_border"}</Icon>}
                              label={property.featured ? "Yes" : "No"}
                              color={property.featured ? "warning" : "default"}
                              size="medium"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                height: 36,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            />
                          </MDBox>
                        </Grid>

                        {/* Property Type */}
                        <Grid item xs={12} sm={4}>
                          <MDBox
                            p={2.5}
                            borderRadius={2}
                            sx={{
                              bgcolor: "info.light",
                              border: "1px solid",
                              borderColor: "info.main",
                              height: "100%",
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                              <Icon color="info" sx={{ fontSize: "1.5rem" }}>
                                category
                              </Icon>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                                textTransform="uppercase"
                                letterSpacing={1}
                              >
                                Property Type
                              </MDTypography>
                            </MDBox>
                            <Chip
                              label={
                                property.propertyType
                                  ? property.propertyType.charAt(0).toUpperCase() +
                                    property.propertyType.slice(1).replace(/_/g, " ")
                                  : "N/A"
                              }
                              color="info"
                              size="medium"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                height: 36,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            />
                          </MDBox>
                        </Grid>
                        {/* Owner-submitted fields (read-only) */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text.secondary"
                            mb={1.5}
                            display="block"
                          >
                            From property owner
                          </MDTypography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                              >
                                Current Property State
                              </MDTypography>
                              <MDTypography variant="body2" color="text" display="block">
                                {property.currentPropertyState || "—"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                              >
                                Ready for Inspection
                              </MDTypography>
                              <MDTypography variant="body2" color="text" display="block">
                                {property.readyForInspector === true
                                  ? "Yes"
                                  : property.readyForInspector === false
                                  ? "No"
                                  : "—"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                              >
                                Preferred Inspection Time
                              </MDTypography>
                              <MDTypography variant="body2" color="text" display="block">
                                {property.preferredInspectionTime || "—"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                              >
                                Current Rental Process
                              </MDTypography>
                              <MDTypography variant="body2" color="text" display="block">
                                {property.currentRentalProcess || "—"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="600"
                              >
                                Current Rental Revenue
                              </MDTypography>
                              <MDTypography variant="body2" color="text" display="block">
                                {property.currentRentalRevenue != null &&
                                property.currentRentalRevenue !== ""
                                  ? String(property.currentRentalRevenue)
                                  : "—"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  {/* Videos and Building Images - Quick view in Overview */}
                  {(property.videos?.length > 0 || property.buildingImages?.length > 0) && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">photo_library</Icon>
                          Videos & Building Images
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.videos?.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="button"
                                fontWeight="bold"
                                color="text"
                                mb={1}
                                display="block"
                              >
                                Videos ({property.videos.length})
                              </MDTypography>
                              <Grid container spacing={1}>
                                {property.videos.slice(0, 3).map((video, index) => (
                                  <Grid item xs={12} sm={4} key={index}>
                                    <MDButton
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      fullWidth
                                      startIcon={<Icon>play_arrow</Icon>}
                                      href={video}
                                      target="_blank"
                                    >
                                      Video {index + 1}
                                    </MDButton>
                                  </Grid>
                                ))}
                                {property.videos.length > 3 && (
                                  <Grid item xs={12}>
                                    <MDTypography variant="caption" color="text.secondary">
                                      + {property.videos.length - 3} more in Media tab
                                    </MDTypography>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          )}
                          {property.buildingImages?.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <MDTypography
                                variant="button"
                                fontWeight="bold"
                                color="text"
                                mb={1}
                                display="block"
                              >
                                Building Images ({property.buildingImages.length})
                              </MDTypography>
                              <Grid container spacing={1}>
                                {property.buildingImages.slice(0, 3).map((image, index) => {
                                  const imgUrl =
                                    typeof image === "string" ? image : image?.url || image;
                                  return (
                                    <Grid item xs={4} key={index}>
                                      <Box
                                        component="span"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                          openImageLightbox(
                                            property.buildingImages.map((i) =>
                                              typeof i === "string" ? i : i?.url || i
                                            ),
                                            index,
                                            "Building Images"
                                          )
                                        }
                                        onKeyDown={(e) =>
                                          e.key === "Enter" &&
                                          openImageLightbox(
                                            property.buildingImages.map((i) =>
                                              typeof i === "string" ? i : i?.url || i
                                            ),
                                            index,
                                            "Building Images"
                                          )
                                        }
                                        sx={{
                                          display: "block",
                                          width: "100%",
                                          height: 80,
                                          borderRadius: 1,
                                          overflow: "hidden",
                                          cursor: "pointer",
                                          "&:hover": { opacity: 0.9 },
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={imgUrl}
                                          alt={`Building ${index + 1}`}
                                          sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      </Box>
                                    </Grid>
                                  );
                                })}
                                {property.buildingImages.length > 3 && (
                                  <Grid item xs={12}>
                                    <MDTypography variant="caption" color="text.secondary">
                                      + {property.buildingImages.length - 3} more in Building Info
                                      tab
                                    </MDTypography>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                        <MDBox display="flex" gap={1} mt={2} flexWrap="wrap">
                          {property.videos?.length > 0 && (
                            <MDButton
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => setDetailsTab(8)}
                            >
                              View all videos
                            </MDButton>
                          )}
                          {property.buildingImages?.length > 0 && (
                            <MDButton
                              size="small"
                              color="info"
                              variant="outlined"
                              onClick={() => setDetailsTab(5)}
                            >
                              View all building images
                            </MDButton>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* Property Details Tab */}
            {detailsTab === 1 && (
              <MDBox sx={{ minWidth: 0 }}>
                <MDBox mb={{ xs: 3, sm: 4 }}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDTypography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      Property Details
                    </MDTypography>
                    {!editMode && (
                      <MDButton
                        variant="gradient"
                        color="info"
                        size="large"
                        startIcon={<Icon>edit</Icon>}
                        onClick={() => {
                          initializeEditForm();
                          setEditDialogOpen(true);
                          setEditActiveStep(0);
                        }}
                        sx={{
                          borderRadius: 2.5,
                          px: 4,
                          py: 1.5,
                          boxShadow: "0 6px 20px rgba(25, 118, 210, 0.35)",
                          background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                          "&:hover": {
                            boxShadow: "0 8px 24px rgba(25, 118, 210, 0.45)",
                            transform: "translateY(-2px)",
                            background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          textTransform: "none",
                        }}
                      >
                        Edit Property Details
                      </MDButton>
                    )}
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">
                    Complete property specifications and information
                  </MDTypography>
                </MDBox>

                <Grid container spacing={3} sx={{ minWidth: 0 }}>
                  {/* Helper function to render info card */}
                  {(() => {
                    const InfoCard = ({ icon, label, value, color = "info", xs = 12, md = 4 }) => {
                      const colorMap = {
                        info: {
                          main: "#1976d2",
                          light: "rgba(25, 118, 210, 0.1)",
                          gradient: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        },
                        success: {
                          main: "#2e7d32",
                          light: "rgba(46, 125, 50, 0.1)",
                          gradient: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)",
                        },
                        warning: {
                          main: "#ed6c02",
                          light: "rgba(237, 108, 2, 0.1)",
                          gradient: "linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)",
                        },
                        secondary: {
                          main: "#9c27b0",
                          light: "rgba(156, 39, 176, 0.1)",
                          gradient: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
                        },
                        primary: {
                          main: "#1976d2",
                          light: "rgba(25, 118, 210, 0.1)",
                          gradient: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        },
                      };
                      const colors = colorMap[color] || colorMap.info;

                      return (
                        <Grid item xs={xs} md={md} sx={{ minWidth: 0 }}>
                          <Card
                            sx={{
                              p: { xs: 2, sm: 3 },
                              borderRadius: 3,
                              height: "100%",
                              minWidth: 0,
                              background: `linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, ${colors.light} 100%)`,
                              border: "1px solid",
                              borderColor: "rgba(0,0,0,0.08)",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: `0 4px 16px ${colors.light}`,
                                borderColor: colors.main,
                              },
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={2} mb={1.5}>
                              <MDBox
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2.5,
                                  background: colors.gradient,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: `0 4px 12px ${colors.light}`,
                                }}
                              >
                                <Icon sx={{ color: "white", fontSize: "1.5rem" }}>{icon}</Icon>
                              </MDBox>
                              <MDBox flex={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="600"
                                  textTransform="uppercase"
                                  letterSpacing={0.5}
                                  fontSize="0.7rem"
                                >
                                  {label}
                                </MDTypography>
                                <MDTypography variant="h6" fontWeight="bold" color="text" mt={0.5}>
                                  {value || "N/A"}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </Card>
                        </Grid>
                      );
                    };

                    return (
                      <>
                        <InfoCard icon="bed" label="Bedrooms" value={property.rooms} color="info" />
                        <InfoCard
                          icon="bathroom"
                          label="Bathrooms"
                          value={property.bathrooms}
                          color="success"
                        />
                        {property.balconies > 0 && (
                          <InfoCard
                            icon="deck"
                            label="Balconies"
                            value={property.balconies}
                            color="secondary"
                          />
                        )}
                        <InfoCard
                          icon="square_foot"
                          label="Size"
                          value={property.size ? `${property.size} m²` : null}
                          color="warning"
                        />
                        <InfoCard
                          icon="layers"
                          label="Floors"
                          value={property.floors}
                          color="secondary"
                        />
                        <InfoCard
                          icon="stairs"
                          label="Floor Number"
                          value={property.floorNumber}
                          color="info"
                        />
                        <InfoCard
                          icon="category"
                          label="Property Type"
                          value={
                            property.propertyType
                              ? property.propertyType.charAt(0).toUpperCase() +
                                property.propertyType.slice(1)
                              : null
                          }
                          color="primary"
                        />
                        <InfoCard
                          icon="home"
                          label="Building Age"
                          value={property.buildingAge ? `${property.buildingAge} years` : null}
                          color="warning"
                        />
                        <InfoCard
                          icon="chair"
                          label="Furniture Age"
                          value={property.furnitureAge ? `${property.furnitureAge} years` : null}
                          color="secondary"
                        />
                        <InfoCard
                          icon="apartment"
                          label="Apartments per Floor"
                          value={property.apartmentsPerFloor}
                          color="info"
                        />
                        <InfoCard
                          icon="visibility"
                          label="Property View"
                          value={property.propertyView}
                          color="success"
                        />
                        <InfoCard
                          icon="bolt"
                          label="Electricity Meter"
                          value={property.electricityMeterType}
                          color="warning"
                        />
                        <InfoCard
                          icon="business"
                          label="In Compound"
                          value={property.isInCompound ? "Yes" : "No"}
                          color={property.isInCompound ? "success" : "default"}
                        />
                      </>
                    );
                  })()}

                  {/* Title and Description */}
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        p: 4,
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 255, 1) 100%)",
                        border: "1px solid",
                        borderColor: "rgba(0,0,0,0.08)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1.5} mb={3}>
                        <MDBox
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                          }}
                        >
                          <Icon sx={{ color: "white", fontSize: "1.5rem" }}>title</Icon>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color="text">
                          Property Titles
                        </MDTypography>
                      </MDBox>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={property.title?.ar ? 6 : 12}>
                          <MDBox
                            p={3}
                            borderRadius={2.5}
                            sx={{
                              background: "white",
                              border: "1px solid",
                              borderColor: "rgba(0,0,0,0.08)",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                              <Chip
                                label="English"
                                size="small"
                                sx={{
                                  bgcolor: "info.light",
                                  color: "info.dark",
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                }}
                              />
                            </MDBox>
                            <MDTypography
                              variant="h6"
                              fontWeight="600"
                              color="text"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {property.title?.en || property.title || "N/A"}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        {property.title?.ar && (
                          <Grid item xs={12} md={6}>
                            <MDBox
                              p={3}
                              borderRadius={2.5}
                              sx={{
                                background: "white",
                                border: "1px solid",
                                borderColor: "rgba(0,0,0,0.08)",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              }}
                            >
                              <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                                <Chip
                                  label="العربية"
                                  size="small"
                                  sx={{
                                    bgcolor: "info.light",
                                    color: "info.dark",
                                    fontWeight: "bold",
                                    fontSize: "0.7rem",
                                  }}
                                />
                              </MDBox>
                              <MDTypography
                                variant="h6"
                                fontWeight="600"
                                color="text"
                                sx={{ direction: "rtl", textAlign: "right", lineHeight: 1.6 }}
                              >
                                {property.title.ar}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                  </Grid>

                  {/* Descriptions */}
                  {(property.description?.en || property.description?.ar) && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">description</Icon>
                          Descriptions
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.description?.en && (
                            <Grid item xs={12} md={property.description?.ar ? 6 : 12}>
                              <MDBox p={2} borderRadius={1.5} bgcolor="white" minHeight={120}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                  mb={1}
                                >
                                  English
                                </MDTypography>
                                <MDTypography
                                  variant="body2"
                                  color="text"
                                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}
                                >
                                  {property.description.en}
                                </MDTypography>
                              </MDBox>
                            </Grid>
                          )}
                          {property.description?.ar && (
                            <Grid item xs={12} md={property.description?.en ? 6 : 12}>
                              <MDBox p={2} borderRadius={1.5} bgcolor="white" minHeight={120}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                  mb={1}
                                >
                                  Arabic
                                </MDTypography>
                                <MDTypography
                                  variant="body2"
                                  color="text"
                                  sx={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: 1.8,
                                    direction: "rtl",
                                    textAlign: "right",
                                  }}
                                >
                                  {property.description.ar}
                                </MDTypography>
                              </MDBox>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Compound Information */}
                  {property.isInCompound && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "success.light",
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="success">business</Icon>
                          Compound Information
                        </MDTypography>
                        <Grid container spacing={2}>
                          {(property.compoundName?.en || property.compoundName?.ar) && (
                            <>
                              {property.compoundName?.en && (
                                <Grid item xs={12} md={property.compoundName?.ar ? 6 : 12}>
                                  <MDBox p={2} borderRadius={1.5} bgcolor="white">
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      mb={0.5}
                                    >
                                      Compound Name (English)
                                    </MDTypography>
                                    <MDTypography variant="body1" fontWeight="600">
                                      {property.compoundName.en}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                              )}
                              {property.compoundName?.ar && (
                                <Grid item xs={12} md={property.compoundName?.en ? 6 : 12}>
                                  <MDBox p={2} borderRadius={1.5} bgcolor="white">
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      mb={0.5}
                                    >
                                      Compound Name (Arabic)
                                    </MDTypography>
                                    <MDTypography
                                      variant="body1"
                                      fontWeight="600"
                                      sx={{ direction: "rtl", textAlign: "right" }}
                                    >
                                      {property.compoundName.ar}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                              )}
                            </>
                          )}
                          {property.compoundFacilities &&
                            property.compoundFacilities.length > 0 && (
                              <Grid item xs={12}>
                                <MDBox p={2} borderRadius={1.5} bgcolor="white">
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    mb={1}
                                  >
                                    Compound Facilities
                                  </MDTypography>
                                  <MDBox display="flex" flexWrap="wrap" gap={1}>
                                    {property.compoundFacilities.map((facility, index) => (
                                      <Chip
                                        key={index}
                                        label={facility?.en || facility?.ar || facility}
                                        size="small"
                                        sx={{
                                          bgcolor: "success.light",
                                          color: "success.dark",
                                          fontWeight: "medium",
                                        }}
                                      />
                                    ))}
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Landmark */}
                  {(property.landmark?.en || property.landmark?.ar) && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">place</Icon>
                          Landmark
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.landmark?.en && (
                            <Grid item xs={12} md={property.landmark?.ar ? 6 : 12}>
                              <MDBox p={2} borderRadius={1.5} bgcolor="white">
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                  mb={0.5}
                                >
                                  English
                                </MDTypography>
                                <MDTypography variant="body1">{property.landmark.en}</MDTypography>
                              </MDBox>
                            </Grid>
                          )}
                          {property.landmark?.ar && (
                            <Grid item xs={12} md={property.landmark?.en ? 6 : 12}>
                              <MDBox p={2} borderRadius={1.5} bgcolor="white">
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight="medium"
                                  mb={0.5}
                                >
                                  Arabic
                                </MDTypography>
                                <MDTypography
                                  variant="body1"
                                  sx={{ direction: "rtl", textAlign: "right" }}
                                >
                                  {property.landmark.ar}
                                </MDTypography>
                              </MDBox>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* Location Tab */}
            {detailsTab === 2 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">location_on</Icon>
                  Location & Address
                </MDTypography>
                <Grid container spacing={3}>
                  {/* Address English */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "divider",
                        height: "100%",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        <Icon color="info">location_on</Icon>
                        <MDTypography variant="h6" fontWeight="bold">
                          Address (English)
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="body1" color="text" sx={{ lineHeight: 1.8 }}>
                        {property.address?.en || property.address || "N/A"}
                      </MDTypography>
                    </Card>
                  </Grid>

                  {/* Address Arabic */}
                  {property.address?.ar && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="info">location_on</Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Address (Arabic)
                          </MDTypography>
                        </MDBox>
                        <MDTypography
                          variant="body1"
                          color="text"
                          sx={{ lineHeight: 1.8, direction: "rtl", textAlign: "right" }}
                        >
                          {property.address.ar}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* City & Country */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "info.light",
                        border: "1px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        <Icon color="info">place</Icon>
                        <MDTypography variant="h6" fontWeight="bold">
                          City & Country
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" flexDirection="column" gap={1}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            English:
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="600">
                            {property.city?.en || property.city || "N/A"},{" "}
                            {property.country?.en || property.country || "N/A"}
                          </MDTypography>
                        </MDBox>
                        {(property.city?.ar || property.country?.ar) && (
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Arabic:
                            </MDTypography>
                            <MDTypography
                              variant="body1"
                              fontWeight="600"
                              sx={{ direction: "rtl", textAlign: "right" }}
                            >
                              {property.city?.ar || ""}
                              {property.city?.ar && property.country?.ar ? "، " : ""}
                              {property.country?.ar || ""}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* Google Maps Link */}
                  {property.googleMapLink && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "success.light",
                          border: "1px solid",
                          borderColor: "success.main",
                          height: "100%",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="success">map</Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Google Maps
                          </MDTypography>
                        </MDBox>
                        <MDButton
                          variant="gradient"
                          color="success"
                          startIcon={<Icon>open_in_new</Icon>}
                          href={property.googleMapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                        >
                          View on Google Maps
                        </MDButton>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* Pricing Tab */}
            {detailsTab === 3 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">attach_money</Icon>
                  Pricing & Packages
                </MDTypography>
                <Grid container spacing={3}>
                  {/* Price per Night - Super Admin Only */}
                  {isSuperAdmin && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "success.light",
                          border: "1px solid",
                          borderColor: "success.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="success" sx={{ fontSize: "2rem" }}>
                            attach_money
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Price per Night
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="success.dark">
                          {property.price ? `${property.price.toLocaleString()} EGP` : "Not Set"}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Profit Percentage */}
                  {isSuperAdmin && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "info.light",
                          border: "1px solid",
                          borderColor: "info.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="info" sx={{ fontSize: "2rem" }}>
                            percent
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Profit Percentage
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="info.dark">
                          {property.profitPercentage !== undefined &&
                          property.profitPercentage !== null
                            ? `${property.profitPercentage}%`
                            : "Not Set"}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Insurance Price - Super Admin Only */}
                  {isSuperAdmin && property.status === "Approved" && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "warning.light",
                          border: "1px solid",
                          borderColor: "warning.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="warning" sx={{ fontSize: "2rem" }}>
                            shield
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Insurance Price
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="warning.dark">
                          {property.insurancePrice !== undefined && property.insurancePrice !== null
                            ? `${property.insurancePrice.toLocaleString()} EGP`
                            : "Not Set"}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Discount - Super Admin Only */}
                  {isSuperAdmin && property.discount > 0 && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "secondary.light",
                          border: "1px solid",
                          borderColor: "secondary.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="secondary" sx={{ fontSize: "2rem" }}>
                            local_offer
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Discount
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="secondary.dark">
                          {property.discount}%
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Packages */}
                  {property.packages && property.packages.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">inventory</Icon>
                          Packages ({property.packages.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {packages
                            .filter(
                              (pkg) =>
                                property.packages.includes(pkg._id) ||
                                property.packages.some((id) => id.toString() === pkg._id.toString())
                            )
                            .map((pkg) => (
                              <Grid item xs={12} sm={6} md={4} key={pkg._id}>
                                <Card
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: "white",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
                                    {pkg.name?.en || pkg.name || "Package"}
                                  </MDTypography>
                                  <MDTypography variant="h6" fontWeight="bold" color="success.main">
                                    {pkg.price ? `${pkg.price.toLocaleString()} EGP` : "Free"}
                                  </MDTypography>
                                </Card>
                              </Grid>
                            ))}
                        </Grid>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* WiFi & Access Tab */}
            {detailsTab === 4 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">wifi</Icon>
                  WiFi & Access Information
                </MDTypography>
                <Grid container spacing={3}>
                  {/* WiFi Name */}
                  {property.wifiName && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "info.light",
                          border: "1px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="info" sx={{ fontSize: "2rem" }}>
                            wifi
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            WiFi Network Name
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h5" fontWeight="bold" color="info.dark">
                          {property.wifiName}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* WiFi Password */}
                  {property.wifiPassword && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "success.light",
                          border: "1px solid",
                          borderColor: "success.main",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="success" sx={{ fontSize: "2rem" }}>
                            lock
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            WiFi Password
                          </MDTypography>
                        </MDBox>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color="success.dark"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {property.wifiPassword}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Access Code */}
                  {property.accessCode && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "warning.light",
                          border: "1px solid",
                          borderColor: "warning.main",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="warning" sx={{ fontSize: "2rem" }}>
                            vpn_key
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Access Code
                          </MDTypography>
                        </MDBox>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color="warning.dark"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {property.accessCode}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Electricity Meter Type */}
                  {property.electricityMeterType && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "secondary.light",
                          border: "1px solid",
                          borderColor: "secondary.main",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="secondary" sx={{ fontSize: "2rem" }}>
                            bolt
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Electricity Meter
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color="secondary.dark">
                          {property.electricityMeterType}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Water Meter Type */}
                  {property.waterMeterType && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "primary.light",
                          border: "1px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Icon color="primary" sx={{ fontSize: "2rem" }}>
                            water_drop
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Water Meter
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color="primary.dark">
                          {property.waterMeterType}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* No WiFi/Access Info */}
                  {!property.wifiName &&
                    !property.wifiPassword &&
                    !property.accessCode &&
                    !property.electricityMeterType &&
                    !property.waterMeterType && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          No WiFi or access information available for this property.
                        </Alert>
                      </Grid>
                    )}
                </Grid>
              </MDBox>
            )}

            {/* Building Info Tab */}
            {detailsTab === 5 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">business</Icon>
                  Building Information
                </MDTypography>
                <Grid container spacing={3}>
                  {/* Building Age */}
                  {property.buildingAge && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "info.light",
                          border: "1px solid",
                          borderColor: "info.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="info" sx={{ fontSize: "2rem" }}>
                            home
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Building Age
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="info.dark">
                          {property.buildingAge} {property.buildingAge === 1 ? "Year" : "Years"}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Furniture Age */}
                  {property.furnitureAge && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "success.light",
                          border: "1px solid",
                          borderColor: "success.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="success" sx={{ fontSize: "2rem" }}>
                            chair
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Furniture Age
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="success.dark">
                          {property.furnitureAge} {property.furnitureAge === 1 ? "Year" : "Years"}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Floors */}
                  {property.floors && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "warning.light",
                          border: "1px solid",
                          borderColor: "warning.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="warning" sx={{ fontSize: "2rem" }}>
                            layers
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Total Floors
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="warning.dark">
                          {property.floors}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Floor Number */}
                  {property.floorNumber && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "secondary.light",
                          border: "1px solid",
                          borderColor: "secondary.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="secondary" sx={{ fontSize: "2rem" }}>
                            stairs
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Floor Number
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="secondary.dark">
                          {property.floorNumber}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Apartments per Floor */}
                  {property.apartmentsPerFloor && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "primary.light",
                          border: "1px solid",
                          borderColor: "primary.main",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="primary" sx={{ fontSize: "2rem" }}>
                            apartment
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Apartments per Floor
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h3" fontWeight="bold" color="primary.dark">
                          {property.apartmentsPerFloor}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Property View */}
                  {property.propertyView && (
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                          textAlign: "center",
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.5}
                          mb={2}
                        >
                          <Icon color="info" sx={{ fontSize: "2rem" }}>
                            visibility
                          </Icon>
                          <MDTypography variant="h6" fontWeight="bold">
                            Property View
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color="text">
                          {property.propertyView}
                        </MDTypography>
                      </Card>
                    </Grid>
                  )}

                  {/* Building Images */}
                  {property.buildingImages && property.buildingImages.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">photo_camera</Icon>
                          Building Images ({property.buildingImages.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.buildingImages.map((image, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper
                                elevation={2}
                                sx={{
                                  position: "relative",
                                  padding: 1,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                    transition: "transform 0.2s",
                                  },
                                }}
                                onClick={() =>
                                  openImageLightbox(
                                    property.buildingImages.map((i) =>
                                      typeof i === "string" ? i : i?.url || i
                                    ),
                                    index,
                                    "Building Images"
                                  )
                                }
                              >
                                <Box
                                  component="img"
                                  src={typeof image === "string" ? image : image?.url || image}
                                  alt={`Building image ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 200,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                  }}
                                />
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* Amenities Tab */}
            {detailsTab === 6 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">star</Icon>
                  Amenities & Features
                </MDTypography>
                <Grid container spacing={3}>
                  {property.amenities && property.amenities.length > 0 ? (
                    property.amenities.map((amenity, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: "info.light",
                            border: "1px solid",
                            borderColor: "info.main",
                            height: "100%",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1.5}>
                            <Icon color="info" sx={{ fontSize: "1.5rem" }}>
                              check_circle
                            </Icon>
                            <MDBox flex={1}>
                              <MDTypography variant="body1" fontWeight="bold" color="info.dark">
                                {amenity?.en || amenity?.ar || amenity}
                              </MDTypography>
                              {amenity?.ar && amenity?.en && (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    direction: "rtl",
                                    textAlign: "right",
                                    display: "block",
                                    mt: 0.5,
                                  }}
                                >
                                  {amenity.ar}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No amenities listed for this property.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* Criteria Tab */}
            {detailsTab === 7 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">checklist</Icon>
                  Inspection Criteria
                </MDTypography>
                <Grid container spacing={3}>
                  {property.criteria && property.criteria.length > 0 ? (
                    property.criteria.map((criterion, index) => (
                      <Grid item xs={12} key={index}>
                        <Card
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: criterion.verified ? "success.light" : "warning.light",
                            border: "1px solid",
                            borderColor: criterion.verified ? "success.main" : "warning.main",
                          }}
                        >
                          <MDBox
                            display="flex"
                            alignItems="start"
                            justifyContent="space-between"
                            gap={2}
                          >
                            <MDBox flex={1}>
                              <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                                <Icon color={criterion.verified ? "success" : "warning"}>
                                  {criterion.verified ? "check_circle" : "pending"}
                                </Icon>
                                <MDTypography variant="h6" fontWeight="bold">
                                  {criterion.name?.en || criterion.name || "Criterion"}
                                </MDTypography>
                                <Chip
                                  label={criterion.verified ? "Verified" : "Not Verified"}
                                  color={criterion.verified ? "success" : "warning"}
                                  size="small"
                                  sx={{ fontWeight: "bold" }}
                                />
                              </MDBox>
                              {criterion.name?.ar && (
                                <MDTypography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ direction: "rtl", textAlign: "right", mb: 1 }}
                                >
                                  {criterion.name.ar}
                                </MDTypography>
                              )}
                              {criterion.category?.en && (
                                <MDBox mb={1}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                  >
                                    Category:{" "}
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    component="span"
                                    fontWeight="medium"
                                  >
                                    {criterion.category.en}
                                  </MDTypography>
                                </MDBox>
                              )}
                              {criterion.notes?.en && (
                                <MDBox mt={1} p={1.5} borderRadius={1} bgcolor="white">
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                  >
                                    Notes:{" "}
                                  </MDTypography>
                                  <MDTypography variant="body2" color="text">
                                    {criterion.notes.en}
                                  </MDTypography>
                                </MDBox>
                              )}
                              {criterion.verifiedAt && (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 1 }}
                                >
                                  Verified: {formatDate(criterion.verifiedAt)}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No inspection criteria available for this property.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            )}

            {/* Media Tab */}
            {detailsTab === 8 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">photo_library</Icon>
                  Media Gallery
                </MDTypography>
                <Grid container spacing={3}>
                  {/* Property Images */}
                  {property.images && property.images.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">image</Icon>
                          Property Images ({property.images.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.images.map((image, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper
                                elevation={2}
                                sx={{
                                  position: "relative",
                                  padding: 1,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  "&:hover": {
                                    transform: "scale(1.02)",
                                    transition: "transform 0.2s",
                                  },
                                }}
                                onClick={() =>
                                  openImageLightbox(property.images, index, "Property Images")
                                }
                              >
                                <Box
                                  component="img"
                                  src={image}
                                  alt={`Property image ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: 200,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                  }}
                                />
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Living Room Photo */}
                  {property.livingRoomPhoto && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">living</Icon>
                          Living Room Photo
                        </MDTypography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Paper
                              elevation={2}
                              sx={{
                                position: "relative",
                                padding: 1,
                                borderRadius: 2,
                                overflow: "hidden",
                                cursor: "pointer",
                                "&:hover": {
                                  transform: "scale(1.02)",
                                  transition: "transform 0.2s",
                                },
                              }}
                              onClick={() =>
                                openImageLightbox(
                                  [property.livingRoomPhoto],
                                  0,
                                  "Living Room Photo"
                                )
                              }
                            >
                              <Box
                                component="img"
                                src={property.livingRoomPhoto}
                                alt="Living Room"
                                sx={{
                                  width: "100%",
                                  height: 200,
                                  objectFit: "cover",
                                  borderRadius: 1,
                                }}
                                onError={(e) => {
                                  console.error(
                                    "Failed to load living room photo:",
                                    property.livingRoomPhoto
                                  );
                                  e.target.style.display = "none";
                                }}
                              />
                            </Paper>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Room Photos */}
                  {property.roomPhotos && property.roomPhotos.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">bed</Icon>
                          Room Photos ({property.roomPhotos.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.roomPhotos.map((roomPhoto, index) => {
                            const photoUrl =
                              typeof roomPhoto === "object" && roomPhoto.photoUrl
                                ? roomPhoto.photoUrl
                                : typeof roomPhoto === "string"
                                ? roomPhoto
                                : null;
                            const roomNumber =
                              typeof roomPhoto === "object" && roomPhoto.roomNumber
                                ? roomPhoto.roomNumber
                                : index + 1;

                            if (!photoUrl) return null;

                            return (
                              <Grid item xs={12} sm={6} md={4} key={`room-${index}`}>
                                <Paper
                                  elevation={2}
                                  sx={{
                                    position: "relative",
                                    padding: 1,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "scale(1.02)",
                                      transition: "transform 0.2s",
                                    },
                                  }}
                                  onClick={() => {
                                    const urls = property.roomPhotos.map((p) =>
                                      typeof p === "object" && p?.photoUrl ? p.photoUrl : p
                                    );
                                    const idx = urls.indexOf(photoUrl);
                                    openImageLightbox(urls, idx >= 0 ? idx : index, "Room Photos");
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={photoUrl}
                                    alt={`Room ${roomNumber}`}
                                    sx={{
                                      width: "100%",
                                      height: 200,
                                      objectFit: "cover",
                                      borderRadius: 1,
                                    }}
                                    onError={(e) => {
                                      console.error("Failed to load room photo:", photoUrl);
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <MDBox p={1}>
                                    <MDTypography
                                      variant="body2"
                                      fontWeight="medium"
                                      textAlign="center"
                                    >
                                      Room {roomNumber}
                                    </MDTypography>
                                  </MDBox>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Balcony Photos */}
                  {property.balconyPhotos && property.balconyPhotos.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">deck</Icon>
                          Balcony Photos ({property.balconyPhotos.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.balconyPhotos.map((balconyPhoto, index) => {
                            const photoUrl =
                              typeof balconyPhoto === "object" && balconyPhoto.photoUrl
                                ? balconyPhoto.photoUrl
                                : typeof balconyPhoto === "string"
                                ? balconyPhoto
                                : null;
                            const balconyNumber =
                              typeof balconyPhoto === "object" && balconyPhoto.balconyNumber
                                ? balconyPhoto.balconyNumber
                                : index + 1;
                            if (!photoUrl) return null;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={`balcony-${index}`}>
                                <Paper
                                  elevation={2}
                                  sx={{
                                    position: "relative",
                                    padding: 1,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "scale(1.02)",
                                      transition: "transform 0.2s",
                                    },
                                  }}
                                  onClick={() => {
                                    const urls = property.balconyPhotos.map((p) =>
                                      typeof p === "object" && p?.photoUrl ? p.photoUrl : p
                                    );
                                    const idx = urls.indexOf(photoUrl);
                                    openImageLightbox(
                                      urls,
                                      idx >= 0 ? idx : index,
                                      "Balcony Photos"
                                    );
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={photoUrl}
                                    alt={`Balcony ${balconyNumber}`}
                                    sx={{
                                      width: "100%",
                                      height: 200,
                                      objectFit: "cover",
                                      borderRadius: 1,
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <MDBox p={1}>
                                    <MDTypography
                                      variant="body2"
                                      fontWeight="medium"
                                      textAlign="center"
                                    >
                                      Balcony {balconyNumber}
                                    </MDTypography>
                                  </MDBox>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Bathroom Photos */}
                  {property.bathroomPhotos && property.bathroomPhotos.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">bathroom</Icon>
                          Bathroom Photos ({property.bathroomPhotos.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.bathroomPhotos.map((bathroomPhoto, index) => {
                            const photoUrl =
                              typeof bathroomPhoto === "object" && bathroomPhoto.photoUrl
                                ? bathroomPhoto.photoUrl
                                : typeof bathroomPhoto === "string"
                                ? bathroomPhoto
                                : null;
                            const bathroomNumber =
                              typeof bathroomPhoto === "object" && bathroomPhoto.bathroomNumber
                                ? bathroomPhoto.bathroomNumber
                                : index + 1;

                            if (!photoUrl) return null;

                            return (
                              <Grid item xs={12} sm={6} md={4} key={`bathroom-${index}`}>
                                <Paper
                                  elevation={2}
                                  sx={{
                                    position: "relative",
                                    padding: 1,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "scale(1.02)",
                                      transition: "transform 0.2s",
                                    },
                                  }}
                                  onClick={() => {
                                    const urls = property.bathroomPhotos.map((p) =>
                                      typeof p === "object" && p?.photoUrl ? p.photoUrl : p
                                    );
                                    const idx = urls.indexOf(photoUrl);
                                    openImageLightbox(
                                      urls,
                                      idx >= 0 ? idx : index,
                                      "Bathroom Photos"
                                    );
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={photoUrl}
                                    alt={`Bathroom ${bathroomNumber}`}
                                    sx={{
                                      width: "100%",
                                      height: 200,
                                      objectFit: "cover",
                                      borderRadius: 1,
                                    }}
                                    onError={(e) => {
                                      console.error("Failed to load bathroom photo:", photoUrl);
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <MDBox p={1}>
                                    <MDTypography
                                      variant="body2"
                                      fontWeight="medium"
                                      textAlign="center"
                                    >
                                      Bathroom {bathroomNumber}
                                    </MDTypography>
                                  </MDBox>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Videos */}
                  {property.videos && property.videos.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">videocam</Icon>
                          Videos ({property.videos.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.videos.map((video, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                                  <Icon color="error" sx={{ fontSize: "2rem" }}>
                                    videocam
                                  </Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Video {index + 1}
                                  </MDTypography>
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>play_arrow</Icon>}
                                  href={video}
                                  target="_blank"
                                >
                                  Play Video
                                </MDButton>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* No Media */}
                  {(!property.images || property.images.length === 0) &&
                    (!property.videos || property.videos.length === 0) &&
                    !property.livingRoomPhoto &&
                    (!property.roomPhotos || property.roomPhotos.length === 0) &&
                    (!property.bathroomPhotos || property.bathroomPhotos.length === 0) &&
                    (!property.balconyPhotos || property.balconyPhotos.length === 0) &&
                    (!property.buildingImages || property.buildingImages.length === 0) && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          No media available for this property.
                        </Alert>
                      </Grid>
                    )}
                </Grid>
              </MDBox>
            )}

            {/* Documents Tab */}
            {detailsTab === 9 && (
              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">description</Icon>
                  Documents
                </MDTypography>
                <Grid container spacing={3}>
                  {/* Property Owner Uploaded Documents */}
                  {(property.propertyOwnerId ||
                    property.rentalOrOwnershipContract ||
                    property.powerOfAttorney) && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="primary">badge</Icon>
                          Property Owner Uploaded Documents
                        </MDTypography>
                        <Grid container spacing={2}>
                          {/* Property Owner ID */}
                          {property.propertyOwnerId && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                  <Icon color="primary" sx={{ fontSize: "2rem" }}>
                                    badge
                                  </Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Property Owner ID
                                  </MDTypography>
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>visibility</Icon>}
                                  onClick={() => {
                                    setPdfLightboxUrl(fixPdfUrl(property.propertyOwnerId));
                                    setPdfLightboxTitle("Property Owner ID");
                                    setPdfLightboxOpen(true);
                                  }}
                                >
                                  View Document
                                </MDButton>
                              </Card>
                            </Grid>
                          )}

                          {/* Rental or Ownership Contract */}
                          {property.rentalOrOwnershipContract && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                  <Icon color="success" sx={{ fontSize: "2rem" }}>
                                    description
                                  </Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Rental/Ownership Contract
                                  </MDTypography>
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="success"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>visibility</Icon>}
                                  onClick={() => {
                                    setPdfLightboxUrl(
                                      fixPdfUrl(property.rentalOrOwnershipContract)
                                    );
                                    setPdfLightboxTitle("Rental or Ownership Contract");
                                    setPdfLightboxOpen(true);
                                  }}
                                >
                                  View Document
                                </MDButton>
                              </Card>
                            </Grid>
                          )}

                          {/* Power of Attorney */}
                          {property.powerOfAttorney && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                  <Icon color="warning" sx={{ fontSize: "2rem" }}>
                                    gavel
                                  </Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Power of Attorney
                                  </MDTypography>
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>visibility</Icon>}
                                  onClick={() => {
                                    setPdfLightboxUrl(fixPdfUrl(property.powerOfAttorney));
                                    setPdfLightboxTitle("Power of Attorney");
                                    setPdfLightboxOpen(true);
                                  }}
                                >
                                  View Document
                                </MDButton>
                              </Card>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Legal Papers */}
                  {property.legalPapers && property.legalPapers.length > 0 && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">gavel</Icon>
                          Legal Papers ({property.legalPapers.length})
                        </MDTypography>
                        <Grid container spacing={2}>
                          {property.legalPapers.map((doc, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                  <Icon color="error" sx={{ fontSize: "2rem" }}>
                                    description
                                  </Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Legal Paper {index + 1}
                                  </MDTypography>
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>visibility</Icon>}
                                  onClick={() => {
                                    setPdfLightboxUrl(doc);
                                    setPdfLightboxTitle(`Legal Paper ${index + 1}`);
                                    setPdfLightboxOpen(true);
                                  }}
                                >
                                  View Document
                                </MDButton>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  {/* Official Documents - Super Admin can manage */}
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon color="info">folder</Icon>
                          Official Documents ({property.officialDocuments?.length || 0})
                        </MDTypography>
                        {isSuperAdmin && (
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            startIcon={<Icon>upload_file</Icon>}
                            onClick={() => {
                              setOfficialDocumentsFiles([]);
                              setOfficialDocumentsDialogOpen(true);
                            }}
                          >
                            Add Documents
                          </MDButton>
                        )}
                      </MDBox>
                      {property.officialDocuments && property.officialDocuments.length > 0 ? (
                        <Grid container spacing={2}>
                          {property.officialDocuments.map((doc, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  mb={1.5}
                                >
                                  <MDBox display="flex" alignItems="center" gap={1.5}>
                                    <Icon color="info" sx={{ fontSize: "2rem" }}>
                                      description
                                    </Icon>
                                    <MDTypography variant="body2" fontWeight="medium">
                                      Document {index + 1}
                                    </MDTypography>
                                  </MDBox>
                                  {isSuperAdmin && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={async () => {
                                        if (
                                          window.confirm(
                                            "Are you sure you want to delete this document?"
                                          )
                                        ) {
                                          try {
                                            setMessage({ type: "", text: "" });
                                            // Send the document URL to delete
                                            const docToDelete = property.officialDocuments[index];
                                            const response = await adminAPI.updatePropertyDetails(
                                              id,
                                              {
                                                officialDocumentsToDelete: JSON.stringify([
                                                  docToDelete,
                                                ]),
                                              }
                                            );
                                            if (response.property || response.success) {
                                              setMessage({
                                                type: "success",
                                                text: "Document deleted successfully.",
                                              });
                                              // Refresh property data
                                              const propertyResponse =
                                                await adminAPI.getPropertyById(id);
                                              setProperty(propertyResponse.property);
                                              setTimeout(
                                                () => setMessage({ type: "", text: "" }),
                                                3000
                                              );
                                            }
                                          } catch (error) {
                                            console.error("Error deleting document:", error);
                                            setMessage({
                                              type: "error",
                                              text:
                                                error.message ||
                                                "Failed to delete document. Please try again.",
                                            });
                                            setTimeout(
                                              () => setMessage({ type: "", text: "" }),
                                              3000
                                            );
                                          }
                                        }
                                      }}
                                    >
                                      <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                  )}
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>visibility</Icon>}
                                  onClick={() => {
                                    setPdfLightboxUrl(doc);
                                    setPdfLightboxTitle(`Official Document ${index + 1}`);
                                    setPdfLightboxOpen(true);
                                  }}
                                >
                                  View Document
                                </MDButton>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          {isSuperAdmin
                            ? "No official documents uploaded. Click 'Add Documents' to upload official documents."
                            : "No official documents available for this property."}
                        </Alert>
                      )}
                    </Card>
                  </Grid>

                  {/* No Documents */}
                  {!property.propertyOwnerId &&
                    !property.rentalOrOwnershipContract &&
                    !property.powerOfAttorney &&
                    (!property.legalPapers || property.legalPapers.length === 0) &&
                    (!property.officialDocuments || property.officialDocuments.length === 0) && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          No documents available for this property.
                        </Alert>
                      </Grid>
                    )}
                </Grid>
              </MDBox>
            )}

            {/* Assignments Tab */}
            {detailsTab === 10 && (
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={{ xs: 2, sm: 3 }}
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Icon color="info">people</Icon>
                  Assignments & Agents
                </MDTypography>
                <Grid container spacing={3} sx={{ minWidth: 0 }}>
                  {/* Inspector Assignment */}
                  {property.assignedInspector && (
                    <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                      <Card
                        sx={{
                          p: 0,
                          borderRadius: 3,
                          overflow: "hidden",
                          boxShadow:
                            "0 8px 24px rgba(25, 118, 210, 0.12), 0 2px 8px rgba(25, 118, 210, 0.08)",
                          border: "1px solid",
                          borderColor: "info.main",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow:
                              "0 12px 32px rgba(25, 118, 210, 0.16), 0 4px 12px rgba(25, 118, 210, 0.12)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {/* Header Section */}
                        <MDBox
                          sx={{
                            p: { xs: 2, sm: 2.5 },
                            background:
                              "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(33, 150, 243, 0.15) 100%)",
                            borderBottom: "2px solid",
                            borderColor: "info.main",
                          }}
                        >
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={1.5}
                          >
                            <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                              <MDBox
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2,
                                  bgcolor: "info.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                                }}
                              >
                                <Icon sx={{ color: "white", fontSize: "1.75rem" }}>
                                  assignment_ind
                                </Icon>
                              </MDBox>
                              <MDBox>
                                <MDTypography variant="h6" fontWeight="bold" color="info.main">
                                  Assigned Inspector
                                </MDTypography>
                                <MDTypography variant="caption" color="text.secondary">
                                  Property Inspector
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            {isSuperAdmin && (
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<Icon>edit</Icon>}
                                onClick={() => {
                                  setSelectedInspector(
                                    property.assignedInspector._id ||
                                      property.assignedInspector ||
                                      ""
                                  );
                                  setAssignDialogOpen(true);
                                }}
                                sx={{
                                  borderRadius: 2,
                                  minHeight: "36px",
                                  px: 2,
                                  borderWidth: 1.5,
                                  fontWeight: 600,
                                  textTransform: "none",
                                  flexShrink: 0,
                                  "&:hover": {
                                    borderWidth: 1.5,
                                    bgcolor: "info.light",
                                  },
                                }}
                              >
                                Change
                              </MDButton>
                            )}
                          </MDBox>
                        </MDBox>

                        {/* Content Section */}
                        <MDBox p={{ xs: 2, sm: 2.5, md: 3 }}>
                          {/* Inspector Info */}
                          <MDBox
                            mb={3}
                            p={{ xs: 2, sm: 2.5 }}
                            sx={{
                              borderRadius: 2,
                              bgcolor: "grey.50",
                              border: "1px solid",
                              borderColor: "grey.200",
                            }}
                          >
                            <MDBox
                              display="flex"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={2}
                              mb={2}
                              sx={{ "& > *": { minWidth: 0 } }}
                            >
                              <MDBox
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: "50%",
                                  bgcolor: "info.light",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid",
                                  borderColor: "info.main",
                                  flexShrink: 0,
                                }}
                              >
                                <Icon sx={{ color: "info.main", fontSize: "2rem" }}>person</Icon>
                              </MDBox>
                              <MDBox flex="1 1 180px" minWidth={0}>
                                <MDTypography
                                  variant="h6"
                                  fontWeight="bold"
                                  mb={0.5}
                                  sx={{ wordBreak: "break-word" }}
                                >
                                  {property.assignedInspector.fullName || "N/A"}
                                </MDTypography>
                                {property.assignedInspector.email && (
                                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Icon
                                      sx={{
                                        fontSize: "1rem",
                                        color: "text.secondary",
                                        flexShrink: 0,
                                      }}
                                    >
                                      email
                                    </Icon>
                                    <MDTypography
                                      variant="body2"
                                      component="a"
                                      href={`mailto:${property.assignedInspector.email}`}
                                      sx={{
                                        textDecoration: "none",
                                        color: "info.main",
                                        fontWeight: 500,
                                        wordBreak: "break-all",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                      }}
                                    >
                                      {property.assignedInspector.email}
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            </MDBox>
                          </MDBox>

                          {/* Inspection Date */}
                          {property.inspectionDate && (
                            <MDBox
                              p={{ xs: 1.5, sm: 2 }}
                              sx={{
                                borderRadius: 2,
                                bgcolor: "info.lighter",
                                border: "1px solid",
                                borderColor: "info.main",
                              }}
                            >
                              <MDBox display="flex" alignItems="center" gap={1.5}>
                                <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>
                                  calendar_today
                                </Icon>
                                <MDBox>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Inspection Date
                                  </MDTypography>
                                  <MDTypography variant="body2" fontWeight="bold" color="info.main">
                                    {formatDate(property.inspectionDate)}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  )}

                  {/* Check-In/Check-Out Agent */}
                  {property.assignedCheckInOutAgent && (
                    <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                      <Card
                        sx={{
                          p: 0,
                          borderRadius: 3,
                          overflow: "hidden",
                          boxShadow:
                            "0 8px 24px rgba(156, 39, 176, 0.12), 0 2px 8px rgba(156, 39, 176, 0.08)",
                          border: "1px solid",
                          borderColor: "secondary.main",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow:
                              "0 12px 32px rgba(156, 39, 176, 0.16), 0 4px 12px rgba(156, 39, 176, 0.12)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {/* Header Section */}
                        <MDBox
                          sx={{
                            p: { xs: 2, sm: 2.5 },
                            background:
                              "linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(171, 71, 188, 0.15) 100%)",
                            borderBottom: "2px solid",
                            borderColor: "secondary.main",
                          }}
                        >
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={1.5}
                          >
                            <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                              <MDBox
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2,
                                  bgcolor: "secondary.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
                                  flexShrink: 0,
                                }}
                              >
                                <Icon sx={{ color: "white", fontSize: "1.75rem" }}>person_pin</Icon>
                              </MDBox>
                              <MDBox minWidth={0}>
                                <MDTypography
                                  variant="h6"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  sx={{ wordBreak: "break-word" }}
                                >
                                  Check-In/Check-Out Agent
                                </MDTypography>
                                <MDTypography variant="caption" color="text.secondary">
                                  Guest Services Agent
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            {isSuperAdmin && (
                              <MDButton
                                variant="outlined"
                                color="secondary"
                                size="small"
                                startIcon={<Icon>edit</Icon>}
                                onClick={() => {
                                  setSelectedCheckInOutAgentId(
                                    property.assignedCheckInOutAgent?._id ||
                                      property.assignedCheckInOutAgent ||
                                      ""
                                  );
                                  setAssignCheckInOutAgentDialogOpen(true);
                                }}
                                sx={{
                                  borderRadius: 2,
                                  minHeight: "36px",
                                  px: 2,
                                  borderWidth: 1.5,
                                  fontWeight: 600,
                                  textTransform: "none",
                                  flexShrink: 0,
                                  "&:hover": {
                                    borderWidth: 1.5,
                                    bgcolor: "secondary.light",
                                  },
                                }}
                              >
                                Change
                              </MDButton>
                            )}
                          </MDBox>
                        </MDBox>

                        {/* Content Section */}
                        <MDBox p={{ xs: 2, sm: 2.5, md: 3 }}>
                          {/* Agent Info */}
                          <MDBox
                            mb={2}
                            p={{ xs: 2, sm: 2.5 }}
                            sx={{
                              borderRadius: 2,
                              bgcolor: "grey.50",
                              border: "1px solid",
                              borderColor: "grey.200",
                            }}
                          >
                            <MDBox
                              display="flex"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={2}
                              mb={2}
                              sx={{ "& > *": { minWidth: 0 } }}
                            >
                              <MDBox
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: "50%",
                                  bgcolor: "secondary.light",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid",
                                  borderColor: "secondary.main",
                                  flexShrink: 0,
                                }}
                              >
                                <Icon sx={{ color: "secondary.main", fontSize: "2rem" }}>
                                  person
                                </Icon>
                              </MDBox>
                              <MDBox flex="1 1 180px" minWidth={0}>
                                <MDTypography
                                  variant="h6"
                                  fontWeight="bold"
                                  mb={0.5}
                                  sx={{ wordBreak: "break-word" }}
                                >
                                  {property.assignedCheckInOutAgent.fullName || "N/A"}
                                </MDTypography>
                                {property.assignedCheckInOutAgent.email && (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                    flexWrap="wrap"
                                  >
                                    <Icon
                                      sx={{
                                        fontSize: "1rem",
                                        color: "text.secondary",
                                        flexShrink: 0,
                                      }}
                                    >
                                      email
                                    </Icon>
                                    <MDTypography
                                      variant="body2"
                                      component="a"
                                      href={`mailto:${property.assignedCheckInOutAgent.email}`}
                                      sx={{
                                        textDecoration: "none",
                                        color: "secondary.main",
                                        fontWeight: 500,
                                        wordBreak: "break-all",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                      }}
                                    >
                                      {property.assignedCheckInOutAgent.email}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {property.assignedCheckInOutAgent.phone && (
                                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Icon
                                      sx={{
                                        fontSize: "1rem",
                                        color: "text.secondary",
                                        flexShrink: 0,
                                      }}
                                    >
                                      phone
                                    </Icon>
                                    <MDTypography
                                      variant="body2"
                                      component="a"
                                      href={`tel:${property.assignedCheckInOutAgent.phone}`}
                                      sx={{
                                        textDecoration: "none",
                                        color: "secondary.main",
                                        fontWeight: 500,
                                        wordBreak: "break-all",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                      }}
                                    >
                                      {property.assignedCheckInOutAgent.phone}
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  )}

                  {/* No Assignments - Other Statuses */}
                  {property.status !== "Pending" &&
                    !property.assignedInspector &&
                    !property.assignedCheckInOutAgent && (
                      <Grid item xs={12} sx={{ minWidth: 0 }}>
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          No assignments available. Inspector and Check-In/Check-Out Agent need to
                          be assigned.
                        </Alert>
                      </Grid>
                    )}
                </Grid>
              </MDBox>
            )}

            {/* Owner Details Tab */}
            {detailsTab === 11 && (
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={{ xs: 2, sm: 3 }}
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Icon color="info">person</Icon>
                  Property Owner Details
                </MDTypography>
                {property.owner ? (
                  <Grid container spacing={3} sx={{ minWidth: 0 }}>
                    <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                      <Card
                        sx={{
                          p: { xs: 2, sm: 3 },
                          borderRadius: 3,
                          boxShadow: "0 8px 24px rgba(25, 118, 210, 0.12)",
                          border: "1px solid",
                          borderColor: "info.light",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                          <MDBox
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "50%",
                              bgcolor: "info.light",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon sx={{ color: "info.main", fontSize: "1.75rem" }}>person</Icon>
                          </MDBox>
                          <MDTypography variant="h6" fontWeight="bold" color="info.main">
                            {property.owner.fullName || "—"}
                          </MDTypography>
                        </MDBox>
                        <Divider sx={{ my: 2 }} />
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="600"
                            display="block"
                            mb={0.5}
                          >
                            Email
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            color="text"
                            component="a"
                            href={`mailto:${property.owner.email}`}
                            sx={{ textDecoration: "none", color: "info.main" }}
                          >
                            {property.owner.email || "—"}
                          </MDTypography>
                        </MDBox>
                        <MDBox mt={2}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="600"
                            display="block"
                            mb={0.5}
                          >
                            Phone
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            color="text"
                            component="a"
                            href={`tel:${property.owner.phone}`}
                            sx={{ textDecoration: "none", color: "info.main" }}
                          >
                            {property.owner.phone || "—"}
                          </MDTypography>
                        </MDBox>
                        {property.owner.type && (
                          <MDBox mt={2}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="600"
                              display="block"
                              mb={0.5}
                            >
                              Type
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {property.owner.type}
                            </MDTypography>
                          </MDBox>
                        )}
                        {property.owner.notes && (
                          <MDBox mt={2}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="600"
                              display="block"
                              mb={0.5}
                            >
                              Notes
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {property.owner.notes}
                            </MDTypography>
                          </MDBox>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No owner information available for this property.
                  </Alert>
                )}
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Dialogs and Modals */}
      {/* Assign Inspector Dialog with Calendar */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => {
          if (!submitting) {
            setAssignDialogOpen(false);
            setSelectedSlot(null);
            setSelectedInspector("");
            setAssignFormData({
              inspectionDate: "",
              inspectionLocation: "",
            });
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="info">assignment</Icon>
              {property?.assignedInspector ? "Change Inspector" : "Assign Inspector"}
            </MDTypography>
            <IconButton
              onClick={() => {
                if (!submitting) {
                  setAssignDialogOpen(false);
                  setSelectedSlot(null);
                  setSelectedInspector("");
                  setAssignFormData({
                    inspectionDate: "",
                    inspectionLocation: "",
                  });
                }
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Grid container spacing={0} sx={{ height: "100%" }}>
            {/* Left Column - Property Info & Form */}
            <Grid
              item
              xs={12}
              lg={4}
              sx={{ borderRight: { lg: "1px solid" }, borderColor: { lg: "divider" } }}
            >
              <MDBox p={3}>
                {/* Property Info Card */}
                <Card sx={{ mb: 3, bgcolor: "grey.50", p: 2.5, borderRadius: 2 }}>
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDBox
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        bgcolor: "grey.200",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ fontSize: "1.75rem", color: "text.secondary" }}>home</Icon>
                    </MDBox>
                    <MDBox flex={1}>
                      <MDTypography variant="h6" fontWeight="bold">
                        {property.title?.en || property.title || "N/A"}
                      </MDTypography>
                      {property.title?.ar && (
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          sx={{ direction: "rtl", textAlign: "right" }}
                        >
                          {property.title.ar}
                        </MDTypography>
                      )}
                      <MDTypography variant="body2" color="text.secondary" mt={0.5}>
                        {property.address?.en || property.address || "N/A"},{" "}
                        {property.city?.en || property.city || ""}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>

                {/* Inspector Selection */}
                <MDBox mb={3}>
                  <FormControl
                    fullWidth
                    required
                    disabled={submitting}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: "44px",
                        borderRadius: 2,
                      },
                      "& .MuiSelect-select": {
                        minHeight: "44px",
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <InputLabel>Select Inspector *</InputLabel>
                    <Select
                      value={selectedInspector || ""}
                      onChange={(e) => {
                        const inspectorId = e.target.value;
                        if (inspectorId && inspectorId !== "" && inspectorId !== "undefined") {
                          setSelectedInspector(String(inspectorId));
                          setSelectedSlot(null);
                          setAssignFormData({ ...assignFormData, inspectionDate: "" });
                          setMessage({ type: "", text: "" });
                        }
                      }}
                      label="Select Inspector *"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& .MuiMenuItem-root": {
                              minHeight: "44px",
                            },
                          },
                        },
                      }}
                    >
                      {inspectors.length === 0 ? (
                        <MenuItem disabled value="">
                          <MDBox p={2} textAlign="center" width="100%">
                            <MDTypography variant="body2" color="text.secondary">
                              No inspectors available
                            </MDTypography>
                          </MDBox>
                        </MenuItem>
                      ) : (
                        inspectors.map((inspector) => {
                          const inspectorId = inspector._id || inspector.id;
                          const inspectorIdString = String(inspectorId);
                          const isSelected = inspectorIdString === String(selectedInspector);
                          return (
                            <MenuItem
                              key={inspectorIdString}
                              value={inspectorIdString}
                              selected={isSelected}
                              sx={{
                                minHeight: "44px",
                                px: 2,
                                py: 1.5,
                              }}
                            >
                              <MDBox display="flex" alignItems="center" width="100%">
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    bgcolor: isSelected ? "info.light" : "grey.100",
                                    mr: 2,
                                  }}
                                >
                                  <Icon
                                    sx={{
                                      color: isSelected ? "info.main" : "text.secondary",
                                      fontSize: "1.5rem",
                                    }}
                                  >
                                    assignment_ind
                                  </Icon>
                                </MDBox>
                                <MDBox flex={1}>
                                  <MDTypography
                                    variant="body2"
                                    fontWeight={isSelected ? "medium" : "regular"}
                                    color={isSelected ? "info.main" : "text.primary"}
                                  >
                                    {inspector.fullName}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.25 }}
                                  >
                                    {inspector.email}
                                  </MDTypography>
                                </MDBox>
                                {isSelected && (
                                  <Icon
                                    sx={{
                                      color: "info.main",
                                      ml: 1,
                                    }}
                                  >
                                    check_circle
                                  </Icon>
                                )}
                              </MDBox>
                            </MenuItem>
                          );
                        })
                      )}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Selected Inspector Info */}
                {selectedInspector &&
                  inspectors.find(
                    (insp) => String(insp._id || insp.id) === String(selectedInspector)
                  ) && (
                    <MDBox
                      mb={3}
                      p={2}
                      sx={{
                        bgcolor: "info.lighter",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" mb={1}>
                        <Icon sx={{ mr: 1, color: "info.main" }}>check_circle</Icon>
                        <MDTypography
                          variant="caption"
                          color="info.main"
                          fontWeight="bold"
                          textTransform="uppercase"
                        >
                          Selected Inspector
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" mt={1}>
                        <Icon sx={{ mr: 1, color: "text.secondary" }}>person</Icon>
                        <MDBox>
                          <MDTypography variant="body2" fontWeight="medium" color="dark">
                            {
                              inspectors.find(
                                (insp) => String(insp._id || insp.id) === String(selectedInspector)
                              )?.fullName
                            }
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            {
                              inspectors.find(
                                (insp) => String(insp._id || insp.id) === String(selectedInspector)
                              )?.email
                            }
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  )}

                {/* Inspection Date */}
                <MDBox mb={3}>
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Inspection Date & Time *
                  </MDTypography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    name="inspectionDate"
                    value={assignFormData.inspectionDate}
                    onChange={(e) => {
                      const dateValue = e.target.value;
                      setAssignFormData({ ...assignFormData, inspectionDate: dateValue });
                      // Update selected slot when date is manually changed
                      if (dateValue) {
                        const selectedDateTime = new Date(dateValue);
                        selectedDateTime.setMinutes(0, 0, 0);
                        const endDateTime = new Date(selectedDateTime);
                        endDateTime.setHours(endDateTime.getHours() + 1);
                        setSelectedSlot({
                          start: selectedDateTime,
                          end: endDateTime,
                        });
                      } else {
                        setSelectedSlot(null);
                      }
                    }}
                    disabled={submitting || !selectedInspector}
                    error={!!dateConflict}
                    helperText={
                      dateConflict
                        ? `Conflict: Inspector has another inspection at ${new Date(
                            dateConflict.inspectionDate
                          ).toLocaleString()}`
                        : "Select a date and time from the calendar or enter manually"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: "44px",
                        "& input": {
                          padding: "12px 14px",
                          fontSize: "0.875rem",
                        },
                      },
                    }}
                  />
                </MDBox>

                {/* Inspection Location */}
                <TextField
                  fullWidth
                  label="Inspection Location *"
                  name="inspectionLocation"
                  value={assignFormData.inspectionLocation}
                  onChange={(e) =>
                    setAssignFormData({ ...assignFormData, inspectionLocation: e.target.value })
                  }
                  placeholder="Enter the exact location for inspection"
                  multiline
                  rows={4}
                  disabled={submitting}
                  helperText="Location address and Google Maps link will be auto-filled. You can edit if needed."
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </MDBox>
            </Grid>

            {/* Right Column - Calendar */}
            <Grid item xs={12} lg={8}>
              <MDBox p={3} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDTypography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: "info.main" }}>calendar_today</Icon>
                    Inspector Schedule
                    {selectedInspector &&
                      inspectors.find(
                        (insp) => String(insp._id || insp.id) === String(selectedInspector)
                      ) && (
                        <Chip
                          label={
                            inspectors.find(
                              (insp) => String(insp._id || insp.id) === String(selectedInspector)
                            )?.fullName
                          }
                          color="info"
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      )}
                  </MDTypography>
                  {!selectedInspector && (
                    <Alert severity="info" sx={{ flex: 1, ml: 2 }}>
                      Please select an inspector to view their schedule
                    </Alert>
                  )}
                </MDBox>

                {selectedInspector ? (
                  <>
                    {/* Calendar Controls */}
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => navigateCalendar("PREV")}
                          startIcon={<Icon>chevron_left</Icon>}
                          sx={{ minHeight: "44px" }}
                        >
                          Previous
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => setCalendarDate(new Date())}
                          sx={{ minHeight: "44px" }}
                        >
                          Today
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => navigateCalendar("NEXT")}
                          endIcon={<Icon>chevron_right</Icon>}
                          sx={{ minHeight: "44px" }}
                        >
                          Next
                        </MDButton>
                      </MDBox>
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          variant={calendarView === "month" ? "contained" : "outlined"}
                          color="info"
                          size="small"
                          onClick={() => handleViewChange("month")}
                          sx={{ minHeight: "44px" }}
                        >
                          Month
                        </MDButton>
                        <MDButton
                          variant={calendarView === "week" ? "contained" : "outlined"}
                          color="info"
                          size="small"
                          onClick={() => handleViewChange("week")}
                          sx={{ minHeight: "44px" }}
                        >
                          Week
                        </MDButton>
                        <MDButton
                          variant={calendarView === "day" ? "contained" : "outlined"}
                          color="info"
                          size="small"
                          onClick={() => handleViewChange("day")}
                          sx={{ minHeight: "44px" }}
                        >
                          Day
                        </MDButton>
                      </MDBox>
                    </MDBox>

                    {/* Calendar */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 2,
                        height: 600,
                        overflow: "visible",
                        position: "relative",
                        "& .rbc-calendar": {
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        },
                        "& .rbc-time-view": {
                          border: "none",
                        },
                        "& .rbc-time-header": {
                          border: "1px solid #e0e0e0",
                        },
                        "& .rbc-time-content": {
                          borderTop: "2px solid #e0e0e0",
                        },
                        "& .rbc-month-view": {
                          "& .rbc-day-bg": {
                            minHeight: "120px",
                            border: "1px solid #e0e0e0",
                          },
                        },
                        "& .rbc-event": {
                          padding: "6px 10px",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          borderRadius: "4px",
                          minHeight: "24px",
                        },
                        "& .rbc-selected": {
                          backgroundColor: "#4CAF50 !important",
                          border: "2px solid #2e7d32 !important",
                          boxShadow: "0 2px 8px rgba(76, 175, 80, 0.4) !important",
                          zIndex: 10,
                        },
                        "& .rbc-slot-reserved": {
                          backgroundColor: "rgba(244, 67, 54, 0.1) !important",
                          border: "1px dashed rgba(244, 67, 54, 0.3) !important",
                          cursor: "not-allowed !important",
                          pointerEvents: "none !important",
                          opacity: 0.6,
                        },
                      }}
                    >
                      <Calendar
                        localizer={localizer}
                        events={getCalendarEvents()}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: "100%", minHeight: "550px" }}
                        view={calendarView}
                        date={calendarDate}
                        onView={handleViewChange}
                        onNavigate={setCalendarDate}
                        onSelectEvent={handleSelectEvent}
                        onSelectSlot={handleSelectSlot}
                        selectable
                        eventPropGetter={eventStyleGetter}
                        slotPropGetter={slotPropGetter}
                        popup
                        showMultiDayTimes
                        step={60}
                        timeslots={1}
                        min={new Date(2024, 0, 1, 8, 0)}
                        max={new Date(2024, 0, 1, 20, 0)}
                        defaultView={
                          calendarView === "month"
                            ? "month"
                            : calendarView === "week"
                            ? "week"
                            : "day"
                        }
                        formats={{
                          timeGutterFormat: "HH:mm",
                          eventTimeRangeFormat: ({ start, end }) =>
                            `${localizer.format(start, "HH:mm", "en")} - ${localizer.format(
                              end,
                              "HH:mm",
                              "en"
                            )}`,
                          dayFormat: "ddd M/D",
                          dayHeaderFormat: "dddd M/D",
                        }}
                        components={{
                          event: ({ event }) => {
                            const isExisting = event.resource?.isExisting;
                            const isSelected = event.resource?.isSelected;
                            const eventStyle = eventStyleGetter(event);

                            return (
                              <div
                                data-existing={isExisting ? "true" : "false"}
                                data-selected={isSelected ? "true" : "false"}
                                style={{
                                  ...eventStyle.style,
                                  cursor: isExisting
                                    ? "pointer"
                                    : isSelected
                                    ? "pointer"
                                    : "default",
                                  width: "100%",
                                  height: "100%",
                                }}
                                onClick={(e) => {
                                  if (isExisting) {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleSelectEvent(event);
                                  }
                                }}
                                title={
                                  isExisting
                                    ? `${String(
                                        event.title || "Existing Inspection"
                                      )} - Click to view details`
                                    : String(event.title || "Inspection")
                                }
                              >
                                {event.title && String(event.title).trim() !== "" ? (
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "block",
                                      width: "100%",
                                    }}
                                  >
                                    {String(event.title)}
                                  </span>
                                ) : null}
                              </div>
                            );
                          },
                        }}
                      />
                    </Paper>
                  </>
                ) : (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      height: 600,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      border: "2px dashed",
                      borderColor: "grey.300",
                    }}
                  >
                    <MDBox textAlign="center">
                      <Icon sx={{ fontSize: "4rem", color: "grey.400", mb: 2 }}>
                        calendar_today
                      </Icon>
                      <MDTypography variant="h6" color="text.secondary" fontWeight="medium">
                        Select an Inspector
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary" mt={1}>
                        Choose an inspector from the dropdown to view their schedule
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => {
              if (!submitting) {
                setAssignDialogOpen(false);
                setSelectedSlot(null);
                setSelectedInspector("");
                setAssignFormData({
                  inspectionDate: "",
                  inspectionLocation: "",
                });
              }
            }}
            disabled={submitting}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color={property?.assignedInspector ? "warning" : "info"}
            onClick={handleAssignInspector}
            disabled={
              submitting ||
              !selectedInspector ||
              !assignFormData.inspectionDate ||
              !assignFormData.inspectionLocation.trim()
            }
            startIcon={submitting ? <CircularProgress size={16} /> : <Icon>assignment</Icon>}
            sx={{
              borderRadius: 2,
              boxShadow: property?.assignedInspector
                ? "0 4px 12px rgba(237, 108, 2, 0.3)"
                : "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                boxShadow: property?.assignedInspector
                  ? "0 6px 16px rgba(237, 108, 2, 0.4)"
                  : "0 6px 16px rgba(25, 118, 210, 0.4)",
              },
            }}
          >
            {submitting
              ? property?.assignedInspector
                ? "Changing..."
                : "Assigning..."
              : property?.assignedInspector
              ? "Change Inspector"
              : "Assign Inspector"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Reject Property Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !submitting && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="error">cancel</Icon>
              Reject Property
            </MDTypography>
            <IconButton onClick={() => !submitting && setRejectDialogOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <MDBox mb={2}>
              <MDTypography variant="body2" color="text" mb={2}>
                You are about to reject the following property. This action cannot be undone.
              </MDTypography>
              {property && (
                <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {property.title?.en || property.title || "N/A"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {property.address?.en || property.address || "N/A"},{" "}
                    {property.city?.en || property.city || "N/A"}
                  </MDTypography>
                </MDBox>
              )}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Rejection Reason"
                placeholder="Please provide a reason for rejecting this property..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                required
                disabled={submitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "44px",
                  },
                }}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => !submitting && setRejectDialogOpen(false)}
            disabled={submitting}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleRejectProperty}
            variant="gradient"
            color="error"
            disabled={submitting || !rejectReason.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : <Icon>cancel</Icon>}
            sx={{
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(211, 47, 47, 0.4)",
              },
            }}
          >
            {submitting ? "Rejecting..." : "Reject Property"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Approve to Contracting Dialog */}
      <Dialog
        open={approveToContractingDialogOpen}
        onClose={() => !approvingToContracting && setApproveToContractingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="success">check_circle</Icon>
              Approve Property
            </MDTypography>
            <IconButton
              onClick={() => !approvingToContracting && setApproveToContractingDialogOpen(false)}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={2}>
            Approve this property and move it to the Contracting phase. The draft contract was
            already sent to the owner when the inspector submitted the inspection.
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            In the next step (Contracting), you will set finance details and move the property to
            Preparation before activation.
          </MDTypography>
          {property && (
            <MDBox p={2} borderRadius={2} bgcolor="grey.100">
              <MDTypography variant="subtitle2" fontWeight="medium">
                {property.title?.en || property.title || "N/A"}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {property.address?.en || property.address || "N/A"},{" "}
                {property.city?.en || property.city || ""}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => !approvingToContracting && setApproveToContractingDialogOpen(false)}
            disabled={approvingToContracting}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleApproveToContracting}
            variant="gradient"
            color="success"
            disabled={approvingToContracting}
            startIcon={
              approvingToContracting ? <CircularProgress size={16} /> : <Icon>check_circle</Icon>
            }
            sx={{ borderRadius: 2 }}
          >
            {approvingToContracting ? "Approving..." : "Approve & Move to Contracting"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Approve to Inspection Offer Dialog */}
      <Dialog
        open={approveToInspectionOfferDialogOpen}
        onClose={() => !approvingToInspectionOffer && setApproveToInspectionOfferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="success">check_circle</Icon>
              Approve to Inspection Offer
            </MDTypography>
            <IconButton
              onClick={() =>
                !approvingToInspectionOffer && setApproveToInspectionOfferDialogOpen(false)
              }
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={2}>
            Create the Inspection Offer: a draft contract will be generated, uploaded, and sent to
            the property owner .
          </MDTypography>
          {property && (
            <MDBox p={2} borderRadius={2} bgcolor="grey.100">
              <MDTypography variant="subtitle2" fontWeight="medium">
                {property.title?.en || property.title || "N/A"}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {property.address?.en || property.address || "N/A"},{" "}
                {property.city?.en || property.city || ""}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() =>
              !approvingToInspectionOffer && setApproveToInspectionOfferDialogOpen(false)
            }
            disabled={approvingToInspectionOffer}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleApproveToInspectionOffer}
            variant="gradient"
            color="success"
            disabled={approvingToInspectionOffer}
            startIcon={
              approvingToInspectionOffer ? (
                <CircularProgress size={16} />
              ) : (
                <Icon>check_circle</Icon>
              )
            }
            sx={{ borderRadius: 2 }}
          >
            {approvingToInspectionOffer ? "Approving..." : "Approve to Inspection Offer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Send Missing Items Email Dialog */}
      <Dialog
        open={missingItemsDialogOpen}
        onClose={() => !submittingMissingItems && setMissingItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="warning">email</Icon>
              Send Missing Items Email
            </MDTypography>
            <IconButton onClick={() => !submittingMissingItems && setMissingItemsDialogOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            Send a PDF email to the property owner with missing items, expected dates, and income
            projection.
          </MDTypography>
          {(missingItemsData.items || []).map((item, idx) => (
            <MDBox key={idx} display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="flex-start">
              <TextField
                label="Item name"
                value={item.name || ""}
                onChange={(e) => {
                  const items = [...(missingItemsData.items || [])];
                  items[idx] = { ...items[idx], name: e.target.value };
                  setMissingItemsData({ ...missingItemsData, items });
                }}
                placeholder="e.g. AC"
                disabled={submittingMissingItems}
                sx={{ minWidth: 100, flex: 1 }}
              />
              <TextField
                type="number"
                label="Qty"
                value={item.quantity !== undefined && item.quantity !== "" ? item.quantity : ""}
                onChange={(e) => {
                  const items = [...(missingItemsData.items || [])];
                  items[idx] = { ...items[idx], quantity: e.target.value };
                  setMissingItemsData({ ...missingItemsData, items });
                }}
                placeholder="1"
                disabled={submittingMissingItems}
                inputProps={{ min: 1 }}
                sx={{ minWidth: 70 }}
              />
              <TextField
                type="number"
                label="Price (EGP)"
                value={item.price !== undefined && item.price !== "" ? item.price : ""}
                onChange={(e) => {
                  const items = [...(missingItemsData.items || [])];
                  items[idx] = { ...items[idx], price: e.target.value };
                  setMissingItemsData({ ...missingItemsData, items });
                }}
                placeholder="0"
                disabled={submittingMissingItems}
                sx={{ minWidth: 110 }}
              />
              <IconButton
                color="error"
                onClick={() => {
                  const items = (missingItemsData.items || []).filter((_, i) => i !== idx);
                  setMissingItemsData({
                    ...missingItemsData,
                    items: items.length ? items : [{ name: "", quantity: 1, price: "" }],
                  });
                }}
                disabled={submittingMissingItems || (missingItemsData.items || []).length <= 1}
              >
                <Icon>delete</Icon>
              </IconButton>
            </MDBox>
          ))}
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Icon>add</Icon>}
            onClick={() =>
              setMissingItemsData({
                ...missingItemsData,
                items: [...(missingItemsData.items || []), { name: "", quantity: 1, price: "" }],
              })
            }
            disabled={submittingMissingItems}
            sx={{ mb: 2 }}
          >
            Add item
          </MDButton>
          <TextField
            fullWidth
            type="date"
            label="Expected completion date"
            value={missingItemsData.expectedDate || ""}
            onChange={(e) =>
              setMissingItemsData({ ...missingItemsData, expectedDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            disabled={submittingMissingItems}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Expected date from approval to listing phase"
            value={missingItemsData.expectedDateApprovalToListing || ""}
            onChange={(e) =>
              setMissingItemsData({
                ...missingItemsData,
                expectedDateApprovalToListing: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            disabled={submittingMissingItems}
            sx={{ mb: 2 }}
          />
          <MDTypography variant="caption" color="text.secondary" display="block" mt={2} mb={1}>
            Income projection (auto-calculated; shown in PDF if filled)
          </MDTypography>
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
            <TextField
              type="number"
              label="Price per day (EGP)"
              value={missingItemsData.pricePerDay ?? ""}
              onChange={(e) =>
                setMissingItemsData({ ...missingItemsData, pricePerDay: e.target.value })
              }
              disabled={submittingMissingItems}
              sx={{ minWidth: 130 }}
            />
            <TextField
              type="number"
              label="Occupancy rate %"
              value={missingItemsData.occupancyRate ?? ""}
              onChange={(e) =>
                setMissingItemsData({ ...missingItemsData, occupancyRate: e.target.value })
              }
              disabled={submittingMissingItems}
              inputProps={{ min: 0, max: 100 }}
              sx={{ minWidth: 120 }}
            />
            <TextField
              type="number"
              label="Company %"
              value={missingItemsData.companyPercentage ?? ""}
              onChange={(e) =>
                setMissingItemsData({ ...missingItemsData, companyPercentage: e.target.value })
              }
              disabled={submittingMissingItems}
              inputProps={{ min: 0, max: 100 }}
              sx={{ minWidth: 90 }}
            />
            <TextField
              type="number"
              label="Booking site %"
              value={missingItemsData.bookingWebsitePercentage ?? ""}
              onChange={(e) =>
                setMissingItemsData({
                  ...missingItemsData,
                  bookingWebsitePercentage: e.target.value,
                })
              }
              disabled={submittingMissingItems}
              inputProps={{ min: 0, max: 100 }}
              sx={{ minWidth: 100 }}
            />
          </MDBox>
          {(missingItemsData.pricePerDay || missingItemsData.occupancyRate) && (
            <MDBox p={2} borderRadius={2} bgcolor="success.lighter" mb={2}>
              <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                Estimated owner income:
              </MDTypography>
              <MDTypography variant="body2">
                Per day: {missingItemsIncomeProjection.incomePerDay.toFixed(2)} EGP
              </MDTypography>
              <MDTypography variant="body2">
                Per month: {missingItemsIncomeProjection.incomePerMonth.toFixed(2)} EGP
              </MDTypography>
              <MDTypography variant="body2">
                Per year: {missingItemsIncomeProjection.incomePerYear.toFixed(2)} EGP
              </MDTypography>
            </MDBox>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional message (optional)"
            value={missingItemsData.customMessage || ""}
            onChange={(e) =>
              setMissingItemsData({ ...missingItemsData, customMessage: e.target.value })
            }
            placeholder="Add any custom notes for the owner..."
            disabled={submittingMissingItems}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => !submittingMissingItems && setMissingItemsDialogOpen(false)}
            disabled={submittingMissingItems}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSendMissingItemsEmail}
            variant="gradient"
            color="warning"
            disabled={
              submittingMissingItems ||
              !(missingItemsData.items || []).some((i) => (i.name || i.item || "").trim())
            }
            startIcon={submittingMissingItems ? <CircularProgress size={16} /> : <Icon>send</Icon>}
            sx={{ borderRadius: 2 }}
          >
            {submittingMissingItems ? "Sending..." : "Send Email"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Assign/Change Check-In/Check-Out Agent Dialog */}
      <Dialog
        open={assignCheckInOutAgentDialogOpen}
        onClose={() => {
          if (!updatingCheckInOutAgent) {
            setAssignCheckInOutAgentDialogOpen(false);
            setSelectedCheckInOutAgentId("");
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="secondary">person_pin</Icon>
              {property?.assignedCheckInOutAgent
                ? "Change Check-In/Check-Out Agent"
                : "Assign Check-In/Check-Out Agent"}
            </MDTypography>
            <IconButton
              onClick={() => {
                if (!updatingCheckInOutAgent) {
                  setAssignCheckInOutAgentDialogOpen(false);
                  setSelectedCheckInOutAgentId("");
                }
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text" mb={2}>
              {property?.assignedCheckInOutAgent
                ? "Select a new check-in/check-out agent for this property."
                : "Select a check-in/check-out agent to assign to this property. This is required for the property to be available for reservations."}
            </MDTypography>
            {property && (
              <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  {property.title?.en || property.title || "N/A"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {property.address?.en || property.address || "N/A"},{" "}
                  {property.city?.en || property.city || "N/A"}
                </MDTypography>
              </MDBox>
            )}
            <FormControl fullWidth required>
              <InputLabel>Check-In/Check-Out Agent *</InputLabel>
              <Select
                value={selectedCheckInOutAgentId}
                onChange={(e) => setSelectedCheckInOutAgentId(e.target.value)}
                label="Check-In/Check-Out Agent *"
                disabled={updatingCheckInOutAgent || loadingCheckInOutAgents}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "44px",
                  },
                  "& .MuiSelect-select": {
                    minHeight: "44px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                {checkInOutAgents.map((agent) => (
                  <MenuItem key={agent._id || agent.id} value={agent._id || agent.id}>
                    {agent.fullName} ({agent.email})
                  </MenuItem>
                ))}
              </Select>
              {loadingCheckInOutAgents && (
                <MDBox display="flex" alignItems="center" mt={1}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <MDTypography variant="caption" color="text.secondary">
                    Loading agents...
                  </MDTypography>
                </MDBox>
              )}
              {!loadingCheckInOutAgents && checkInOutAgents.length === 0 && (
                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                  No check-in/check-out agents available. Please create an agent first.
                </MDTypography>
              )}
            </FormControl>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => {
              setAssignCheckInOutAgentDialogOpen(false);
              setSelectedCheckInOutAgentId("");
            }}
            disabled={updatingCheckInOutAgent}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={async () => {
              if (!selectedCheckInOutAgentId) {
                setMessage({
                  type: "error",
                  text: "Please select a check-in/check-out agent",
                });
                return;
              }

              try {
                setUpdatingCheckInOutAgent(true);
                setMessage({ type: "", text: "" });

                const response = await adminAPI.updatePropertyCheckInOutAgent(
                  id,
                  selectedCheckInOutAgentId
                );

                if (response.success) {
                  setProperty(response.property);
                  setMessage({
                    type: "success",
                    text: property?.assignedCheckInOutAgent
                      ? "Check-in/check-out agent updated successfully"
                      : "Check-in/check-out agent assigned successfully",
                  });
                  setAssignCheckInOutAgentDialogOpen(false);
                  setSelectedCheckInOutAgentId("");
                  setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                }
              } catch (error) {
                console.error("Error updating check-in/check-out agent:", error);
                setMessage({
                  type: "error",
                  text: error.message || "Failed to update check-in/check-out agent",
                });
              } finally {
                setUpdatingCheckInOutAgent(false);
              }
            }}
            variant="gradient"
            color={property?.assignedCheckInOutAgent ? "warning" : "secondary"}
            disabled={updatingCheckInOutAgent || !selectedCheckInOutAgentId}
            startIcon={
              updatingCheckInOutAgent ? <CircularProgress size={16} /> : <Icon>person_pin</Icon>
            }
            sx={{
              borderRadius: 2,
              boxShadow: property?.assignedCheckInOutAgent
                ? "0 4px 12px rgba(237, 108, 2, 0.3)"
                : "0 4px 12px rgba(156, 39, 176, 0.3)",
              "&:hover": {
                boxShadow: property?.assignedCheckInOutAgent
                  ? "0 6px 16px rgba(237, 108, 2, 0.4)"
                  : "0 6px 16px rgba(156, 39, 176, 0.4)",
              },
            }}
          >
            {updatingCheckInOutAgent
              ? property?.assignedCheckInOutAgent
                ? "Changing..."
                : "Assigning..."
              : property?.assignedCheckInOutAgent
              ? "Change Agent"
              : "Assign Agent"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.95)",
            maxHeight: "90vh",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold" color="white">
              {lightboxTitle} ({lightboxImageIndex + 1} of {lightboxImages?.length || 0})
            </MDTypography>
            <IconButton onClick={() => setLightboxOpen(false)} sx={{ color: "white" }}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "transparent", p: 0 }}>
          <MDBox
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="60vh"
          >
            {lightboxImages && lightboxImages.length > 0 && lightboxImages[lightboxImageIndex] && (
              <>
                <Box
                  component="img"
                  src={lightboxImages[lightboxImageIndex]}
                  alt={`${lightboxTitle} ${lightboxImageIndex + 1}`}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
                {lightboxImages.length > 1 && (
                  <>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImageIndex((prev) =>
                          prev > 0 ? prev - 1 : lightboxImages.length - 1
                        );
                      }}
                      sx={{
                        position: "absolute",
                        left: 16,
                        color: "white",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                      }}
                    >
                      <Icon>chevron_left</Icon>
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImageIndex((prev) =>
                          prev < lightboxImages.length - 1 ? prev + 1 : 0
                        );
                      }}
                      sx={{
                        position: "absolute",
                        right: 16,
                        color: "white",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" },
                      }}
                    >
                      <Icon>chevron_right</Icon>
                    </IconButton>
                  </>
                )}
              </>
            )}
          </MDBox>
        </DialogContent>
        {lightboxImages && lightboxImages.length > 1 && (
          <DialogActions sx={{ bgcolor: "transparent", justifyContent: "center", pb: 2 }}>
            <MDBox display="flex" gap={1} flexWrap="wrap" justifyContent="center">
              {lightboxImages.map((image, index) => (
                <Box
                  key={index}
                  component="img"
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  onClick={() => setLightboxImageIndex(index)}
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: "pointer",
                    border:
                      lightboxImageIndex === index ? "2px solid white" : "2px solid transparent",
                    opacity: lightboxImageIndex === index ? 1 : 0.7,
                    "&:hover": { opacity: 1 },
                  }}
                />
              ))}
            </MDBox>
          </DialogActions>
        )}
      </Dialog>

      {/* PDF Lightbox Viewer */}
      <Dialog
        open={pdfLightboxOpen}
        onClose={() => setPdfLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color="error">description</Icon>
              {pdfLightboxTitle || "Document"}
            </MDTypography>
            <MDBox display="flex" gap={1} alignItems="center">
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                onClick={() => setUseGoogleViewer(!useGoogleViewer)}
                startIcon={<Icon>{useGoogleViewer ? "code" : "open_in_new"}</Icon>}
              >
                {useGoogleViewer ? "Embed View" : "Google Viewer"}
              </MDButton>
              <IconButton onClick={() => setPdfLightboxOpen(false)}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, minHeight: "60vh", bgcolor: "grey.900" }}>
          {pdfLightboxUrl && (
            <MDBox
              sx={{
                width: "100%",
                height: "70vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {useGoogleViewer ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                    pdfLightboxUrl
                  )}&embedded=true`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title={pdfLightboxTitle}
                />
              ) : (
                <object
                  data={pdfLightboxUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  style={{ minHeight: "70vh" }}
                  onError={() => setUseGoogleViewer(true)}
                >
                  <MDBox p={4} textAlign="center">
                    <MDTypography variant="h6" color="error" mb={2}>
                      Unable to display PDF
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() => setUseGoogleViewer(true)}
                      startIcon={<Icon>refresh</Icon>}
                    >
                      Try Google Viewer
                    </MDButton>
                    <MDBox mt={2}>
                      <MDButton
                        variant="outlined"
                        color="info"
                        href={pdfLightboxUrl}
                        target="_blank"
                        startIcon={<Icon>download</Icon>}
                      >
                        Download PDF
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </object>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "grey.50" }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => {
              if (pdfLightboxUrl) {
                window.open(pdfLightboxUrl, "_blank");
              }
            }}
            startIcon={<Icon>open_in_new</Icon>}
          >
            Open in New Tab
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => {
              if (pdfLightboxUrl) {
                const link = document.createElement("a");
                link.href = addDownloadTransformation(pdfLightboxUrl);
                link.download = pdfLightboxTitle || "document.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }}
            startIcon={<Icon>download</Icon>}
          >
            Download PDF
          </MDButton>
          <MDButton onClick={() => setPdfLightboxOpen(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* Multi-Step Edit Property Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditActiveStep(0);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            maxHeight: isMobile ? "100%" : "95vh",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 4 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "16px 16px 0 0",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDBox
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: "1.75rem" }}>edit</Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold">
                Edit Property Details
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                Step {editActiveStep + 1} of 8
              </MDTypography>
            </MDBox>
          </MDBox>
          <IconButton
            onClick={() => {
              setEditDialogOpen(false);
              setEditActiveStep(0);
            }}
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "grey.50", overflow: "auto" }}>
          <MDBox p={{ xs: 2, sm: 3, md: 4 }}>
            {/* Progress Indicator */}
            <MDBox mb={{ xs: 2, sm: 4 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
                  Progress: {Math.round(((editActiveStep + 1) / 8) * 100)}%
                </MDTypography>
                <Chip label={`${editActiveStep + 1}/8`} color="info" sx={{ fontWeight: "bold" }} />
              </MDBox>
              <LinearProgress
                variant="determinate"
                value={((editActiveStep + 1) / 8) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                  },
                }}
              />
            </MDBox>

            {/* Stepper */}
            <Stepper
              activeStep={editActiveStep}
              sx={{
                mb: { xs: 2, sm: 4 },
                overflowX: "auto",
                pb: 1,
                "& .MuiStepLabel-root": {
                  "& .MuiStepLabel-label": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 500,
                  },
                },
              }}
            >
              {[
                "Basic Information",
                "Location & Address",
                "Property Details",
                "Compound Information",
                "WiFi & Utilities",
                "Amenities",
                "Media Upload",
                "Legal Papers",
              ].map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <Card
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                bgcolor: "white",
                minHeight: { xs: "320px", sm: "450px" },
                overflow: "hidden",
              }}
            >
              <MDBox>
                {/* Step 0: Basic Information */}
                {editActiveStep === 0 && (
                  <MDBox sx={{ minWidth: 0 }}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem", flexShrink: 0 }}>
                        info
                      </Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Basic Information
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3} sx={{ minWidth: 0 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Title (English) *"
                          name="titleEn"
                          value={editFormData.titleEn || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, titleEn: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Title (Arabic) *"
                          name="titleAr"
                          value={editFormData.titleAr || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, titleAr: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Description (English)"
                          name="descriptionEn"
                          value={editFormData.descriptionEn || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, descriptionEn: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Description (Arabic)"
                          name="descriptionAr"
                          value={editFormData.descriptionAr || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, descriptionAr: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Property Type *</InputLabel>
                          <Select
                            value={editFormData.propertyType || ""}
                            label="Property Type *"
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, propertyType: e.target.value })
                            }
                            sx={{
                              minHeight: "44px",
                              "& .MuiSelect-select": {
                                minHeight: "44px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    minHeight: "44px",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="villa">Villa</MenuItem>
                            <MenuItem value="apartment">Apartment</MenuItem>
                            <MenuItem value="vacation home">Vacation Home</MenuItem>
                            <MenuItem value="studio">Studio</MenuItem>
                            <MenuItem value="townhome">Townhome</MenuItem>
                            <MenuItem value="house">House</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          sx={{
                            minHeight: "44px",
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.300",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={editFormData.featured || false}
                                onChange={(e) =>
                                  setEditFormData({ ...editFormData, featured: e.target.checked })
                                }
                                color="info"
                              />
                            }
                            label={
                              <MDTypography variant="body1" fontWeight="medium">
                                Featured Property
                              </MDTypography>
                            }
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {/* Step 1: Location & Address */}
                {editActiveStep === 1 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>location_on</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Location & Address
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Address (English) *"
                          name="addressEn"
                          value={editFormData.addressEn || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, addressEn: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Address (Arabic) *"
                          name="addressAr"
                          value={editFormData.addressAr || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, addressAr: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="City (English)"
                          name="cityEn"
                          value={editFormData.cityEn || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, cityEn: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="City (Arabic)"
                          name="cityAr"
                          value={editFormData.cityAr || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, cityAr: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Country (English)"
                          name="countryEn"
                          value={editFormData.countryEn || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, countryEn: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Country (Arabic)"
                          name="countryAr"
                          value={editFormData.countryAr || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, countryAr: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Landmark (English)"
                          name="landmarkEn"
                          value={editFormData.landmarkEn || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, landmarkEn: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Landmark (Arabic)"
                          name="landmarkAr"
                          value={editFormData.landmarkAr || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, landmarkAr: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Google Map Link"
                          name="googleMapLink"
                          value={editFormData.googleMapLink || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, googleMapLink: e.target.value })
                          }
                          placeholder="https://maps.google.com/..."
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {/* Step 2: Property Details */}
                {editActiveStep === 2 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>home</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Property Details
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Rooms *"
                          name="rooms"
                          value={editFormData.rooms || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, rooms: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Bathrooms *"
                          name="bathrooms"
                          value={editFormData.bathrooms || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, bathrooms: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Balconies"
                          name="balconies"
                          value={editFormData.balconies || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, balconies: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Size (sqm)"
                          name="size"
                          value={editFormData.size || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, size: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      {editFormData.propertyType === "apartment" && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Floors"
                              name="floors"
                              value={editFormData.floors || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, floors: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Floor Number"
                              name="floorNumber"
                              value={editFormData.floorNumber || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, floorNumber: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Building Age"
                          name="buildingAge"
                          value={editFormData.buildingAge || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, buildingAge: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Furniture Age"
                          name="furnitureAge"
                          value={editFormData.furnitureAge || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, furnitureAge: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      {editFormData.propertyType === "apartment" && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Apartments Per Floor"
                            name="apartmentsPerFloor"
                            value={editFormData.apartmentsPerFloor || ""}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                apartmentsPerFloor: e.target.value,
                              })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                minHeight: "44px",
                              },
                            }}
                          />
                        </Grid>
                      )}
                      {/* Property Status & Inspection Preferences */}
                      <Grid item xs={12}>
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          color="text"
                          mb={1.5}
                          display="block"
                        >
                          Property Status & Inspection Preferences
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          sx={{
                            minHeight: "44px",
                            "& .MuiSelect-select": {
                              minHeight: "44px",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiMenuItem-root": { minHeight: "44px" },
                          }}
                        >
                          <InputLabel>Current Property State</InputLabel>
                          <Select
                            value={editFormData.currentPropertyState || "Available"}
                            label="Current Property State"
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                currentPropertyState: e.target.value,
                              })
                            }
                            sx={{ minHeight: "44px" }}
                            MenuProps={{
                              PaperProps: { sx: { "& .MuiMenuItem-root": { minHeight: "44px" } } },
                            }}
                          >
                            <MenuItem value="Available">Available</MenuItem>
                            <MenuItem value="Vacant">Vacant</MenuItem>
                            <MenuItem value="Rented">Rented</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          sx={{
                            minHeight: "44px",
                            "& .MuiSelect-select": {
                              minHeight: "44px",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiMenuItem-root": { minHeight: "44px" },
                          }}
                        >
                          <InputLabel>Ready for Inspector</InputLabel>
                          <Select
                            value={editFormData.readyForInspector ? "true" : "false"}
                            label="Ready for Inspector"
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                readyForInspector: e.target.value === "true",
                              })
                            }
                            sx={{ minHeight: "44px" }}
                            MenuProps={{
                              PaperProps: { sx: { "& .MuiMenuItem-root": { minHeight: "44px" } } },
                            }}
                          >
                            <MenuItem value="false">No</MenuItem>
                            <MenuItem value="true">Yes</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          sx={{
                            minHeight: "44px",
                            "& .MuiSelect-select": {
                              minHeight: "44px",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiMenuItem-root": { minHeight: "44px" },
                          }}
                        >
                          <InputLabel>Current Rental Process</InputLabel>
                          <Select
                            value={editFormData.currentRentalProcess || ""}
                            label="Current Rental Process"
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                currentRentalProcess: e.target.value,
                              })
                            }
                            sx={{ minHeight: "44px" }}
                            MenuProps={{
                              PaperProps: { sx: { "& .MuiMenuItem-root": { minHeight: "44px" } } },
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            <MenuItem value="Daily">Daily</MenuItem>
                            <MenuItem value="Monthly">Monthly</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Current Rental Revenue (EGP)"
                          name="currentRentalRevenue"
                          value={editFormData.currentRentalRevenue || ""}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              currentRentalRevenue: e.target.value,
                            })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      {editFormData.readyForInspector && (
                        <Grid item xs={12} md={6}>
                          <FormControl
                            fullWidth
                            sx={{
                              minHeight: "44px",
                              "& .MuiSelect-select": {
                                minHeight: "44px",
                                display: "flex",
                                alignItems: "center",
                              },
                              "& .MuiMenuItem-root": { minHeight: "44px" },
                            }}
                          >
                            <InputLabel>Preferred Time for Inspection</InputLabel>
                            <Select
                              value={editFormData.preferredInspectionTime || ""}
                              label="Preferred Time for Inspection"
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  preferredInspectionTime: e.target.value,
                                })
                              }
                              sx={{ minHeight: "44px" }}
                              MenuProps={{
                                PaperProps: {
                                  sx: { "& .MuiMenuItem-root": { minHeight: "44px" } },
                                },
                              }}
                            >
                              <MenuItem value="">Any time</MenuItem>
                              <MenuItem value="Morning (9 AM - 12 PM)">
                                Morning (9 AM - 12 PM)
                              </MenuItem>
                              <MenuItem value="Afternoon (12 PM - 5 PM)">
                                Afternoon (12 PM - 5 PM)
                              </MenuItem>
                              <MenuItem value="Evening (5 PM - 8 PM)">
                                Evening (5 PM - 8 PM)
                              </MenuItem>
                              <MenuItem value="Flexible">Flexible / Any time</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="360 Apartment Image URL"
                          name="tour360Url"
                          value={editFormData.tour360Url || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, tour360Url: e.target.value })
                          }
                          placeholder="https://..."
                          helperText="Paste the link to a 360 virtual tour of the apartment"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      {/* Price and Discount - Super Admin Only */}
                      {isSuperAdmin && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Price"
                              name="price"
                              value={editFormData.price || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, price: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Discount"
                              name="discount"
                              value={editFormData.discount || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, discount: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}
                      {isSuperAdmin && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Profit Percentage"
                              name="profitPercentage"
                              value={editFormData.profitPercentage || ""}
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  profitPercentage: e.target.value,
                                })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Insurance Price"
                              name="insurancePrice"
                              value={editFormData.insurancePrice || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, insurancePrice: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </MDBox>
                )}

                {/* Step 3: Compound Information */}
                {editActiveStep === 3 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>apartment</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Compound Information
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          sx={{
                            minHeight: "44px",
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.300",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={editFormData.isInCompound || false}
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    isInCompound: e.target.checked,
                                  })
                                }
                                color="info"
                              />
                            }
                            label={
                              <MDTypography variant="body1" fontWeight="medium">
                                Property is in a Compound
                              </MDTypography>
                            }
                          />
                        </MDBox>
                      </Grid>
                      {editFormData.isInCompound && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Compound Name (English)"
                              name="compoundNameEn"
                              value={editFormData.compoundNameEn || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, compoundNameEn: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Compound Name (Arabic)"
                              name="compoundNameAr"
                              value={editFormData.compoundNameAr || ""}
                              onChange={(e) =>
                                setEditFormData({ ...editFormData, compoundNameAr: e.target.value })
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "44px",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MDTypography variant="body2" mb={1.5} fontWeight="medium">
                              Compound Facilities
                            </MDTypography>
                            <Autocomplete
                              multiple
                              options={[
                                { en: "Swimming Pool", ar: "مسبح" },
                                { en: "Gym/Fitness Center", ar: "صالة ألعاب رياضية" },
                                { en: "Playground", ar: "ملعب أطفال" },
                                { en: "Parking", ar: "موقف سيارات" },
                                { en: "Security", ar: "أمن" },
                                { en: "Garden", ar: "حديقة" },
                                { en: "BBQ Area", ar: "منطقة شواء" },
                                { en: "Clubhouse", ar: "نادي اجتماعي" },
                                { en: "Tennis Court", ar: "ملعب تنس" },
                                { en: "Basketball Court", ar: "ملعب كرة سلة" },
                                { en: "Football Field", ar: "ملعب كرة قدم" },
                                { en: "Running Track", ar: "مسار جري" },
                                { en: "Kids Play Area", ar: "منطقة لعب للأطفال" },
                                { en: "Laundry Room", ar: "غرفة غسيل" },
                                { en: "Storage Units", ar: "وحدات تخزين" },
                                { en: "Elevator", ar: "مصعد" },
                                { en: "24/7 Security", ar: "أمن على مدار الساعة" },
                                { en: "CCTV", ar: "كاميرات مراقبة" },
                                { en: "Gated Community", ar: "مجتمع مسور" },
                                { en: "Landscaped Gardens", ar: "حدائق منسقة" },
                                { en: "Reception Area", ar: "منطقة استقبال" },
                                { en: "Business Center", ar: "مركز أعمال" },
                                { en: "Library", ar: "مكتبة" },
                                { en: "Cinema Room", ar: "قاعة سينما" },
                                { en: "Spa", ar: "سبا" },
                                { en: "Restaurant", ar: "مطعم" },
                                { en: "Café", ar: "مقهى" },
                                { en: "Supermarket", ar: "سوبر ماركت" },
                                { en: "Pharmacy", ar: "صيدلية" },
                                { en: "ATM", ar: "صراف آلي" },
                              ]}
                              getOptionLabel={(option) =>
                                typeof option === "object" ? option.en : option
                              }
                              isOptionEqualToValue={(option, value) => {
                                const optionEn = typeof option === "object" ? option.en : option;
                                const valueEn = typeof value === "object" ? value.en : value;
                                return optionEn === valueEn;
                              }}
                              value={(editFormData.compoundFacilities || []).map((facility) => {
                                if (typeof facility === "object" && (facility.en || facility.ar)) {
                                  return facility;
                                }
                                return { en: facility, ar: facility };
                              })}
                              onChange={(event, newValue) => {
                                setEditFormData({ ...editFormData, compoundFacilities: newValue });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select facilities"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      minHeight: "44px",
                                    },
                                  }}
                                />
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                  const label = typeof option === "object" ? option.en : option;
                                  return (
                                    <Chip
                                      {...getTagProps({ index })}
                                      key={index}
                                      label={label}
                                      size="small"
                                      sx={{ height: "32px", m: 0.5 }}
                                    />
                                  );
                                })
                              }
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </MDBox>
                )}

                {/* Step 4: WiFi & Utilities */}
                {editActiveStep === 4 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>wifi</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        WiFi & Utilities
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="WiFi Name"
                          name="wifiName"
                          value={editFormData.wifiName || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, wifiName: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="WiFi Password"
                          name="wifiPassword"
                          type="text"
                          value={editFormData.wifiPassword || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, wifiPassword: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Electricity Meter Type</InputLabel>
                          <Select
                            value={editFormData.electricityMeterType || ""}
                            label="Electricity Meter Type"
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                electricityMeterType: e.target.value,
                              })
                            }
                            sx={{
                              minHeight: "44px",
                              "& .MuiSelect-select": {
                                minHeight: "44px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    minHeight: "44px",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Prepaid Meter (Card Recharge)">
                              Prepaid Meter (Card Recharge)
                            </MenuItem>
                            <MenuItem value="Postpaid Meter (Monthly Payment)">
                              Postpaid Meter (Monthly Payment)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Water Meter Type</InputLabel>
                          <Select
                            value={editFormData.waterMeterType || ""}
                            label="Water Meter Type"
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, waterMeterType: e.target.value })
                            }
                            sx={{
                              minHeight: "44px",
                              "& .MuiSelect-select": {
                                minHeight: "44px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    minHeight: "44px",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Prepaid Meter (Card Recharge)">
                              Prepaid Meter (Card Recharge)
                            </MenuItem>
                            <MenuItem value="Postpaid Meter (Monthly Payment)">
                              Postpaid Meter (Monthly Payment)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Property View</InputLabel>
                          <Select
                            value={editFormData.propertyView || ""}
                            label="Property View"
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, propertyView: e.target.value })
                            }
                            sx={{
                              minHeight: "44px",
                              "& .MuiSelect-select": {
                                minHeight: "44px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    minHeight: "44px",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="Front View">Front View</MenuItem>
                            <MenuItem value="Back View">Back View</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Access Code"
                          name="accessCode"
                          value={editFormData.accessCode || ""}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, accessCode: e.target.value })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {/* Step 5: Amenities */}
                {editActiveStep === 5 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>star</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Amenities
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <MDTypography
                          variant="body2"
                          mb={2}
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          Select all amenities available in this property
                        </MDTypography>
                        {loadingAmenitiesCriteria ? (
                          <MDBox display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                          </MDBox>
                        ) : (
                          <Autocomplete
                            multiple
                            filterSelectedOptions
                            options={amenitiesList}
                            getOptionLabel={(option) => {
                              if (typeof option === "object") {
                                return option.name?.en || option.en || option;
                              }
                              return option;
                            }}
                            isOptionEqualToValue={(option, value) => {
                              const optionEn =
                                typeof option === "object" ? option.name?.en || option.en : option;
                              const valueEn =
                                typeof value === "object" ? value.name?.en || value.en : value;
                              return optionEn === valueEn;
                            }}
                            value={(editFormData.amenities || []).map((amenity) => {
                              const amenityEn =
                                typeof amenity === "object"
                                  ? amenity.en || amenity.name?.en
                                  : amenity;
                              // Find matching amenity from API list
                              const matched = amenitiesList.find((a) => {
                                const aEn = a.name?.en || a.en;
                                return aEn === amenityEn;
                              });
                              if (matched) return matched;
                              // Fallback to original format
                              if (
                                typeof amenity === "object" &&
                                (amenity.en || amenity.ar || amenity.name)
                              ) {
                                return amenity;
                              }
                              return { name: { en: amenity, ar: amenity } };
                            })}
                            onChange={(event, newValue) => {
                              // Convert to format expected by backend
                              const formattedAmenities = newValue.map((amenity) => {
                                if (amenity.name) {
                                  return { en: amenity.name.en, ar: amenity.name.ar };
                                }
                                return {
                                  en: amenity.en || amenity,
                                  ar: amenity.ar || amenity.en || amenity,
                                };
                              });
                              setEditFormData({ ...editFormData, amenities: formattedAmenities });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select amenities"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    minHeight: "44px",
                                  },
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const label =
                                  typeof option === "object"
                                    ? option.name?.en || option.en
                                    : option;
                                return (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={index}
                                    label={label}
                                    size="small"
                                    sx={{ height: "32px", m: 0.5 }}
                                  />
                                );
                              })
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} mt={3}>
                        <MDTypography variant="h6" mb={2} fontWeight="bold">
                          Property Criteria
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          mb={2}
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          Select all criteria that apply to this property
                        </MDTypography>
                        {loadingAmenitiesCriteria ? (
                          <MDBox display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                          </MDBox>
                        ) : (
                          <Autocomplete
                            multiple
                            filterSelectedOptions
                            options={criteriaList}
                            getOptionLabel={(option) => {
                              if (typeof option === "object") {
                                return option.name?.en || option.name || option;
                              }
                              return option;
                            }}
                            isOptionEqualToValue={(option, value) => {
                              const optionEn =
                                typeof option === "object"
                                  ? option.name?.en || option.name
                                  : option;
                              const valueEn =
                                typeof value === "object" ? value.name?.en || value.name : value;
                              return optionEn === valueEn;
                            }}
                            value={(editFormData.criteria || []).map((criterion) => {
                              const criterionNameEn =
                                typeof criterion === "object"
                                  ? criterion.name?.en || criterion.name
                                  : criterion;
                              // Find matching criterion from API list
                              const matched = criteriaList.find((c) => {
                                const cEn = c.name?.en || c.name;
                                return cEn === criterionNameEn;
                              });
                              if (matched) return matched;
                              // Fallback to original format
                              return criterion;
                            })}
                            onChange={(event, newValue) => {
                              // Convert to format expected by backend
                              const formattedCriteria = newValue.map((criterion) => {
                                if (criterion.name) {
                                  return {
                                    name: {
                                      en: criterion.name.en,
                                      ar: criterion.name.ar,
                                    },
                                    category: criterion.category
                                      ? {
                                          en: criterion.category.en || "",
                                          ar: criterion.category.ar || "",
                                        }
                                      : undefined,
                                  };
                                }
                                return criterion;
                              });
                              setEditFormData({ ...editFormData, criteria: formattedCriteria });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select criteria"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    minHeight: "44px",
                                  },
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const label =
                                  typeof option === "object"
                                    ? option.name?.en || option.name
                                    : option;
                                return (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={index}
                                    label={label}
                                    size="small"
                                    sx={{ height: "32px", m: 0.5 }}
                                  />
                                );
                              })
                            }
                          />
                        )}
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {/* Step 6: Media Upload */}
                {editActiveStep === 6 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>photo_library</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Media Upload
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Property Images
                          </MDTypography>
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>upload</Icon>}
                            sx={{ mb: 2, minHeight: "44px" }}
                          >
                            Upload Images
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              hidden
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                setNewImages([...newImages, ...files]);
                              }}
                            />
                          </MDButton>
                          {newImages.length > 0 && (
                            <MDBox mt={2}>
                              <MDTypography variant="body2" mb={1} fontWeight="medium">
                                New Images ({newImages.length})
                              </MDTypography>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                {newImages.map((file, idx) => (
                                  <Chip
                                    key={idx}
                                    label={file.name}
                                    onDelete={() => {
                                      setNewImages(newImages.filter((_, i) => i !== idx));
                                    }}
                                    color="info"
                                    size="small"
                                  />
                                ))}
                              </MDBox>
                            </MDBox>
                          )}
                          {property.images && property.images.length > 0 && (
                            <MDBox mt={3}>
                              <MDTypography
                                variant="body2"
                                mb={2}
                                fontWeight="medium"
                                color="text.secondary"
                              >
                                Current Images ({property.images.length}) - Click to mark for
                                deletion
                              </MDTypography>
                              <Grid container spacing={2}>
                                {property.images.map((img, idx) => (
                                  <Grid item xs={6} sm={4} md={3} key={idx}>
                                    <MDBox
                                      position="relative"
                                      sx={{
                                        cursor: "pointer",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: imagesToDelete.includes(img)
                                          ? "2px solid"
                                          : "2px solid transparent",
                                        borderColor: imagesToDelete.includes(img)
                                          ? "error.main"
                                          : "transparent",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                          transform: "scale(1.02)",
                                          boxShadow: 2,
                                        },
                                      }}
                                      onClick={() => {
                                        if (imagesToDelete.includes(img)) {
                                          setImagesToDelete(
                                            imagesToDelete.filter((i) => i !== img)
                                          );
                                        } else {
                                          setImagesToDelete([...imagesToDelete, img]);
                                        }
                                      }}
                                    >
                                      <img
                                        src={img}
                                        alt={`Property ${idx}`}
                                        style={{
                                          width: "100%",
                                          height: "150px",
                                          objectFit: "cover",
                                        }}
                                      />
                                      {imagesToDelete.includes(img) && (
                                        <MDBox
                                          position="absolute"
                                          top={0}
                                          left={0}
                                          right={0}
                                          bottom={0}
                                          bgcolor="rgba(244, 67, 54, 0.8)"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <Icon sx={{ color: "white", fontSize: "2.5rem" }}>
                                            delete
                                          </Icon>
                                        </MDBox>
                                      )}
                                    </MDBox>
                                  </Grid>
                                ))}
                              </Grid>
                            </MDBox>
                          )}
                        </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Property Videos
                          </MDTypography>
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>videocam</Icon>}
                            fullWidth
                            sx={{ minHeight: "44px" }}
                          >
                            Upload Videos
                            <input
                              type="file"
                              accept="video/*"
                              multiple
                              hidden
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                setNewVideos([...newVideos, ...files]);
                              }}
                            />
                          </MDButton>
                          {newVideos.length > 0 && (
                            <MDBox mt={2}>
                              <MDTypography variant="body2" mb={1} fontWeight="medium">
                                New Videos ({newVideos.length})
                              </MDTypography>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                {newVideos.map((file, idx) => (
                                  <Chip
                                    key={idx}
                                    label={file.name}
                                    onDelete={() => {
                                      setNewVideos(newVideos.filter((_, i) => i !== idx));
                                    }}
                                    color="info"
                                    size="small"
                                  />
                                ))}
                              </MDBox>
                            </MDBox>
                          )}
                          {property.videos && property.videos.length > 0 && (
                            <MDBox mt={3}>
                              <MDTypography
                                variant="body2"
                                mb={2}
                                fontWeight="medium"
                                color="text.secondary"
                              >
                                Current Videos ({property.videos.length}) - Click to mark for
                                deletion
                              </MDTypography>
                              <Grid container spacing={2}>
                                {property.videos.map((videoUrl, idx) => (
                                  <Grid item xs={12} sm={6} md={4} key={idx}>
                                    <MDBox
                                      sx={{
                                        p: 2,
                                        border: "2px solid",
                                        borderColor: videosToDelete.includes(videoUrl)
                                          ? "error.main"
                                          : "divider",
                                        borderRadius: 2,
                                        cursor: "pointer",
                                        bgcolor: videosToDelete.includes(videoUrl)
                                          ? "error.lighter"
                                          : "white",
                                        "&:hover": { bgcolor: "grey.50" },
                                      }}
                                      onClick={() => {
                                        if (videosToDelete.includes(videoUrl)) {
                                          setVideosToDelete(
                                            videosToDelete.filter((v) => v !== videoUrl)
                                          );
                                        } else {
                                          setVideosToDelete([...videosToDelete, videoUrl]);
                                        }
                                      }}
                                    >
                                      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                        <Icon color="error">videocam</Icon>
                                        <MDTypography variant="body2" fontWeight="medium">
                                          Video {idx + 1}
                                        </MDTypography>
                                      </MDBox>
                                      <MDButton
                                        component="a"
                                        href={videoUrl}
                                        target="_blank"
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Icon>play_arrow</Icon>}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Play
                                      </MDButton>
                                      {videosToDelete.includes(videoUrl) && (
                                        <MDTypography
                                          variant="caption"
                                          color="error"
                                          display="block"
                                          mt={1}
                                        >
                                          Marked for deletion
                                        </MDTypography>
                                      )}
                                    </MDBox>
                                  </Grid>
                                ))}
                              </Grid>
                            </MDBox>
                          )}
                        </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Building Images
                          </MDTypography>
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>business</Icon>}
                            fullWidth
                            sx={{ minHeight: "44px" }}
                          >
                            Upload Building Images
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              hidden
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                setNewBuildingImages([...newBuildingImages, ...files]);
                              }}
                            />
                          </MDButton>
                          {newBuildingImages.length > 0 && (
                            <MDBox mt={2}>
                              <MDTypography variant="body2" mb={1} fontWeight="medium">
                                New Building Images ({newBuildingImages.length})
                              </MDTypography>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                {newBuildingImages.map((file, idx) => (
                                  <Chip
                                    key={idx}
                                    label={file.name}
                                    onDelete={() => {
                                      setNewBuildingImages(
                                        newBuildingImages.filter((_, i) => i !== idx)
                                      );
                                    }}
                                    color="info"
                                    size="small"
                                  />
                                ))}
                              </MDBox>
                            </MDBox>
                          )}
                          {property.buildingImages && property.buildingImages.length > 0 && (
                            <MDBox mt={3}>
                              <MDTypography
                                variant="body2"
                                mb={2}
                                fontWeight="medium"
                                color="text.secondary"
                              >
                                Current Building Images ({property.buildingImages.length}) - Click
                                to mark for deletion
                              </MDTypography>
                              <Grid container spacing={2}>
                                {property.buildingImages.map((img, idx) => (
                                  <Grid item xs={6} sm={4} md={3} key={idx}>
                                    <MDBox
                                      position="relative"
                                      sx={{
                                        cursor: "pointer",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: buildingImagesToDelete.includes(img)
                                          ? "2px solid"
                                          : "2px solid transparent",
                                        borderColor: buildingImagesToDelete.includes(img)
                                          ? "error.main"
                                          : "transparent",
                                        "&:hover": { transform: "scale(1.02)", boxShadow: 2 },
                                      }}
                                      onClick={() => {
                                        if (buildingImagesToDelete.includes(img)) {
                                          setBuildingImagesToDelete(
                                            buildingImagesToDelete.filter((i) => i !== img)
                                          );
                                        } else {
                                          setBuildingImagesToDelete([
                                            ...buildingImagesToDelete,
                                            img,
                                          ]);
                                        }
                                      }}
                                    >
                                      <img
                                        src={typeof img === "string" ? img : img.url || img}
                                        alt={`Building ${idx}`}
                                        style={{
                                          width: "100%",
                                          height: "100px",
                                          objectFit: "cover",
                                        }}
                                      />
                                      {buildingImagesToDelete.includes(img) && (
                                        <MDBox
                                          position="absolute"
                                          top={0}
                                          left={0}
                                          right={0}
                                          bottom={0}
                                          bgcolor="rgba(244, 67, 54, 0.6)"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <Icon sx={{ color: "white", fontSize: "2rem" }}>
                                            delete
                                          </Icon>
                                        </MDBox>
                                      )}
                                    </MDBox>
                                  </Grid>
                                ))}
                              </Grid>
                            </MDBox>
                          )}
                        </Card>
                      </Grid>

                      {/* Living Room Photo */}
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Living Room Photo
                          </MDTypography>
                          {property.livingRoomPhoto && !livingRoomPhotoToDelete && (
                            <MDBox mb={2}>
                              <img
                                src={property.livingRoomPhoto}
                                alt="Living Room"
                                style={{
                                  width: "100%",
                                  maxHeight: "200px",
                                  objectFit: "cover",
                                  borderRadius: 8,
                                }}
                              />
                              <MDButton
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<Icon>delete</Icon>}
                                onClick={() => setLivingRoomPhotoToDelete(true)}
                                sx={{ mt: 1 }}
                              >
                                Delete Current Photo
                              </MDButton>
                            </MDBox>
                          )}
                          {livingRoomPhotoToDelete && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              Photo marked for deletion
                            </Alert>
                          )}
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>upload</Icon>}
                            fullWidth
                            sx={{ minHeight: "44px" }}
                          >
                            {newLivingRoomPhoto ? "Change Photo" : "Upload Living Room Photo"}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) setNewLivingRoomPhoto(file);
                              }}
                            />
                          </MDButton>
                          {newLivingRoomPhoto && (
                            <MDBox mt={2}>
                              <Chip
                                label={newLivingRoomPhoto.name}
                                onDelete={() => setNewLivingRoomPhoto(null)}
                                color="info"
                                size="small"
                              />
                            </MDBox>
                          )}
                        </Card>
                      </Grid>

                      {/* Room Photos */}
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Room Photos
                          </MDTypography>
                          <MDTypography variant="body2" mb={2} color="text.secondary">
                            Upload one photo per room (based on number of rooms:{" "}
                            {property.rooms || 0})
                          </MDTypography>
                          {property.roomPhotos && property.roomPhotos.length > 0 && (
                            <MDBox mb={2}>
                              {property.roomPhotos.map((roomPhoto, index) => {
                                const photoUrl =
                                  typeof roomPhoto === "object" ? roomPhoto.photoUrl : roomPhoto;
                                const roomNumber =
                                  typeof roomPhoto === "object" ? roomPhoto.roomNumber : index + 1;
                                const isMarkedForDelete = roomPhotosToDelete.includes(photoUrl);
                                return (
                                  <MDBox
                                    key={index}
                                    mb={1.5}
                                    p={1.5}
                                    border="1px solid"
                                    borderColor="divider"
                                    borderRadius={1}
                                  >
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={1}
                                    >
                                      <MDTypography variant="body2" fontWeight="medium">
                                        Room {roomNumber}
                                      </MDTypography>
                                      {!isMarkedForDelete && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            setRoomPhotosToDelete([...roomPhotosToDelete, photoUrl])
                                          }
                                        >
                                          <Icon fontSize="small">delete</Icon>
                                        </IconButton>
                                      )}
                                    </MDBox>
                                    {isMarkedForDelete ? (
                                      <Alert severity="warning" sx={{ py: 0.5 }}>
                                        Marked for deletion
                                      </Alert>
                                    ) : (
                                      <img
                                        src={photoUrl}
                                        alt={`Room ${roomNumber}`}
                                        style={{
                                          width: "100%",
                                          maxHeight: "150px",
                                          objectFit: "cover",
                                          borderRadius: 4,
                                        }}
                                      />
                                    )}
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          )}
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>upload</Icon>}
                            fullWidth
                            sx={{ minHeight: "44px" }}
                          >
                            Upload Room Photo
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const roomNumber = Object.keys(newRoomPhotos).length + 1;
                                  setNewRoomPhotos({
                                    ...newRoomPhotos,
                                    [`room${roomNumber}`]: file,
                                  });
                                }
                              }}
                            />
                          </MDButton>
                          {Object.keys(newRoomPhotos).length > 0 && (
                            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                              {Object.entries(newRoomPhotos).map(([key, file]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${file.name}`}
                                  onDelete={() => {
                                    const updated = { ...newRoomPhotos };
                                    delete updated[key];
                                    setNewRoomPhotos(updated);
                                  }}
                                  color="info"
                                  size="small"
                                />
                              ))}
                            </MDBox>
                          )}
                        </Card>
                      </Grid>

                      {/* Bathroom Photos */}
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Bathroom Photos
                          </MDTypography>
                          <MDTypography variant="body2" mb={2} color="text.secondary">
                            Upload one photo per bathroom (based on number of bathrooms:{" "}
                            {property.bathrooms || 0})
                          </MDTypography>
                          {property.bathroomPhotos && property.bathroomPhotos.length > 0 && (
                            <MDBox mb={2}>
                              {property.bathroomPhotos.map((bathroomPhoto, index) => {
                                const photoUrl =
                                  typeof bathroomPhoto === "object"
                                    ? bathroomPhoto.photoUrl
                                    : bathroomPhoto;
                                const bathroomNumber =
                                  typeof bathroomPhoto === "object"
                                    ? bathroomPhoto.bathroomNumber
                                    : index + 1;
                                const isMarkedForDelete = bathroomPhotosToDelete.includes(photoUrl);
                                return (
                                  <MDBox
                                    key={index}
                                    mb={1.5}
                                    p={1.5}
                                    border="1px solid"
                                    borderColor="divider"
                                    borderRadius={1}
                                  >
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={1}
                                    >
                                      <MDTypography variant="body2" fontWeight="medium">
                                        Bathroom {bathroomNumber}
                                      </MDTypography>
                                      {!isMarkedForDelete && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            setBathroomPhotosToDelete([
                                              ...bathroomPhotosToDelete,
                                              photoUrl,
                                            ])
                                          }
                                        >
                                          <Icon fontSize="small">delete</Icon>
                                        </IconButton>
                                      )}
                                    </MDBox>
                                    {isMarkedForDelete ? (
                                      <Alert severity="warning" sx={{ py: 0.5 }}>
                                        Marked for deletion
                                      </Alert>
                                    ) : (
                                      <img
                                        src={photoUrl}
                                        alt={`Bathroom ${bathroomNumber}`}
                                        style={{
                                          width: "100%",
                                          maxHeight: "150px",
                                          objectFit: "cover",
                                          borderRadius: 4,
                                        }}
                                      />
                                    )}
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          )}
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>upload</Icon>}
                            fullWidth
                            sx={{ minHeight: "44px" }}
                          >
                            Upload Bathroom Photo
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const bathroomNumber = Object.keys(newBathroomPhotos).length + 1;
                                  setNewBathroomPhotos({
                                    ...newBathroomPhotos,
                                    [`bathroom${bathroomNumber}`]: file,
                                  });
                                }
                              }}
                            />
                          </MDButton>
                          {Object.keys(newBathroomPhotos).length > 0 && (
                            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                              {Object.entries(newBathroomPhotos).map(([key, file]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${file.name}`}
                                  onDelete={() => {
                                    const updated = { ...newBathroomPhotos };
                                    delete updated[key];
                                    setNewBathroomPhotos(updated);
                                  }}
                                  color="info"
                                  size="small"
                                />
                              ))}
                            </MDBox>
                          )}
                        </Card>
                      </Grid>

                      {/* Balcony Photos */}
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Balcony Photos
                          </MDTypography>
                          <MDTypography variant="body2" mb={2} color="text.secondary">
                            Upload one photo per balcony (based on number of balconies:{" "}
                            {editFormData.balconies || property?.balconies || 0})
                          </MDTypography>
                          {property?.balconyPhotos && property.balconyPhotos.length > 0 && (
                            <MDBox mb={2}>
                              {property.balconyPhotos.map((balconyPhoto, index) => {
                                const photoUrl =
                                  typeof balconyPhoto === "object"
                                    ? balconyPhoto.photoUrl
                                    : balconyPhoto;
                                const balconyNumber =
                                  typeof balconyPhoto === "object"
                                    ? balconyPhoto.balconyNumber
                                    : index + 1;
                                const isMarkedForDelete = balconyPhotosToDelete.includes(photoUrl);
                                return (
                                  <MDBox
                                    key={index}
                                    mb={1.5}
                                    p={1.5}
                                    border="1px solid"
                                    borderColor="divider"
                                    borderRadius={1}
                                  >
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={1}
                                    >
                                      <MDTypography variant="body2" fontWeight="medium">
                                        Balcony {balconyNumber}
                                      </MDTypography>
                                      {!isMarkedForDelete && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            setBalconyPhotosToDelete([
                                              ...balconyPhotosToDelete,
                                              photoUrl,
                                            ])
                                          }
                                        >
                                          <Icon fontSize="small">delete</Icon>
                                        </IconButton>
                                      )}
                                    </MDBox>
                                    {isMarkedForDelete ? (
                                      <Alert severity="warning" sx={{ py: 0.5 }}>
                                        Marked for deletion
                                      </Alert>
                                    ) : (
                                      <img
                                        src={photoUrl}
                                        alt={`Balcony ${balconyNumber}`}
                                        style={{
                                          width: "100%",
                                          maxHeight: "150px",
                                          objectFit: "cover",
                                          borderRadius: 4,
                                        }}
                                      />
                                    )}
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          )}
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>upload</Icon>}
                            fullWidth
                            sx={{ minHeight: "44px" }}
                          >
                            Upload Balcony Photo
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const balconyNumber = Object.keys(newBalconyPhotos).length + 1;
                                  setNewBalconyPhotos({
                                    ...newBalconyPhotos,
                                    [`balcony${balconyNumber}`]: file,
                                  });
                                }
                              }}
                            />
                          </MDButton>
                          {Object.keys(newBalconyPhotos).length > 0 && (
                            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                              {Object.entries(newBalconyPhotos).map(([key, file]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${file.name}`}
                                  onDelete={() => {
                                    const updated = { ...newBalconyPhotos };
                                    delete updated[key];
                                    setNewBalconyPhotos(updated);
                                  }}
                                  color="info"
                                  size="small"
                                />
                              ))}
                            </MDBox>
                          )}
                        </Card>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}

                {/* Step 7: Legal Papers */}
                {editActiveStep === 7 && (
                  <MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.05) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "info.main", fontSize: "1.5rem" }}>description</Icon>
                      <MDTypography variant="h6" fontWeight="bold" color="info.main">
                        Legal Papers
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Upload Legal Documents
                          </MDTypography>
                          <MDButton
                            component="label"
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>upload_file</Icon>}
                            sx={{ mb: 3, minHeight: "44px" }}
                          >
                            Upload Documents (PDF, DOC, DOCX)
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              multiple
                              hidden
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                setNewLegalPapers([...newLegalPapers, ...files]);
                              }}
                            />
                          </MDButton>
                          {newLegalPapers.length > 0 && (
                            <MDBox mb={3}>
                              <MDTypography variant="body2" mb={1.5} fontWeight="medium">
                                New Documents ({newLegalPapers.length})
                              </MDTypography>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                {newLegalPapers.map((file, idx) => (
                                  <Chip
                                    key={idx}
                                    label={file.name}
                                    onDelete={() => {
                                      setNewLegalPapers(newLegalPapers.filter((_, i) => i !== idx));
                                    }}
                                    color="info"
                                    size="small"
                                  />
                                ))}
                              </MDBox>
                            </MDBox>
                          )}
                          {property.legalPapers && property.legalPapers.length > 0 && (
                            <MDBox>
                              <MDTypography
                                variant="body2"
                                mb={2}
                                fontWeight="medium"
                                color="text.secondary"
                              >
                                Current Legal Papers ({property.legalPapers.length}) - Click to mark
                                for deletion
                              </MDTypography>
                              <List>
                                {property.legalPapers.map((paper, idx) => (
                                  <ListItem
                                    key={idx}
                                    button
                                    onClick={() => {
                                      if (legalPapersToDelete.includes(paper)) {
                                        setLegalPapersToDelete(
                                          legalPapersToDelete.filter((p) => p !== paper)
                                        );
                                      } else {
                                        setLegalPapersToDelete([...legalPapersToDelete, paper]);
                                      }
                                    }}
                                    sx={{
                                      bgcolor: legalPapersToDelete.includes(paper)
                                        ? "error.light"
                                        : "white",
                                      mb: 1.5,
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: legalPapersToDelete.includes(paper)
                                        ? "error.main"
                                        : "grey.300",
                                      transition: "all 0.2s",
                                      "&:hover": {
                                        bgcolor: legalPapersToDelete.includes(paper)
                                          ? "error.light"
                                          : "grey.100",
                                        transform: "translateX(4px)",
                                      },
                                    }}
                                  >
                                    <ListItemIcon>
                                      <Icon
                                        sx={{
                                          color: legalPapersToDelete.includes(paper)
                                            ? "error.main"
                                            : "info.main",
                                        }}
                                      >
                                        description
                                      </Icon>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={paper.split("/").pop()}
                                      primaryTypographyProps={{
                                        fontWeight: legalPapersToDelete.includes(paper)
                                          ? "bold"
                                          : "medium",
                                      }}
                                    />
                                    {legalPapersToDelete.includes(paper) && (
                                      <IconButton size="small" sx={{ color: "error.main" }}>
                                        <Icon>delete</Icon>
                                      </IconButton>
                                    )}
                                  </ListItem>
                                ))}
                              </List>
                            </MDBox>
                          )}
                        </Card>
                      </Grid>

                      {/* Property Owner Documents */}
                      <Grid item xs={12} mt={2}>
                        <Card sx={{ p: 3, borderRadius: 2, bgcolor: "grey.50" }}>
                          <MDTypography variant="h6" mb={2} fontWeight="bold">
                            Property Owner Documents
                          </MDTypography>

                          {/* Property Owner ID */}
                          <MDBox mb={3}>
                            <MDTypography variant="body2" mb={1} fontWeight="medium">
                              Property Owner ID (Required)
                            </MDTypography>
                            {property.propertyOwnerId && !propertyOwnerIdToDelete && (
                              <MDBox mb={1}>
                                <MDButton
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  startIcon={<Icon>description</Icon>}
                                  onClick={() =>
                                    viewPdf(
                                      fixPdfUrl(property.propertyOwnerId),
                                      "Property Owner ID"
                                    )
                                  }
                                >
                                  View Current Document
                                </MDButton>
                                <MDButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Icon>delete</Icon>}
                                  onClick={() => setPropertyOwnerIdToDelete(true)}
                                  sx={{ ml: 1 }}
                                >
                                  Delete
                                </MDButton>
                              </MDBox>
                            )}
                            {propertyOwnerIdToDelete && (
                              <Alert severity="warning" sx={{ mb: 1 }}>
                                Document marked for deletion
                              </Alert>
                            )}
                            <MDButton
                              component="label"
                              variant="outlined"
                              color="info"
                              startIcon={<Icon>upload_file</Icon>}
                              size="small"
                            >
                              {newPropertyOwnerId ? "Change Document" : "Upload Property Owner ID"}
                              <input
                                type="file"
                                accept=".pdf"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file && file.type === "application/pdf") {
                                    setNewPropertyOwnerId(file);
                                  } else {
                                    setMessage({ type: "error", text: "Please upload a PDF file" });
                                  }
                                }}
                              />
                            </MDButton>
                            {newPropertyOwnerId && (
                              <Chip
                                label={newPropertyOwnerId.name}
                                onDelete={() => setNewPropertyOwnerId(null)}
                                color="info"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </MDBox>

                          {/* Rental/Ownership Contract */}
                          <MDBox mb={3}>
                            <MDTypography variant="body2" mb={1} fontWeight="medium">
                              Rental Contract or Ownership Contract (Required)
                            </MDTypography>
                            {property.rentalOrOwnershipContract &&
                              !rentalOrOwnershipContractToDelete && (
                                <MDBox mb={1}>
                                  <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    startIcon={<Icon>description</Icon>}
                                    onClick={() =>
                                      viewPdf(
                                        fixPdfUrl(property.rentalOrOwnershipContract),
                                        "Rental/Ownership Contract"
                                      )
                                    }
                                  >
                                    View Current Document
                                  </MDButton>
                                  <MDButton
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<Icon>delete</Icon>}
                                    onClick={() => setRentalOrOwnershipContractToDelete(true)}
                                    sx={{ ml: 1 }}
                                  >
                                    Delete
                                  </MDButton>
                                </MDBox>
                              )}
                            {rentalOrOwnershipContractToDelete && (
                              <Alert severity="warning" sx={{ mb: 1 }}>
                                Document marked for deletion
                              </Alert>
                            )}
                            <MDButton
                              component="label"
                              variant="outlined"
                              color="info"
                              startIcon={<Icon>upload_file</Icon>}
                              size="small"
                            >
                              {newRentalOrOwnershipContract
                                ? "Change Document"
                                : "Upload Rental/Ownership Contract"}
                              <input
                                type="file"
                                accept=".pdf"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file && file.type === "application/pdf") {
                                    setNewRentalOrOwnershipContract(file);
                                  } else {
                                    setMessage({ type: "error", text: "Please upload a PDF file" });
                                  }
                                }}
                              />
                            </MDButton>
                            {newRentalOrOwnershipContract && (
                              <Chip
                                label={newRentalOrOwnershipContract.name}
                                onDelete={() => setNewRentalOrOwnershipContract(null)}
                                color="info"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </MDBox>

                          {/* Power of Attorney */}
                          <MDBox>
                            <MDTypography variant="body2" mb={1} fontWeight="medium">
                              Power of Attorney (Optional)
                            </MDTypography>
                            {property.powerOfAttorney && !powerOfAttorneyToDelete && (
                              <MDBox mb={1}>
                                <MDButton
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  startIcon={<Icon>description</Icon>}
                                  onClick={() =>
                                    viewPdf(
                                      fixPdfUrl(property.powerOfAttorney),
                                      "Power of Attorney"
                                    )
                                  }
                                >
                                  View Current Document
                                </MDButton>
                                <MDButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Icon>delete</Icon>}
                                  onClick={() => setPowerOfAttorneyToDelete(true)}
                                  sx={{ ml: 1 }}
                                >
                                  Delete
                                </MDButton>
                              </MDBox>
                            )}
                            {powerOfAttorneyToDelete && (
                              <Alert severity="warning" sx={{ mb: 1 }}>
                                Document marked for deletion
                              </Alert>
                            )}
                            <MDButton
                              component="label"
                              variant="outlined"
                              color="info"
                              startIcon={<Icon>upload_file</Icon>}
                              size="small"
                            >
                              {newPowerOfAttorney ? "Change Document" : "Upload Power of Attorney"}
                              <input
                                type="file"
                                accept=".pdf"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file && file.type === "application/pdf") {
                                    setNewPowerOfAttorney(file);
                                  } else {
                                    setMessage({ type: "error", text: "Please upload a PDF file" });
                                  }
                                }}
                              />
                            </MDButton>
                            {newPowerOfAttorney && (
                              <Chip
                                label={newPowerOfAttorney.name}
                                onDelete={() => setNewPowerOfAttorney(null)}
                                color="info"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </MDBox>
                        </Card>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: "grey.50",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            borderTop: "1px solid",
            borderColor: "grey.300",
            borderRadius: "0 0 16px 16px",
          }}
        >
          <MDButton
            onClick={() => {
              setEditDialogOpen(false);
              setEditActiveStep(0);
            }}
            color="secondary"
            variant="outlined"
            fullWidth
            sx={{
              minHeight: "44px",
              px: 3,
              borderRadius: 2,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Cancel
          </MDButton>
          <MDBox
            display="flex"
            gap={2}
            flexWrap="wrap"
            sx={{
              flex: { xs: "none", sm: 1 },
              justifyContent: { xs: "stretch", sm: "flex-end" },
              "& .MuiButton-root": {
                flex: { xs: "1 1 auto", sm: "none" },
                minWidth: { xs: 0, sm: "auto" },
              },
            }}
          >
            <MDButton
              onClick={() => {
                if (editActiveStep > 0) {
                  setEditActiveStep(editActiveStep - 1);
                }
              }}
              disabled={editActiveStep === 0}
              color="info"
              variant="outlined"
              startIcon={<Icon>arrow_back</Icon>}
              sx={{
                minHeight: "44px",
                px: 3,
                borderRadius: 2,
              }}
            >
              Previous
            </MDButton>
            {editActiveStep < 7 ? (
              <MDButton
                onClick={() => {
                  setEditActiveStep(editActiveStep + 1);
                }}
                color="info"
                variant="gradient"
                endIcon={<Icon>arrow_forward</Icon>}
                sx={{
                  minHeight: "44px",
                  px: 4,
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                }}
              >
                Next
              </MDButton>
            ) : (
              <MDButton
                onClick={handleSaveProperty}
                color="success"
                variant="gradient"
                disabled={updatingProperty}
                startIcon={
                  updatingProperty ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Icon>save</Icon>
                  )
                }
                sx={{
                  minHeight: "44px",
                  px: 4,
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(46, 125, 50, 0.3)",
                }}
              >
                {updatingProperty ? "Saving..." : "Save Changes"}
              </MDButton>
            )}
          </MDBox>
        </DialogActions>
      </Dialog>

      {/* Finance Dialog - Super Admin Only */}
      <Dialog
        open={financeDialogOpen}
        onClose={() => {
          if (!updatingFinance) {
            setFinanceDialogOpen(false);
            setFinanceModalError("");
            setFinanceData({
              price: "",
              profitPercentage: "",
              insurancePrice: "",
              maintenanceCostPercentage: "",
              maxGuests: "",
              extraFeesPerPerson: "",
              earlyCheckInFeePercentage: "",
              lateCheckOutFeePercentage: "",
              packages: [],
              checkInOutAgentId: "",
              expectedReadyDate: "",
            });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)",
            color: "white",
            py: 2.5,
            px: 3,
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <Icon sx={{ fontSize: "2rem" }}>attach_money</Icon>
            <MDTypography variant="h5" fontWeight="bold">
              {financeDialogMode === "activate"
                ? "Set Finance & Activate"
                : property?.price ||
                  property?.profitPercentage ||
                  property?.insurancePrice ||
                  property?.maintenanceCostPercentage != null ||
                  property?.maxGuests != null ||
                  property?.extraFeesPerPerson != null ||
                  property?.earlyCheckInFeePercentage != null ||
                  property?.lateCheckOutFeePercentage != null
                ? "Edit Finance"
                : "Add Finance"}
            </MDTypography>
          </MDBox>
          <IconButton
            onClick={() => {
              if (!updatingFinance) {
                setFinanceDialogOpen(false);
                setFinanceModalError("");
                setFinanceData({
                  price: "",
                  profitPercentage: "",
                  insurancePrice: "",
                  maintenanceCostPercentage: "",
                  maxGuests: "",
                  extraFeesPerPerson: "",
                  earlyCheckInFeePercentage: "",
                  lateCheckOutFeePercentage: "",
                  packages: [],
                  checkInOutAgentId: "",
                  expectedReadyDate: "",
                });
              }
            }}
            sx={{ color: "white" }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {financeModalError && (
            <Alert severity="error" sx={{ mb: 2, mt: 3 }} onClose={() => setFinanceModalError("")}>
              {financeModalError}
            </Alert>
          )}
          <MDBox>
            <MDTypography variant="body2" color="text.secondary" mb={3} mt={3}>
              {property?.status === "Contracting"
                ? "Set finance details and check-in/check-out agent to move the property to Preparation phase."
                : financeDialogMode === "activate"
                ? "Set finance details. Saving will activate the property and make it available for booking."
                : "Set the price per night, profit percentage, insurance price, maintenance cost percentage, allowed person number, extra fees per person, early check-in fee, late checkout fee, and packages for this property."}
            </MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price per Night (EGP) *"
                  name="price"
                  value={financeData.price}
                  onChange={(e) => setFinanceData({ ...financeData, price: e.target.value })}
                  helperText="Price per night for renting this property"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Profit Percentage (%)"
                  name="profitPercentage"
                  value={financeData.profitPercentage}
                  onChange={(e) =>
                    setFinanceData({ ...financeData, profitPercentage: e.target.value })
                  }
                  helperText="Percentage of revenue taken as profit (0-100)"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maintenance Cost Percentage (%)"
                  name="maintenanceCostPercentage"
                  value={financeData.maintenanceCostPercentage}
                  onChange={(e) =>
                    setFinanceData({ ...financeData, maintenanceCostPercentage: e.target.value })
                  }
                  helperText="Expected percentage of maintenance cost for the property (0-100)"
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Insurance Price (EGP)"
                  name="insurancePrice"
                  value={financeData.insurancePrice}
                  onChange={(e) =>
                    setFinanceData({ ...financeData, insurancePrice: e.target.value })
                  }
                  helperText="Fixed insurance price for this property"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Allowed Person Number"
                  name="maxGuests"
                  value={financeData.maxGuests}
                  onChange={(e) => setFinanceData({ ...financeData, maxGuests: e.target.value })}
                  helperText="Maximum number of guests allowed"
                  inputProps={{ min: 1 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Extra Fees per Person (EGP)"
                  name="extraFeesPerPerson"
                  value={financeData.extraFeesPerPerson}
                  onChange={(e) =>
                    setFinanceData({ ...financeData, extraFeesPerPerson: e.target.value })
                  }
                  helperText="Additional fee per person in EGP"
                  inputProps={{ min: 0 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Early Check-in Fee (%)"
                  name="earlyCheckInFeePercentage"
                  value={financeData.earlyCheckInFeePercentage}
                  onChange={(e) =>
                    setFinanceData({ ...financeData, earlyCheckInFeePercentage: e.target.value })
                  }
                  helperText="Percentage of price per night charged for early check-in (0–100). Leave empty for no fee."
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Late Checkout Fee (%)"
                  name="lateCheckOutFeePercentage"
                  value={financeData.lateCheckOutFeePercentage}
                  onChange={(e) =>
                    setFinanceData({ ...financeData, lateCheckOutFeePercentage: e.target.value })
                  }
                  helperText="Percentage of price per night charged for late checkout (0–100). Leave empty for no fee."
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={loadingFinancePackages} size="small">
                  <InputLabel>Packages</InputLabel>
                  <Select
                    multiple
                    value={financeData.packages || []}
                    label="Packages"
                    onChange={(e) => setFinanceData({ ...financeData, packages: e.target.value })}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? "No packages"
                        : selected
                            .map((id) => financePackagesList.find((p) => (p._id || p.id) === id))
                            .filter(Boolean)
                            .map((p) => p.name?.en || p._id || p.id)
                            .join(", ")
                    }
                    sx={{ minHeight: "44px" }}
                  >
                    {financePackagesList.map((pkg) => (
                      <MenuItem key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                        {pkg.name?.en || pkg._id || pkg.id}
                        {pkg.price != null && ` — EGP ${Number(pkg.price).toLocaleString()}`}
                      </MenuItem>
                    ))}
                    {financePackagesList.length === 0 && !loadingFinancePackages && (
                      <MenuItem disabled>No packages available</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <MDTypography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Add or remove packages for this property. Leave empty to have no packages.
                </MDTypography>
              </Grid>
              {(financeDialogMode === "approve" || financeDialogMode === "activate") && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Check-In/Check-Out Agent *</InputLabel>
                      <Select
                        value={financeData.checkInOutAgentId || ""}
                        label="Check-In/Check-Out Agent *"
                        onChange={(e) =>
                          setFinanceData({ ...financeData, checkInOutAgentId: e.target.value })
                        }
                        sx={{ minHeight: "44px" }}
                      >
                        {checkInOutAgents.map((agent) => (
                          <MenuItem key={agent._id || agent.id} value={agent._id || agent.id}>
                            {agent.fullName} {agent.email ? `(${agent.email})` : ""}
                          </MenuItem>
                        ))}
                        {(!checkInOutAgents || checkInOutAgents.length === 0) &&
                          !loadingCheckInOutAgents && (
                            <MenuItem disabled>No agents available</MenuItem>
                          )}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            bgcolor: "grey.50",
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <MDButton
            onClick={() => {
              setFinanceDialogOpen(false);
              setFinanceData({
                price: "",
                profitPercentage: "",
                insurancePrice: "",
                maintenanceCostPercentage: "",
                maxGuests: "",
                extraFeesPerPerson: "",
                earlyCheckInFeePercentage: "",
                lateCheckOutFeePercentage: "",
                packages: [],
                checkInOutAgentId: "",
                expectedReadyDate: "",
              });
            }}
            color="secondary"
            variant="outlined"
            disabled={updatingFinance}
            sx={{
              minHeight: "44px",
              px: 3,
              borderRadius: 2,
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={async () => {
              try {
                setUpdatingFinance(true);
                setFinanceModalError("");

                // When in approve mode (Set Finance & Move to Preparation), validate required fields and call approve
                if (financeDialogMode === "approve") {
                  const price = parseFloat(financeData.price);
                  const profitPercentage = parseFloat(financeData.profitPercentage);
                  if (!financeData.price || isNaN(price) || price <= 0) {
                    setFinanceModalError("Price per night is required and must be greater than 0.");
                    setUpdatingFinance(false);
                    return;
                  }
                  if (
                    profitPercentage === undefined ||
                    profitPercentage === null ||
                    isNaN(profitPercentage) ||
                    profitPercentage < 0 ||
                    profitPercentage > 100
                  ) {
                    setFinanceModalError(
                      "Profit percentage is required and must be between 0 and 100."
                    );
                    setUpdatingFinance(false);
                    return;
                  }
                  if (!financeData.checkInOutAgentId) {
                    setFinanceModalError("Check-in/check-out agent is required.");
                    setUpdatingFinance(false);
                    return;
                  }
                  const approveRes = await adminAPI.approveProperty(
                    id,
                    price,
                    profitPercentage,
                    "",
                    financeData.insurancePrice !== ""
                      ? parseFloat(financeData.insurancePrice)
                      : undefined,
                    financeData.checkInOutAgentId,
                    undefined,
                    financeData.maintenanceCostPercentage !== ""
                      ? parseFloat(financeData.maintenanceCostPercentage)
                      : undefined,
                    financeData.maxGuests !== "" ? parseInt(financeData.maxGuests, 10) : undefined,
                    financeData.extraFeesPerPerson !== ""
                      ? parseFloat(financeData.extraFeesPerPerson)
                      : undefined,
                    financeData.earlyCheckInFeePercentage !== ""
                      ? parseFloat(financeData.earlyCheckInFeePercentage)
                      : undefined,
                    financeData.lateCheckOutFeePercentage !== ""
                      ? parseFloat(financeData.lateCheckOutFeePercentage)
                      : undefined
                  );
                  if (approveRes.property) setProperty(approveRes.property);
                  setFinanceModalError("");
                  setFinanceDialogOpen(false);
                  setFinanceDialogMode("edit");
                  setFinanceData({
                    price: "",
                    profitPercentage: "",
                    insurancePrice: "",
                    maintenanceCostPercentage: "",
                    maxGuests: "",
                    extraFeesPerPerson: "",
                    earlyCheckInFeePercentage: "",
                    lateCheckOutFeePercentage: "",
                    packages: [],
                    checkInOutAgentId: "",
                    expectedReadyDate: "",
                  });
                  const propertyResponse = await adminAPI.getPropertyById(id);
                  setProperty(propertyResponse.property);
                  setMessage({
                    type: "success",
                    text: approveRes.message || "Property moved to Preparation.",
                  });
                  setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                  setUpdatingFinance(false);
                  return;
                }

                // When in activate mode (Photo_Shooting → Active): save finance and set Active
                if (financeDialogMode === "activate") {
                  const price = parseFloat(financeData.price);
                  const profitPercentage = parseFloat(financeData.profitPercentage);
                  if (!financeData.price || isNaN(price) || price <= 0) {
                    setFinanceModalError("Price per night is required and must be greater than 0.");
                    setUpdatingFinance(false);
                    return;
                  }
                  if (
                    profitPercentage === undefined ||
                    profitPercentage === null ||
                    isNaN(profitPercentage) ||
                    profitPercentage < 0 ||
                    profitPercentage > 100
                  ) {
                    setFinanceModalError(
                      "Profit percentage is required and must be between 0 and 100."
                    );
                    setUpdatingFinance(false);
                    return;
                  }
                  if (!financeData.checkInOutAgentId) {
                    setFinanceModalError("Check-in/check-out agent is required.");
                    setUpdatingFinance(false);
                    return;
                  }
                  const activateBody = {
                    price,
                    profitPercentage,
                    notes: "",
                    insurancePrice:
                      financeData.insurancePrice !== ""
                        ? parseFloat(financeData.insurancePrice)
                        : undefined,
                    checkInOutAgentId: financeData.checkInOutAgentId,
                    maintenanceCostPercentage:
                      financeData.maintenanceCostPercentage !== ""
                        ? parseFloat(financeData.maintenanceCostPercentage)
                        : undefined,
                    maxGuests:
                      financeData.maxGuests !== ""
                        ? parseInt(financeData.maxGuests, 10)
                        : undefined,
                    extraFeesPerPerson:
                      financeData.extraFeesPerPerson !== ""
                        ? parseFloat(financeData.extraFeesPerPerson)
                        : undefined,
                    earlyCheckInFeePercentage:
                      financeData.earlyCheckInFeePercentage !== ""
                        ? parseFloat(financeData.earlyCheckInFeePercentage)
                        : undefined,
                    lateCheckOutFeePercentage:
                      financeData.lateCheckOutFeePercentage !== ""
                        ? parseFloat(financeData.lateCheckOutFeePercentage)
                        : undefined,
                  };
                  const activateRes = await adminAPI.activateProperty(id, activateBody);
                  if (activateRes.property) setProperty(activateRes.property);
                  setFinanceModalError("");
                  setFinanceDialogOpen(false);
                  setFinanceDialogMode("edit");
                  setFinanceData({
                    price: "",
                    profitPercentage: "",
                    insurancePrice: "",
                    maintenanceCostPercentage: "",
                    maxGuests: "",
                    extraFeesPerPerson: "",
                    earlyCheckInFeePercentage: "",
                    lateCheckOutFeePercentage: "",
                    packages: [],
                    checkInOutAgentId: "",
                    expectedReadyDate: "",
                  });
                  const propertyResponse = await adminAPI.getPropertyById(id);
                  setProperty(propertyResponse.property);
                  setMessage({
                    type: "success",
                    text: activateRes.message || "Property activated successfully.",
                  });
                  setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                  setUpdatingFinance(false);
                  return;
                }

                // Validate price if provided (edit mode)
                if (financeData.price !== "") {
                  const price = parseFloat(financeData.price);
                  if (isNaN(price) || price < 0) {
                    setFinanceModalError("Price must be a valid positive number");
                    setUpdatingFinance(false);
                    return;
                  }
                }

                // Validate profit percentage if provided
                if (financeData.profitPercentage !== "") {
                  const profitPercentage = parseFloat(financeData.profitPercentage);
                  if (isNaN(profitPercentage) || profitPercentage < 0 || profitPercentage > 100) {
                    setFinanceModalError("Profit percentage must be between 0 and 100");
                    setUpdatingFinance(false);
                    return;
                  }
                }

                // Validate maintenance cost percentage if provided
                if (financeData.maintenanceCostPercentage !== "") {
                  const maintenanceCostPercentage = parseFloat(
                    financeData.maintenanceCostPercentage
                  );
                  if (
                    isNaN(maintenanceCostPercentage) ||
                    maintenanceCostPercentage < 0 ||
                    maintenanceCostPercentage > 100
                  ) {
                    setFinanceModalError("Maintenance cost percentage must be between 0 and 100");
                    setUpdatingFinance(false);
                    return;
                  }
                }

                // Validate insurance price if provided
                if (financeData.insurancePrice !== "") {
                  const insurancePrice = parseFloat(financeData.insurancePrice);
                  if (isNaN(insurancePrice) || insurancePrice < 0) {
                    setFinanceModalError("Insurance price must be a valid positive number");
                    setUpdatingFinance(false);
                    return;
                  }
                }

                if (financeData.maxGuests !== "") {
                  const maxGuests = parseInt(financeData.maxGuests, 10);
                  if (isNaN(maxGuests) || maxGuests < 1) {
                    setFinanceModalError("Allowed person number must be at least 1");
                    setUpdatingFinance(false);
                    return;
                  }
                }

                if (financeData.extraFeesPerPerson !== "") {
                  const extraFeesPerPerson = parseFloat(financeData.extraFeesPerPerson);
                  if (isNaN(extraFeesPerPerson) || extraFeesPerPerson < 0) {
                    setFinanceModalError("Extra fees per person must be 0 or greater");
                    setUpdatingFinance(false);
                    return;
                  }
                }
                if (financeData.earlyCheckInFeePercentage !== "") {
                  const earlyCheckInFeePercentage = parseFloat(
                    financeData.earlyCheckInFeePercentage
                  );
                  if (
                    isNaN(earlyCheckInFeePercentage) ||
                    earlyCheckInFeePercentage < 0 ||
                    earlyCheckInFeePercentage > 100
                  ) {
                    setFinanceModalError("Early check-in fee percentage must be between 0 and 100");
                    setUpdatingFinance(false);
                    return;
                  }
                }
                if (financeData.lateCheckOutFeePercentage !== "") {
                  const lateCheckOutFeePercentage = parseFloat(
                    financeData.lateCheckOutFeePercentage
                  );
                  if (
                    isNaN(lateCheckOutFeePercentage) ||
                    lateCheckOutFeePercentage < 0 ||
                    lateCheckOutFeePercentage > 100
                  ) {
                    setFinanceModalError("Late checkout fee percentage must be between 0 and 100");
                    setUpdatingFinance(false);
                    return;
                  }
                }

                // Prepare update data
                const updateData = {};
                if (financeData.price !== "") {
                  updateData.price = parseFloat(financeData.price);
                }
                if (financeData.profitPercentage !== "") {
                  updateData.profitPercentage = parseFloat(financeData.profitPercentage);
                }
                if (financeData.maintenanceCostPercentage !== "") {
                  updateData.maintenanceCostPercentage = parseFloat(
                    financeData.maintenanceCostPercentage
                  );
                }
                if (financeData.insurancePrice !== "") {
                  updateData.insurancePrice = parseFloat(financeData.insurancePrice);
                }
                if (financeData.maxGuests !== "") {
                  updateData.maxGuests = parseInt(financeData.maxGuests, 10);
                }
                if (financeData.extraFeesPerPerson !== "") {
                  updateData.extraFeesPerPerson = parseFloat(financeData.extraFeesPerPerson);
                }
                if (financeData.earlyCheckInFeePercentage !== "") {
                  updateData.earlyCheckInFeePercentage = parseFloat(
                    financeData.earlyCheckInFeePercentage
                  );
                }
                if (financeData.lateCheckOutFeePercentage !== "") {
                  updateData.lateCheckOutFeePercentage = parseFloat(
                    financeData.lateCheckOutFeePercentage
                  );
                }

                // Update property (price, profitPercentage, insurancePrice, maxGuests, extraFeesPerPerson, earlyCheckInFeePercentage, lateCheckOutFeePercentage)
                if (Object.keys(updateData).length > 0) {
                  const detailsResponse = await adminAPI.updatePropertyDetails(
                    id,
                    updateData,
                    null
                  );
                  setProperty(detailsResponse.property);
                }

                // Update packages for this property via pricing endpoint
                const packagesPayload = Array.isArray(financeData.packages)
                  ? financeData.packages
                  : [];
                const pricingResponse = await adminAPI.quickUpdatePropertyPricing(id, {
                  packages: packagesPayload,
                });
                if (pricingResponse.property) {
                  setProperty((prev) =>
                    prev ? { ...prev, packages: pricingResponse.property.packages } : prev
                  );
                }

                setFinanceModalError("");
                setMessage({
                  type: "success",
                  text: "Finance information updated successfully",
                });
                setFinanceDialogOpen(false);
                setFinanceData({
                  price: "",
                  profitPercentage: "",
                  insurancePrice: "",
                  maintenanceCostPercentage: "",
                  maxGuests: "",
                  extraFeesPerPerson: "",
                  earlyCheckInFeePercentage: "",
                  lateCheckOutFeePercentage: "",
                  packages: [],
                });
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
              } catch (error) {
                console.error("Error updating finance:", error);
                setFinanceModalError(
                  error.response?.data?.message ||
                    error.message ||
                    "Failed to update finance information"
                );
              } finally {
                setUpdatingFinance(false);
              }
            }}
            color="warning"
            variant="gradient"
            disabled={updatingFinance}
            startIcon={
              updatingFinance ? <CircularProgress size={20} color="inherit" /> : <Icon>save</Icon>
            }
            sx={{
              minHeight: "44px",
              px: 4,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(237, 108, 2, 0.3)",
            }}
          >
            {updatingFinance ? "Saving..." : "Save Finance"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => {
          if (!updatingStatus) {
            setStatusDialogOpen(false);
            setSelectedStatus("");
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            py: 2.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <Icon sx={{ fontSize: "2rem" }}>swap_horiz</Icon>
            <MDTypography variant="h5" fontWeight="bold">
              Change Property Status
            </MDTypography>
          </MDBox>
          <IconButton
            onClick={() => {
              if (!updatingStatus) {
                setStatusDialogOpen(false);
                setSelectedStatus("");
              }
            }}
            sx={{ color: "white" }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <MDBox>
            <MDTypography variant="body2" color="text.secondary" mb={3}>
              Select the new status for this property.
            </MDTypography>
            {property && (
              <MDBox p={2} borderRadius={2} bgcolor="grey.50" mb={3}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  {property.title?.en || property.title || "N/A"}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Current Status:{" "}
                  <strong>
                    {getStatusLabel(property.status, "admin", {
                      hasInspector: !!property.assignedInspector,
                    })}
                  </strong>
                </MDTypography>
              </MDBox>
            )}
            <FormControl fullWidth>
              <InputLabel>New Status *</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="New Status *"
                disabled={updatingStatus}
                sx={{
                  minHeight: "44px",
                  "& .MuiSelect-select": {
                    minHeight: "44px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        minHeight: "44px",
                      },
                    },
                  },
                }}
              >
                {/* Only allow changing to: Approved, Maintenance, Suspended */}
                <MenuItem value="Approved">
                  <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Chip label="Active" color="success" size="small" />
                    <MDTypography variant="body2">Active</MDTypography>
                  </MDBox>
                </MenuItem>
                <MenuItem value="Suspended">
                  <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Chip label="Suspended" color="error" size="small" />
                    <MDTypography variant="body2">Suspended</MDTypography>
                  </MDBox>
                </MenuItem>
                <MenuItem value="Maintenance">
                  <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Chip label="Maintenance" color="warning" size="small" />
                    <MDTypography variant="body2">Maintenance</MDTypography>
                  </MDBox>
                </MenuItem>
              </Select>
            </FormControl>
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            bgcolor: "grey.50",
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <MDButton
            onClick={() => {
              setStatusDialogOpen(false);
              setSelectedStatus("");
            }}
            color="secondary"
            variant="outlined"
            disabled={updatingStatus}
            sx={{
              minHeight: "44px",
              px: 3,
              borderRadius: 2,
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={async () => {
              if (!selectedStatus || selectedStatus === property.status) {
                setMessage({
                  type: "error",
                  text: "Please select a different status",
                });
                return;
              }

              try {
                setUpdatingStatus(true);
                setMessage({ type: "", text: "" });

                const response = await adminAPI.updatePropertyDetails(
                  id,
                  { status: selectedStatus },
                  null
                );
                setProperty(response.property);
                setMessage({
                  type: "success",
                  text: "Property status updated successfully",
                });
                setStatusDialogOpen(false);
                setSelectedStatus("");
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
              } catch (error) {
                console.error("Error updating property status:", error);
                setMessage({
                  type: "error",
                  text: error.message || "Failed to update property status",
                });
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
              } finally {
                setUpdatingStatus(false);
              }
            }}
            color="info"
            variant="gradient"
            disabled={updatingStatus || !selectedStatus || selectedStatus === property.status}
            startIcon={
              updatingStatus ? <CircularProgress size={20} color="inherit" /> : <Icon>save</Icon>
            }
            sx={{
              minHeight: "44px",
              px: 4,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            }}
          >
            {updatingStatus ? "Updating..." : "Update Status"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Official Documents Management Dialog - Super Admin Only */}
      {isSuperAdmin && (
        <Dialog
          open={officialDocumentsDialogOpen}
          onClose={() => !uploadingOfficialDocuments && setOfficialDocumentsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography
                variant="h5"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Icon color="info">folder</Icon>
                Manage Official Documents
              </MDTypography>
              <IconButton
                onClick={() => !uploadingOfficialDocuments && setOfficialDocumentsDialogOpen(false)}
                disabled={uploadingOfficialDocuments}
              >
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent dividers>
            <MDBox>
              <MDTypography variant="body2" color="text.secondary" mb={3}>
                Upload or update official documents for this property. You can select multiple PDF
                files.
              </MDTypography>

              {/* File Input */}
              <MDBox mb={3}>
                <input
                  accept="application/pdf"
                  style={{ display: "none" }}
                  id="official-documents-upload"
                  multiple
                  type="file"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setOfficialDocumentsFiles(files);
                  }}
                  disabled={uploadingOfficialDocuments}
                />
                <label htmlFor="official-documents-upload">
                  <MDButton
                    variant="outlined"
                    color="info"
                    component="span"
                    startIcon={<Icon>upload_file</Icon>}
                    disabled={uploadingOfficialDocuments}
                    fullWidth
                    sx={{ mb: 2, minHeight: "48px" }}
                  >
                    {officialDocumentsFiles.length > 0
                      ? `${officialDocumentsFiles.length} file(s) selected`
                      : "Select PDF Documents"}
                  </MDButton>
                </label>

                {/* Selected Files List */}
                {officialDocumentsFiles.length > 0 && (
                  <MDBox mt={2}>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      display="block"
                      mb={1}
                    >
                      Selected Files:
                    </MDTypography>
                    {officialDocumentsFiles.map((file, index) => (
                      <MDBox
                        key={index}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={1.5}
                        mb={1}
                        sx={{
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon color="error">description</Icon>
                          <MDTypography variant="body2">{file.name}</MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </MDTypography>
                        </MDBox>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newFiles = officialDocumentsFiles.filter((_, i) => i !== index);
                            setOfficialDocumentsFiles(newFiles);
                          }}
                          disabled={uploadingOfficialDocuments}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </MDBox>
                    ))}
                  </MDBox>
                )}

                {/* Current Documents */}
                {property.officialDocuments && property.officialDocuments.length > 0 && (
                  <MDBox mt={3}>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      display="block"
                      mb={1}
                    >
                      Current Documents ({property.officialDocuments.length}):
                    </MDTypography>
                    <Alert severity="success" sx={{ borderRadius: 1 }}>
                      Uploading new documents will add them to the existing ones. Documents are
                      stored in a bucket and accumulate over time.
                    </Alert>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
            <MDButton
              onClick={() => {
                if (!uploadingOfficialDocuments) {
                  setOfficialDocumentsDialogOpen(false);
                  setOfficialDocumentsFiles([]);
                }
              }}
              disabled={uploadingOfficialDocuments}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={async () => {
                if (officialDocumentsFiles.length === 0) {
                  setMessage({
                    type: "error",
                    text: "Please select at least one document to upload.",
                  });
                  setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                  return;
                }

                try {
                  setUploadingOfficialDocuments(true);
                  setMessage({ type: "", text: "" });

                  // Update property with new official documents
                  const response = await adminAPI.updatePropertyDetails(
                    id,
                    {},
                    {
                      officialDocuments: officialDocumentsFiles,
                    }
                  );

                  if (response.property || response.success) {
                    setMessage({
                      type: "success",
                      text: response.message || "Official documents updated successfully.",
                    });
                    // Refresh property data
                    const propertyResponse = await adminAPI.getPropertyById(id);
                    setProperty(propertyResponse.property);
                    setOfficialDocumentsDialogOpen(false);
                    setOfficialDocumentsFiles([]);
                    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                  }
                } catch (error) {
                  console.error("Error updating official documents:", error);
                  setMessage({
                    type: "error",
                    text: error.message || "Failed to update official documents. Please try again.",
                  });
                  setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                } finally {
                  setUploadingOfficialDocuments(false);
                }
              }}
              disabled={uploadingOfficialDocuments || officialDocumentsFiles.length === 0}
              startIcon={
                uploadingOfficialDocuments ? <CircularProgress size={16} /> : <Icon>save</Icon>
              }
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                },
              }}
            >
              {uploadingOfficialDocuments ? "Uploading..." : "Upload Documents"}
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default PropertyDetails;
